import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { Calendar, Users, Package, PackageCheck, TrendingUp, TrendingDown, DollarSign, Upload, Sparkles, Building2, LogOut, Plus, Eye, FileText, BarChart3, Edit, Save, X, Settings, Paperclip, Shield, UserPlus, Cog, Check, Trash2, CreditCard, Search, Filter, AlertCircle, RefreshCw, Menu, Factory } from "lucide-react";
import { Calendar as ShadCalendar } from "./components/ui/calendar";
import Reports from "./components/Reports";
import { LegalDashboard } from "./components/LegalDashboard";
import { ProjectFinancialTrend } from "./components/ProjectFinancialTrend";
import ProjectSchedule from "./components/ProjectSchedule";
import DistributorModule from "./components/DistributorModule";
import FactoryModule from "./components/FactoryModule";
import PublicCatalog from "./pages/PublicCatalog";
import DashboardKPIs from "./components/DashboardKPIs";
import BankAccountsTab from "./components/BankAccountsTab";
import OwnerContributionsTab from "./components/OwnerContributionsTab";
import PayrollTab from "./components/PayrollTab";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

// Currency formatting function for Brazilian Real
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'R$ 0,00';
  }
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(parseFloat(value));
};

// Robust API URL detection for production deployment
const getApiUrl = () => {
  // In production (custom domains), always use relative path
  const currentHost = window.location.hostname;
  const isCustomDomain = !currentHost.includes('preview.emergentagent.com') && 
                         !currentHost.includes('localhost') &&
                         !currentHost.includes('127.0.0.1');
  
  if (isCustomDomain) {
    // For custom domains (like grupsabino.com.br), use relative path
    console.log(`Using relative API path for custom domain: ${currentHost}`);
    return '/api';
  }
  
  // Check if we have environment variable set (for preview environments)
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl && envUrl.trim()) {
    // Make sure the API URL includes the /api path
    const apiUrl = envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
    console.log(`Using env API URL: ${apiUrl}`);
    return apiUrl;
  }
  
  // Fallback to relative path
  const apiUrl = '/api';
  console.log(`Using relative API path: ${apiUrl}`);
  return apiUrl;
};

const API = getApiUrl();

// Auth Context
const AuthContext = React.createContext();

// AuthProvider Component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Setup axios interceptor to handle auth errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.log("🚨 Token inválido ou expirado - limpando sessão");
          localStorage.removeItem('token');
          localStorage.removeItem('selectedCompany');
          delete axios.defaults.headers.common['Authorization'];
          // Only reload if we had a token before
          if (localStorage.getItem('token') === null && token) {
            window.location.reload();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  // Validate token on app start
  useEffect(() => {
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          // Test if token is valid
          await axios.get(`${API}/auth/me`);
          setToken(storedToken);
        } catch (error) {
          console.log("Token inválido, limpando...");
          localStorage.removeItem('token');
          localStorage.removeItem('selectedCompany');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
        }
      }
    };
    validateToken();
  }, []);

  useEffect(() => {
    if (token) {
      // Set authorization header when token exists
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      // Remove authorization header when no token
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const login = async (email, password) => {
    console.log("Tentando login com:", email);
    try {
      const response = await axios.post(`${API}/auth/login`, { 
        email: email.trim(), 
        password: password 
      });
      
      console.log("Resposta do login:", response.data);
      
      const { access_token, user: userData } = response.data;
      
      if (access_token && userData) {
        setToken(access_token);
        setUser(userData);
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        console.log("Login bem-sucedido!");
        toast.success("Login realizado com sucesso!");
        
        // Auto-select company if user has only one company (with delay to ensure token is set)
        if (userData.companies && userData.companies.length === 1) {
          setTimeout(async () => {
            try {
              console.log("Buscando empresas após login...");
              const companiesResponse = await axios.get(`${API}/companies`);
              console.log("Empresas encontradas:", companiesResponse.data);
              
              const userCompany = companiesResponse.data.find(company => 
                company.id === userData.companies[0]
              );
              if (userCompany) {
                setSelectedCompany(userCompany);
                console.log("Empresa selecionada automaticamente:", userCompany.name);
                toast.success(`Bem-vindo à ${userCompany.name}!`);
              } else {
                console.log("Empresa do usuário não encontrada na lista");
              }
            } catch (error) {
              console.error("Erro ao buscar dados da empresa:", error);
              toast.error("Erro ao buscar empresas: " + (error.response?.data?.detail || error.message));
            }
          }, 1000); // Delay de 1 segundo para garantir que o token esteja configurado
        }
        
        return true;
      } else {
        throw new Error("Dados de login inválidos");
      }
    } catch (error) {
      console.error("Erro no login:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Erro desconhecido";
      toast.error("Erro ao fazer login: " + errorMessage);
      return false;
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success("Cadastro realizado com sucesso!");
      return true;
    } catch (error) {
      toast.error("Erro ao cadastrar: " + (error.response?.data?.detail || "Erro desconhecido"));
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setSelectedCompany(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    toast.success("Logout realizado com sucesso!");
  };

  const value = { user, token, selectedCompany, setSelectedCompany, login, register, logout };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook for using auth context
function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Login Component
function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("HandleSubmit chamado - isLogin:", isLogin, "email:", email);
    
    if (isLogin) {
      console.log("Chamando função login...");
      const success = await login(email, password);
      console.log("Resultado do login:", success);
    } else {
      console.log("Chamando função register...");
      const success = await register(name, email, password);
      console.log("Resultado do register:", success);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo-sabino.jpeg" 
              alt="Grupo Sabino" 
              className="h-24 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-xl md:text-2xl font-bold text-gray-800">
            {isLogin ? "Entrar" : "Cadastrar"}
          </CardTitle>
          <CardDescription className="text-gray-600">
            Sistema de Gestão Empresarial
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <Input
                type="text"
                placeholder="Nome completo"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="text-black placeholder:text-gray-600"
              />
            )}
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="text-black placeholder:text-gray-600"
              data-testid="login-email"
            />
            <Input
              type="password"
              placeholder="Senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="text-black placeholder:text-gray-600"
              data-testid="login-password"
            />
            <Button type="submit" data-testid="login-submit" className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700">
              {isLogin ? "Entrar" : "Cadastrar"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Button 
              variant="link" 
              onClick={() => setIsLogin(!isLogin)}
            >
              {isLogin ? "Não tem conta? Cadastre-se" : "Já tem conta? Faça login"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Company Selection Component
function CompanySelection() {
  const [companies, setCompanies] = useState([]);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyCNPJ, setNewCompanyCNPJ] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editCompanyCNPJ, setEditCompanyCNPJ] = useState("");
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [showModuleConfig, setShowModuleConfig] = useState(null);
  const { user, selectedCompany, setSelectedCompany, token } = useAuth();

  useEffect(() => {
    fetchAvailableModules(); // Can be called without authentication
    if (token) {
      fetchCompanies(); // Requires authentication
    }
  }, [token]);

  const fetchCompanies = async () => {
    if (!token) return; // Only fetch if authenticated
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        console.log("Token expirado, redirecionando para login");
        // Don't show error toast for auth issues
        return;
      }
      toast.error("Erro ao buscar empresas");
    }
  };

  const fetchAvailableModules = async () => {
    try {
      const response = await axios.get(`${API}/modules`);
      setAvailableModules(response.data);
    } catch (error) {
      console.error("Erro ao buscar módulos:", error);
      toast.error("Erro ao buscar módulos disponíveis");
    }
  };

  const createCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/companies`, {
        name: newCompanyName,
        cnpj: newCompanyCNPJ
      });
      toast.success("Empresa criada com sucesso!");
      setNewCompanyName("");
      setNewCompanyCNPJ("");
      setShowCreateForm(false);
      fetchCompanies();
    } catch (error) {
      toast.error("Erro ao criar empresa");
    }
  };

  const startEditCompany = (company) => {
    setEditingCompany(company);
    setEditCompanyName(company.name);
    setEditCompanyCNPJ(company.cnpj || "");
  };

  const cancelEdit = () => {
    setEditingCompany(null);
    setEditCompanyName("");
    setEditCompanyCNPJ("");
  };

  const updateCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/companies/${editingCompany.id}`, {
        name: editCompanyName,
        cnpj: editCompanyCNPJ
      });
      toast.success("Empresa atualizada com sucesso!");
      cancelEdit();
      fetchCompanies();
    } catch (error) {
      toast.error("Erro ao atualizar empresa");
    }
  };

  const startModuleConfig = (company) => {
    setShowModuleConfig(company);
    setSelectedModules(company.active_modules || []);
  };

  const deleteCompany = async (company) => {
    const typed = window.prompt(
      `ATENÇÃO: Isso vai apagar PERMANENTEMENTE a empresa "${company.name}" e TODOS os dados vinculados (transações, clientes, obras, folha, fábrica, etc.).\n\nEsta ação NÃO pode ser desfeita.\n\nPara confirmar, digite o nome da empresa exatamente como está:`
    );
    if (!typed) return;
    if (typed.trim() !== (company.name || "").trim()) {
      toast.error("Nome digitado não confere. Operação cancelada.");
      return;
    }
    try {
      await axios.delete(`${API}/companies/${company.id}`);
      toast.success(`Empresa "${company.name}" apagada`);
      if (selectedCompany?.id === company.id) setSelectedCompany(null);
      fetchCompanies();
    } catch (e) {
      toast.error("Erro ao apagar: " + (e.response?.data?.detail || ""));
    }
  };


  const handleModuleToggle = (moduleCode) => {
    setSelectedModules(prev => 
      prev.includes(moduleCode) 
        ? prev.filter(m => m !== moduleCode)
        : [...prev, moduleCode]
    );
  };

  const saveModuleConfig = async () => {
    try {
      await axios.put(`${API}/companies/${showModuleConfig.id}/modules`, selectedModules);
      toast.success("Módulos atualizados com sucesso!");
      setShowModuleConfig(null);
      fetchCompanies();
    } catch (error) {
      toast.error("Erro ao atualizar módulos");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800">
            Selecione uma Empresa
          </CardTitle>
          <CardDescription>
            Escolha ou crie uma empresa para gerenciar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {companies.map((company) => (
              <Card key={company.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  {editingCompany && editingCompany.id === company.id ? (
                    // Formulário de edição
                    <form onSubmit={updateCompany} className="space-y-4">
                      <Input
                        placeholder="Nome da empresa"
                        value={editCompanyName}
                        onChange={(e) => setEditCompanyName(e.target.value)}
                        required
                        className="text-black"
                      />
                      <Input
                        placeholder="CNPJ (opcional)"
                        value={editCompanyCNPJ}
                        onChange={(e) => setEditCompanyCNPJ(e.target.value)}
                        className="text-black"
                      />
                      <div className="flex gap-2">
                        <Button type="submit" size="sm" className="bg-green-500 hover:bg-green-600">
                          <Save className="h-4 w-4 mr-2" />
                          Salvar
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={cancelEdit}>
                          <X className="h-4 w-4 mr-2" />
                          Cancelar
                        </Button>
                      </div>
                    </form>
                  ) : (
                    // Visualização normal
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base sm:text-lg break-words">{company.name}</h3>
                        {company.cnpj && <p className="text-sm text-gray-600">{company.cnpj}</p>}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => startEditCompany(company)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => startModuleConfig(company)}
                        >
                          <Cog className="h-4 w-4 mr-2" />
                          Módulos
                        </Button>
                        {company.owner_id === user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-700 border-red-300 hover:bg-red-50"
                          onClick={() => deleteCompany(company)}
                          data-testid={`company-delete-${company.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Apagar
                        </Button>
                        )}
                        <Button 
                          onClick={() => setSelectedCompany(company)}
                          data-testid={`company-select-${company.id}`}
                          className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                        >
                          Selecionar
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {showCreateForm ? (
              <Card>
                <CardContent className="p-4">
                  <form onSubmit={createCompany} className="space-y-4">
                    <Input
                      placeholder="Nome da empresa"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      required
                      className="text-black"
                    />
                    <Input
                      placeholder="CNPJ (opcional)"
                      value={newCompanyCNPJ}
                      onChange={(e) => setNewCompanyCNPJ(e.target.value)}
                      className="text-black"
                    />
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1">Criar</Button>
                      <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Button 
                onClick={() => setShowCreateForm(true)}
                variant="outline"
                className="w-full p-8 border-dashed border-2 hover:bg-gray-50"
              >
                <Plus className="mr-2 h-5 w-5" />
                Criar Nova Empresa
              </Button>
            )}
          </div>
        </CardContent>
        
        {/* Module Configuration Dialog */}
        <Dialog open={showModuleConfig !== null} onOpenChange={() => setShowModuleConfig(null)}>
          <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-black">
                Configurar Módulos - {showModuleConfig?.name}
              </DialogTitle>
              <DialogDescription>
                Selecione os módulos que estarão disponíveis para esta empresa
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              {availableModules.map((module) => (
                <div key={module.code} className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id={`module-${module.code}`}
                    checked={selectedModules.includes(module.code)}
                    onChange={() => handleModuleToggle(module.code)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor={`module-${module.code}`}
                      className="text-sm font-medium text-gray-900 cursor-pointer"
                    >
                      {module.name}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">{module.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModuleConfig(null)}>
                Cancelar
              </Button>
              <Button onClick={saveModuleConfig}>
                Salvar Configurações
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
}

// Main Dashboard Component
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceClients, setServiceClients] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [viewMode, setViewMode] = useState("timeline"); // timeline or diary
  const { user, selectedCompany, logout, setSelectedCompany } = useAuth();

  // Loading state
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Form states
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [showServiceClientForm, setShowServiceClientForm] = useState(false);
  const [editingServiceClient, setEditingServiceClient] = useState(null);
  const [showClientDocuments, setShowClientDocuments] = useState(null);
  const [showServiceClientDocuments, setShowServiceClientDocuments] = useState(null);
  const [clientDocuments, setClientDocuments] = useState([]);
  const [serviceClientDocuments, setServiceClientDocuments] = useState([]);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [myPermissions, setMyPermissions] = useState({
    is_owner: false,
    role: "user",
    permissions: [],
    can_view_dashboard: false
  });
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    permissions: []
  });
  
  // Construction states
  const [constructionProjects, setConstructionProjects] = useState([]);
  const [selectedTrendProjectId, setSelectedTrendProjectId] = useState("");
  const [allProjectPhases, setAllProjectPhases] = useState([]); // Todas as fases de todos os projetos
  const [materials, setMaterials] = useState([]);
  const [materialSearchTerm, setMaterialSearchTerm] = useState("");
  
  // Material Orders states (Pedidos de Material)
  const [materialOrders, setMaterialOrders] = useState([]);
  const [showMaterialOrderForm, setShowMaterialOrderForm] = useState(false);
  const [selectedMaterialOrder, setSelectedMaterialOrder] = useState(null);
  const [editingMaterialOrder, setEditingMaterialOrder] = useState(false);
  const [materialOrderForm, setMaterialOrderForm] = useState({
    project_id: "",
    phase_id: "",
    supplier_id: "",
    observations: "",
    kind: "material",
    destination: "obra",
    items: [{ id: Date.now().toString(), description: "", unit: "unidade", quantity: "", unit_price: "" }]
  });
  const [orderDocumentUploading, setOrderDocumentUploading] = useState(false);
  const [uploadDocumentType, setUploadDocumentType] = useState("boleto"); // boleto ou nota_fiscal
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  
  // Aprovação Financeira states
  const [showFinancialApprovalForm, setShowFinancialApprovalForm] = useState(false);
  const [financialApprovalForm, setFinancialApprovalForm] = useState({
    due_date: new Date().toISOString().split('T')[0],
    expense_type: "material",
    is_installment: false,
    total_installments: 1,
    installments: []
  });
  
  // Accounts Payable states
  const [accountsPayable, setAccountsPayable] = useState([]);
  const [showAccountPayableForm, setShowAccountPayableForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    transactions: { search: "", type: "all", dateFrom: "", dateTo: "", bank_account_id: "all" },
    clients: { search: "" },
    suppliers: { search: "" },
    services: { search: "" },
    projects: { search: "", status: "all" },
    accountsPayable: { search: "", status: "all", type: "all", dateFrom: "", dateTo: "", obra: "todas" }
  });

  // Filtro de mês e ano do Dashboard de Contas a Pagar (separados)
  const [apDashboardYear, setApDashboardYear] = useState(() => new Date().getFullYear());
  const [apDashboardMonth, setApDashboardMonth] = useState(() => new Date().getMonth() + 1); // 1-12 ou 0 = todos os meses
  // Aba ativa do módulo Contas a Pagar (subtabs horizontais)
  const [apActiveTab, setApActiveTab] = useState("dashboard");
  // Contas bancárias (usado por transações e AP para vincular pagamento a uma conta)
  const [bankAccounts, setBankAccounts] = useState([]);
  // Aba ativa do módulo Construção (subtabs horizontais)
  const [cmActiveTab, setCmActiveTab] = useState("projetos");
  // Sub-aba de Pedidos de Material
  const [moStatusTab, setMoStatusTab] = useState("pendente"); // pendente | em_andamento | concluido
  // Filtros de Pedido de Material
  const [moFilters, setMoFilters] = useState({
    search: "",
    month: "all", // all | 1..12
    year: new Date().getFullYear(),
    solic_inicio: "",
    solic_fim: "",
    aprov_inicio: "",
    aprov_fim: "",
  });
  // ============ ESTADOS ESTOQUE ============
  const [stockItems, setStockItems] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [stockExits, setStockExits] = useState([]);
  const [stockActiveTab, setStockActiveTab] = useState("itens");
  const [stockFilters, setStockFilters] = useState({ search: "", lowStockOnly: false, kind: "all" });
  // Entrada manual removida - todas as entradas devem ser via Pedido com aprovação
  const [showStockExitForm, setShowStockExitForm] = useState(false);
  const [stockExitForm, setStockExitForm] = useState({ item_id: "", quantity: "", requester_name: "", project_id: "", phase_id: "", observations: "" });
  const [showStockAdjustForm, setShowStockAdjustForm] = useState(false);
  const [stockAdjustForm, setStockAdjustForm] = useState({ item_id: "", quantity: "", reason: "" });
  const [editingStockItem, setEditingStockItem] = useState(null);
  const [showStockEditForm, setShowStockEditForm] = useState(false);

  // Approval dialog states
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalAccount, setApprovalAccount] = useState(null);
  const [scheduledPaymentDate, setScheduledPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  // Report dialog states
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    status: "all",
    dateFrom: "",
    dateTo: ""
  });

  // ============ ESTADOS PROCESSO JUDICIÁRIO ============
  const [legalClients, setLegalClients] = useState([]);
  const [legalProcesses, setLegalProcesses] = useState([]);
  const [showLegalClientForm, setShowLegalClientForm] = useState(false);
  const [showLegalProcessForm, setShowLegalProcessForm] = useState(false);
  const [editingLegalClient, setEditingLegalClient] = useState(null);
  const [editingLegalProcess, setEditingLegalProcess] = useState(null);
  const [selectedLegalProcess, setSelectedLegalProcess] = useState(null);
  const [legalClientForm, setLegalClientForm] = useState({
    nome: "",
    cpf: "",
    telefone: "",
    email: "",
    senha_gov: "",
    vendedor: "",
    exames_realizados: [],
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: ""
  });
  const [legalProcessForm, setLegalProcessForm] = useState({
    client_id: "",
    numero_protocolo: "",
    valor_honorario: "",
    tipo_processo: "loas",
    detalhamento: "",
    andamento: "em_analise",
    etapa_atual: "",
    prazo_inicio: "",
    prazo_final: "",
    status_prorrogacao: "",
    // Nova categorização
    categoria: "administrativo",
    valor_processo: "",
    origem_administrativo_id: "",
    // Administrativo - Concedido
    honorario_previsao_pagamento: "",
    honorario_pago: false,
    // Judicial - RPV
    rpv_valor: "",
    rpv_protocolo: "",
    rpv_recebido: false,
    // Status das etapas do processo
    peticao_inicial_status: "pendente",
    peticao_inicial_data: "",
    peticao_inicial_obs: "",
    contestacao_status: "pendente",
    contestacao_data: "",
    contestacao_obs: "",
    contra_razoes_status: "pendente",
    contra_razoes_data: "",
    contra_razoes_obs: "",
    recurso_inominato_status: "pendente",
    recurso_inominato_data: "",
    recurso_inominato_obs: "",
    replica_contestacao_status: "pendente",
    replica_contestacao_data: "",
    replica_contestacao_obs: "",
    observacoes: ""
  });
  const [legalClientSearch, setLegalClientSearch] = useState("");
  const [legalClientDropdownOpen, setLegalClientDropdownOpen] = useState(false);
  const [legalFilters, setLegalFilters] = useState({
    clients: { search: "" },
    processes: { search: "", andamento: "all", tipo: "all" }
  });
  
  // Estados para Agenda de Audiências e Perícias
  const [agendamentos, setAgendamentos] = useState([]);
  const [showAgendamentoForm, setShowAgendamentoForm] = useState(false);
  const [editingAgendamento, setEditingAgendamento] = useState(null);
  const [agendamentoForm, setAgendamentoForm] = useState({
    process_id: "",
    tipo: "audiencia",
    titulo: "",
    data_hora: "",
    local: "",
    advogado_responsavel_id: "",
    descricao: "",
    notificar_email: true
  });

  // Estados do Dashboard de Processo Judiciário
  const [legalDashboard, setLegalDashboard] = useState(null);
  const [legalDashboardPeriod, setLegalDashboardPeriod] = useState(7);
  const [legalActiveTab, setLegalActiveTab] = useState("dashboard");
  // Filtros da aba Honorários
  const [honorariosFilters, setHonorariosFilters] = useState({
    search: "",
    status: "all", // all | a_receber | recebidos
    tipo: "all",   // all | administrativo | judicial
    data_inicio: "",
    data_fim: ""
  });
  // Dialog de parcelamento para honorário/RPV
  const [paymentDialog, setPaymentDialog] = useState({ open: false });
  // Agenda de Pagamentos Aprovados — filtros
  const [agendaPagamentosFiltros, setAgendaPagamentosFiltros] = useState({ data_inicio: "", data_fim: "", fornecedor: "" });
  // Próximos Vencimentos — filtro
  const [vencimentosFiltro, setVencimentosFiltro] = useState("");
  // Programados — filtros
  const [programadosFiltros, setProgramadosFiltros] = useState({ data_inicio: "", data_fim: "", fornecedor: "", periodo: "todos", obra: "todas" });
  const [progDetailsDlg, setProgDetailsDlg] = useState({ open: false, account: null });
  const [progDetailsConstruction, setProgDetailsConstruction] = useState(null);
  const [progDetailsLoading, setProgDetailsLoading] = useState(false);
  // Programados — seleção em lote e dialog de reprogramação
  const [programadosSelecionados, setProgramadosSelecionados] = useState(new Set());
  const [programDialog, setProgramDialog] = useState({ open: false, accountId: null, currentDate: "", date: "" });
  const [payDialog, setPayDialog] = useState({ open: false, account: null, bank_account_id: "" });
  const [batchPayDialog, setBatchPayDialog] = useState({ open: false, ids: [], bank_account_id: "", total: 0 });
  // Dashboard Contas a Pagar — drilldown
  const [apDrillDown, setApDrillDown] = useState({ open: false, kind: null, title: "", color: "blue", accounts: [] });
  // Busca de fornecedor em Nova Conta a Pagar
  const [apSupplierSearch, setApSupplierSearch] = useState("");
  const [apSupplierDropdownOpen, setApSupplierDropdownOpen] = useState(false);
  // Buscas inline em Pedido de Material
  const [moProjectSearch, setMoProjectSearch] = useState("");
  const [moProjectDropdownOpen, setMoProjectDropdownOpen] = useState(false);
  const [moSupplierSearch, setMoSupplierSearch] = useState("");
  const [moSupplierDropdownOpen, setMoSupplierDropdownOpen] = useState(false);
  // Agenda Calendar state
  const [agendaSelectedDay, setAgendaSelectedDay] = useState(null); // date do dia clicado
  const [agendaDayDialogOpen, setAgendaDayDialogOpen] = useState(false);
  // ============ FIM ESTADOS PROCESSO JUDICIÁRIO ============

  // Budget upload states
  const [showBudgetUpload, setShowBudgetUpload] = useState(false);
  const [budgetFile, setBudgetFile] = useState(null);
  const [budgetUploading, setBudgetUploading] = useState(false);
  const [budgetUploadProject, setBudgetUploadProject] = useState(null);

  // Phase services states
  const [phaseServices, setPhaseServices] = useState([]);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showServiceFormDialog, setShowServiceFormDialog] = useState(false);
  const [selectedPhaseForService, setSelectedPhaseForService] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [phaseServiceForm, setPhaseServiceForm] = useState({
    service_name: "",
    description: "",
    unit: "horas",
    quantity: "",
    unit_price: "",
    supplier_name: "",
    observations: ""
  });
  const [accountForm, setAccountForm] = useState({
    creditor_name: "",
    supplier_id: "",
    amount: "",
    due_date: "",
    expense_type: "",
    description: ""
  });
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [showPhaseForm, setShowPhaseForm] = useState(false);
  const [showMaterialUsageForm, setShowMaterialUsageForm] = useState(false);
  const [showServiceUsageForm, setShowServiceUsageForm] = useState(false);
  const [editingPhaseService, setEditingPhaseService] = useState(null);
  const [showEditProjectForm, setShowEditProjectForm] = useState(false);
  const [showEditPhaseForm, setShowEditPhaseForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingPhase, setEditingPhase] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [projectSchedule, setProjectSchedule] = useState([]);
  const [transactionPhases, setTransactionPhases] = useState([]); // fases carregadas para a obra selecionada no form de transação
  const [activeTab, setActiveTab] = useState("transactions");
  const [sidebarOpen, setSidebarOpen] = useState(false); // drawer mobile
  const [materialUsage, setMaterialUsage] = useState([]);
  const [projectDocuments, setProjectDocuments] = useState([]);
  const [projectForm, setProjectForm] = useState({
    name: "",
    client_id: "",
    approved_value: "",
    estimated_cost: "",
    description: ""
  });
  const [editProjectForm, setEditProjectForm] = useState({
    name: "",
    approved_value: "",
    estimated_cost: "",
    description: "",
    status: "planejamento"
  });
  const [materialForm, setMaterialForm] = useState({
    name: "",
    unit: "unidade",
    unit_price: "",
    description: "",
    kind: "material"
  });
  const [phaseForm, setPhaseForm] = useState({
    phase_name: "",
    description: "",
    estimated_cost: "",
    start_date: "",
    end_date: "",
    order: 1
  });
  const [materialUsageForm, setMaterialUsageForm] = useState({
    material_id: "",
    quantity: "",
    observations: ""
  });
  const [serviceUsageForm, setServiceUsageForm] = useState({
    service_name: "",
    unit: "horas",
    quantity: "",
    unit_price: "",
    supplier_name: "",
    description: "",
    observations: ""
  });
  const [transactionForm, setTransactionForm] = useState({
    type: "entrada",
    amount: "",
    description: "",
    client_id: "",
    supplier_id: "",
    product_id: "",
    bank_account_id: "",
    date: new Date().toISOString().split('T')[0],
    is_installment: false,
    total_installments: 1,
    installment_value: 0,
    // Vínculo com obra
    is_obra: false,
    project_id: "",
    phase_id: ""
  });
  const [installmentsList, setInstallmentsList] = useState([]);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedInstallmentTransaction, setSelectedInstallmentTransaction] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [clientForm, setClientForm] = useState({
    name: "",
    email: "",
    phone: "",
    document: "",
    responsible_name: ""
  });
  const [supplierForm, setSupplierForm] = useState({
    name: "",
    email: "",
    phone: "",
    document: ""
  });
  const [serviceForm, setServiceForm] = useState({
    name: "",
    price: "",
    description: ""
  });
  const [serviceClientForm, setServiceClientForm] = useState({
    service_id: "",
    client_id: "",
    status: "iniciado",
    notes: "",
    start_date: "",
    end_date: ""
  });

  useEffect(() => {
    console.log("useEffect triggered - selectedCompany changed:", selectedCompany?.name);
    if (selectedCompany && user) {
      fetchMyPermissions();
      fetchData();
      fetchAvailablePermissions();
    }
  }, [selectedCompany, user]);

  // Auto-refresh a cada 10 segundos para sincronizar dados entre usuários
  useEffect(() => {
    if (!selectedCompany?.id || !user) return;
    
    const autoRefreshInterval = setInterval(() => {
      console.log("🔄 Auto-refresh: Atualizando dados...");
      fetchDataSilent(); // Atualiza sem mostrar loading
    }, 10000); // 10 segundos

    return () => {
      clearInterval(autoRefreshInterval);
      console.log("🛑 Auto-refresh desativado");
    };
  }, [selectedCompany, user]);

  // Função de atualização silenciosa (sem loading spinner)
  const fetchDataSilent = async () => {
    if (!selectedCompany?.id) return;
    
    try {
      // Buscar dados principais em paralelo
      const [
        dashboardRes,
        transactionsRes,
        clientsRes,
        suppliersRes,
        servicesRes,
        serviceClientsRes
      ] = await Promise.all([
        axios.get(`${API}/companies/${selectedCompany.id}/dashboard`),
        axios.get(`${API}/companies/${selectedCompany.id}/transactions`),
        axios.get(`${API}/companies/${selectedCompany.id}/clients`),
        axios.get(`${API}/companies/${selectedCompany.id}/suppliers`),
        axios.get(`${API}/companies/${selectedCompany.id}/services`),
        axios.get(`${API}/companies/${selectedCompany.id}/service-clients`)
      ]);
      
      setStats(dashboardRes.data);
      setTransactions(transactionsRes.data);
      setClients(clientsRes.data);
      setSuppliers(suppliersRes.data);
      setServices(servicesRes.data);
      setServiceClients(serviceClientsRes.data);
      
      // Buscar dados de construção se o módulo estiver ativo
      if (selectedCompany?.active_modules?.includes('construction')) {
        const [projectsRes, materialsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/companies/${selectedCompany.id}/construction/projects`),
          axios.get(`${API}/companies/${selectedCompany.id}/construction/materials`),
          axios.get(`${API}/companies/${selectedCompany.id}/material-orders`)
        ]);
        
        setConstructionProjects(projectsRes.data);
        setMaterials(materialsRes.data);
        setMaterialOrders(ordersRes.data);
        
        // Buscar fases de todos os projetos
        if (projectsRes.data.length > 0) {
          const allPhasesPromises = projectsRes.data.map(project => 
            axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${project.id}/schedule`)
          );
          const phasesResponses = await Promise.all(allPhasesPromises);
          const allPhases = phasesResponses.flatMap(res => res.data);
          setAllProjectPhases(allPhases);
        }
      }
      
      // Buscar contas a pagar se o módulo estiver ativo
      if (selectedCompany?.active_modules?.includes('accounts_payable')) {
        const accountsRes = await axios.get(`${API}/companies/${selectedCompany.id}/accounts-payable`);
        setAccountsPayable(accountsRes.data);
      }

      // Buscar contas bancárias (sempre — necessárias para transações e AP)
      try {
        const bankRes = await axios.get(`${API}/companies/${selectedCompany.id}/bank-accounts`);
        setBankAccounts(bankRes.data || []);
      } catch (e) { /* silencioso */ }

      setLastUpdate(new Date());
      console.log("✅ Auto-refresh concluído");
    } catch (error) {
      console.error("❌ Erro no auto-refresh:", error.message);
    }
  };

  const fetchMyPermissions = async () => {
    if (!selectedCompany?.id) return;
    try {
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/my-permissions`);
      setMyPermissions(response.data);
      console.log("Minhas permissões:", response.data);
      // Se tiver permissão de gerenciar usuários, buscar lista de usuários
      if (response.data.is_owner || response.data.permissions.includes('manage_users')) {
        fetchCompanyUsers();
      }
    } catch (error) {
      console.error("Erro ao buscar minhas permissões");
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const response = await axios.get(`${API}/permissions`);
      setAvailablePermissions(response.data);
    } catch (error) {
      console.error("Erro ao buscar permissões");
    }
  };

  const fetchCompanyUsers = async () => {
    if (!selectedCompany?.id) return;
    try {
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/users`);
      setCompanyUsers(response.data);
    } catch (error) {
      console.error("Erro ao buscar usuários da empresa");
    }
  };

  const refreshTokenIfNeeded = async () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      // Test token with a simple call
      await axios.get(`${API}/companies`);
      return true; // Token is still valid
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log("🔄 Token expirado, fazendo novo login...");
        // Token expired, try to refresh by getting user data
        return false;
      }
      return true; // Other errors, keep trying
    }
  };

  const fetchData = async () => {
    if (!selectedCompany?.id) {
      console.log("❌ Sem selectedCompany.id");
      return;
    }
    
    console.log("🚀 Iniciando fetchData para empresa:", selectedCompany.name, selectedCompany.id);
    setLoading(true);
    
    try {
      // Core data calls first
      console.log("1. 🔍 Buscando dashboard...");
      const dashboardRes = await axios.get(`${API}/companies/${selectedCompany.id}/dashboard`);
      setStats(dashboardRes.data);
      console.log("✅ Dashboard carregado");

      console.log("2. 🔍 Buscando transações...");
      const transactionsRes = await axios.get(`${API}/companies/${selectedCompany.id}/transactions`);
      setTransactions(transactionsRes.data);

      console.log("3. 🔍 Buscando clientes...");
      const clientsRes = await axios.get(`${API}/companies/${selectedCompany.id}/clients`);
      setClients(clientsRes.data);

      console.log("4. 🔍 Buscando fornecedores...");
      const suppliersRes = await axios.get(`${API}/companies/${selectedCompany.id}/suppliers`);
      setSuppliers(suppliersRes.data);

      console.log("5. 🔍 Buscando serviços...");
      const servicesRes = await axios.get(`${API}/companies/${selectedCompany.id}/services`);
      setServices(servicesRes.data);

      console.log("6. 🔍 Buscando service-clients...");
      const serviceClientsRes = await axios.get(`${API}/companies/${selectedCompany.id}/service-clients`);
      setServiceClients(serviceClientsRes.data);

      // CRITICAL: Always try to load construction projects
      console.log("7. 🔍 Buscando PROJETOS DE CONSTRUÇÃO...");
      try {
        const constructionProjectsRes = await axios.get(`${API}/companies/${selectedCompany.id}/construction/projects`);
        setConstructionProjects(constructionProjectsRes.data);
        console.log("✅ PROJETOS DE CONSTRUÇÃO carregados:", constructionProjectsRes.data.length, "projetos");
        console.log("📋 Projetos encontrados:", constructionProjectsRes.data);
        
        // Buscar fases de todos os projetos em andamento
        const projectsInProgress = constructionProjectsRes.data.filter(p => p.status === 'em_andamento');
        let allPhases = [];
        for (const project of projectsInProgress) {
          try {
            const phasesRes = await axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${project.id}/schedule`);
            allPhases = [...allPhases, ...phasesRes.data];
          } catch (e) {
            console.error(`Erro ao buscar fases do projeto ${project.id}:`, e);
          }
        }
        setAllProjectPhases(allPhases);
        console.log("✅ Fases de todos os projetos carregadas:", allPhases.length);
      } catch (constructionError) {
        console.error("❌ Erro ao carregar projetos de construção:", constructionError);
        setConstructionProjects([]);
        setAllProjectPhases([]);
      }

      // Try to load materials
      console.log("8. 🔍 Buscando materiais...");
      try {
        const materialsRes = await axios.get(`${API}/companies/${selectedCompany.id}/construction/materials`);
        setMaterials(materialsRes.data);
        console.log("✅ Materiais carregados:", materialsRes.data.length, "itens");
      } catch (materialsError) {
        console.error("❌ Erro ao carregar materiais:", materialsError);
        setMaterials([]);
      }

      // Try to load accounts payable
      console.log("9. 🔍 Buscando contas a pagar...");
      try {
        const accountsRes = await axios.get(`${API}/companies/${selectedCompany.id}/accounts-payable`);
        setAccountsPayable(accountsRes.data);
        console.log("✅ Contas a pagar carregadas:", accountsRes.data.length, "itens");
      } catch (accountsError) {
        console.error("❌ Erro ao carregar contas a pagar:", accountsError);
        setAccountsPayable([]);
      }

      // Load bank accounts (Caixa Geral é criada automaticamente pelo backend)
      try {
        const bankRes = await axios.get(`${API}/companies/${selectedCompany.id}/bank-accounts`);
        setBankAccounts(bankRes.data || []);
      } catch (e) { setBankAccounts([]); }

      console.log("10. 🔍 Buscando pedidos de material...");
      try {
        const ordersRes = await axios.get(`${API}/companies/${selectedCompany.id}/material-orders`);
        setMaterialOrders(ordersRes.data);
        console.log("✅ Pedidos de material carregados:", ordersRes.data.length, "itens");
      } catch (ordersError) {
        console.error("❌ Erro ao carregar pedidos de material:", ordersError);
        setMaterialOrders([]);
      }

      // Buscar dados do módulo Processo Judiciário se ativo
      if (selectedCompany?.active_modules?.includes('legal_process')) {
        console.log("11. 🔍 Buscando clientes jurídicos...");
        try {
          const legalClientsRes = await axios.get(`${API}/companies/${selectedCompany.id}/legal-clients`);
          setLegalClients(legalClientsRes.data);
          console.log("✅ Clientes jurídicos carregados:", legalClientsRes.data.length, "itens");
        } catch (e) {
          console.error("❌ Erro ao carregar clientes jurídicos:", e);
          setLegalClients([]);
        }

        console.log("12. 🔍 Buscando processos judiciais...");
        try {
          const legalProcessesRes = await axios.get(`${API}/companies/${selectedCompany.id}/legal-processes`);
          setLegalProcesses(legalProcessesRes.data);
          console.log("✅ Processos judiciais carregados:", legalProcessesRes.data.length, "itens");
        } catch (e) {
          console.error("❌ Erro ao carregar processos judiciais:", e);
          setLegalProcesses([]);
        }

        console.log("13. 🔍 Buscando agendamentos...");
        try {
          const agendamentosRes = await axios.get(`${API}/companies/${selectedCompany.id}/agendamentos`);
          setAgendamentos(agendamentosRes.data);
          console.log("✅ Agendamentos carregados:", agendamentosRes.data.length, "itens");
        } catch (e) {
          console.error("❌ Erro ao carregar agendamentos:", e);
          setAgendamentos([]);
        }

        console.log("14. 🔍 Buscando dashboard jurídico...");
        try {
          const dashRes = await axios.get(`${API}/companies/${selectedCompany.id}/legal-processes/dashboard?dias=7`);
          setLegalDashboard(dashRes.data);
          console.log("✅ Dashboard jurídico carregado");
        } catch (e) {
          console.error("❌ Erro ao carregar dashboard jurídico:", e);
        }
      }

      console.log("🎉 TODOS os dados carregados com SUCESSO!");
      toast.success("Dados carregados com sucesso!");
      setLastUpdate(new Date());
      
    } catch (error) {
      console.error("❌ ERRO no fetchData:", error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log("🚨 Erro de autenticação - sessão expirada");
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        toast.error("Sessão expirada. Redirecionando para login...");
        setTimeout(() => window.location.reload(), 1500);
        return;
      }
      
      // Set default data so UI doesn't break
      setStats({ total_entrada: 0, total_saida: 0, saldo: 0, total_vendas: 0, transacoes_hoje: 0, parcelas_vencidas: 0, valor_parcelas_vencidas: 0, parcelas_vencidas_lista: [] });
      setTransactions([]);
      setClients([]);
      setSuppliers([]);
      setServices([]);
      setServiceClients([]);
      setConstructionProjects([]);
      setMaterials([]);
      setAccountsPayable([]);
      
      toast.error("Erro ao carregar alguns dados. Dados básicos foram inicializados.");
    } finally {
      setLoading(false);
      console.log("🏁 fetchData FINALIZADO - loading = false");
    }
  };

  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    try {
      const transactionData = {
        ...transactionForm,
        amount: parseFloat(transactionForm.amount),
        date: transactionForm.date ? new Date(transactionForm.date).toISOString() : new Date().toISOString(),
        is_installment: transactionForm.is_installment,
        total_installments: parseInt(transactionForm.total_installments) || 1,
        installment_value: parseFloat(transactionForm.installment_value) || 0,
        // Enviar lista de parcelas com valores individuais
        installments_list: transactionForm.is_installment ? installmentsList : null
      };
      
      if (editingTransaction) {
        await axios.put(`${API}/companies/${selectedCompany.id}/transactions/${editingTransaction.id}`, transactionData);
        toast.success("Transação atualizada com sucesso!");
        setEditingTransaction(null);
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/transactions`, transactionData);
        if (transactionData.is_installment && transactionData.type === "venda") {
          toast.success(`Venda parcelada criada! ${installmentsList.length} parcelas foram geradas.`);
        } else {
          toast.success("Transação criada com sucesso!");
        }
      }
      
      setTransactionForm({
        type: "entrada",
        amount: "",
        description: "",
        client_id: "",
        supplier_id: "",
        product_id: "",
        bank_account_id: "",
        date: new Date().toISOString().split('T')[0],
        is_installment: false,
        total_installments: 1,
        installment_value: 0,
        is_obra: false,
        project_id: "",
        phase_id: ""
      });
      setInstallmentsList([]);
      setTransactionPhases([]);
      setShowTransactionForm(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || (editingTransaction ? "Erro ao atualizar transação" : "Erro ao criar transação"));
    }
  };

  const startEditTransaction = (transaction) => {
    setTransactionForm({
      type: transaction.type,
      amount: transaction.amount.toString(),
      description: transaction.description,
      client_id: transaction.client_id || "",
      supplier_id: transaction.supplier_id || "",
      product_id: transaction.product_id || "",
      bank_account_id: transaction.bank_account_id || "",
      date: transaction.date ? transaction.date.split('T')[0] : new Date().toISOString().split('T')[0],
      is_installment: transaction.is_installment || false,
      total_installments: transaction.total_installments || 1,
      installment_value: transaction.installment_value || 0,
      is_obra: transaction.is_obra || false,
      project_id: transaction.project_id || "",
      phase_id: transaction.phase_id || ""
    });
    // Se transação tinha obra, carregar as fases
    if (transaction.project_id) {
      axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${transaction.project_id}/schedule`)
        .then(res => setTransactionPhases(res.data))
        .catch(err => { console.error("Erro ao carregar medições:", err); setTransactionPhases([]); });
    } else {
      setTransactionPhases([]);
    }
    setEditingTransaction(transaction);
    setShowTransactionForm(true);
  };

  const deleteTransaction = async (transactionId) => {
    if (window.confirm("Tem certeza que deseja excluir esta transação?")) {
      try {
        await axios.delete(`${API}/companies/${selectedCompany.id}/transactions/${transactionId}`);
        toast.success("Transação excluída com sucesso!");
        fetchData();
      } catch (error) {
        toast.error("Erro ao excluir transação");
      }
    }
  };

  // Funções de Parcelas (Installments)
  const fetchInstallments = async (transactionId) => {
    try {
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/transactions/${transactionId}/installments`);
      setInstallments(response.data);
    } catch (error) {
      console.error("Erro ao buscar parcelas:", error);
      toast.error("Erro ao carregar parcelas");
    }
  };

  // Modal de pagamento de parcela (obra/medição)
  const [payingInstallment, setPayingInstallment] = useState(null); // {id, installment_number, amount}
  const [payForm, setPayForm] = useState({ is_obra: false, project_id: "", phase_id: "", bank_account_id: "" });
  const [payPhases, setPayPhases] = useState([]);

  // Adicionar nova parcela a uma venda existente
  const [newInstallmentForm, setNewInstallmentForm] = useState({
    amount: "",
    due_date: new Date().toISOString().split('T')[0],
    description: ""
  });
  const addNewInstallment = async () => {
    if (!selectedInstallmentTransaction) return;
    if (!newInstallmentForm.amount || parseFloat(newInstallmentForm.amount) <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    if (!newInstallmentForm.due_date) {
      toast.error("Informe a data de vencimento");
      return;
    }
    try {
      await axios.post(
        `${API}/companies/${selectedCompany.id}/transactions/${selectedInstallmentTransaction.id}/installments`,
        {
          amount: parseFloat(newInstallmentForm.amount),
          due_date: new Date(newInstallmentForm.due_date).toISOString(),
          description: newInstallmentForm.description || null,
        }
      );
      toast.success("Parcela adicionada!");
      setNewInstallmentForm({
        amount: "",
        due_date: new Date().toISOString().split('T')[0],
        description: ""
      });
      fetchInstallments(selectedInstallmentTransaction.id);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao adicionar parcela");
    }
  };

  const payInstallment = async (installmentId, payload = null) => {
    try {
      const body = payload || {};
      const response = await axios.patch(
        `${API}/companies/${selectedCompany.id}/installments/${installmentId}/pay`,
        body
      );
      toast.success(response.data.message || "Parcela paga com sucesso!");
      // Atualizar lista de parcelas
      if (selectedInstallmentTransaction) {
        fetchInstallments(selectedInstallmentTransaction.id);
      }
      // Atualizar transações para refletir a entrada criada
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao pagar parcela");
    }
  };

  // Abre o dialog de pagamento de parcela (perguntando obra/medição)
  const openPayInstallmentDialog = (installment) => {
    setPayingInstallment(installment);
    const defaultBank = bankAccounts.find(b => b.is_default);
    setPayForm({ is_obra: false, project_id: "", phase_id: "", bank_account_id: defaultBank?.id || (bankAccounts[0]?.id || "") });
    setPayPhases([]);
  };

  const confirmPayInstallment = async () => {
    if (!payingInstallment) return;
    if (!payForm.bank_account_id) {
      toast.error("Selecione a conta bancária que receberá o valor");
      return;
    }
    // Validação
    if (payForm.is_obra && (!payForm.project_id || !payForm.phase_id)) {
      toast.error("Selecione a obra e a medição para vincular o pagamento");
      return;
    }
    await payInstallment(payingInstallment.id, {
      is_obra: payForm.is_obra,
      project_id: payForm.is_obra ? payForm.project_id : null,
      phase_id: payForm.is_obra ? payForm.phase_id : null,
      bank_account_id: payForm.bank_account_id,
    });
    // Refresh bank accounts to reflect balance
    try {
      const bankRes = await axios.get(`${API}/companies/${selectedCompany.id}/bank-accounts`);
      setBankAccounts(bankRes.data || []);
    } catch (e) { /* silencioso */ }
    setPayingInstallment(null);
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/companies/${selectedCompany.id}/clients/${editingClient.id}`, clientForm);
        toast.success("Cliente atualizado com sucesso!");
        setEditingClient(null);
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/clients`, clientForm);
        toast.success("Cliente criado com sucesso!");
      }
      setClientForm({ name: "", email: "", phone: "", document: "", responsible_name: "" });
      setShowClientForm(false);
      fetchData();
    } catch (error) {
      toast.error(editingClient ? "Erro ao atualizar cliente" : "Erro ao criar cliente");
    }
  };

  const startEditClient = (client) => {
    setClientForm({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      document: client.document || "",
      responsible_name: client.responsible_name || ""
    });
    setEditingClient(client);
    setShowClientForm(true);
  };

  const deleteClient = async (clientId) => {
    if (window.confirm("Tem certeza que deseja excluir este cliente?")) {
      try {
        await axios.delete(`${API}/companies/${selectedCompany.id}/clients/${clientId}`);
        toast.success("Cliente excluído com sucesso!");
        fetchData();
      } catch (error) {
        toast.error("Erro ao excluir cliente");
      }
    }
  };

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await axios.put(`${API}/companies/${selectedCompany.id}/suppliers/${editingSupplier.id}`, supplierForm);
        toast.success("Fornecedor atualizado com sucesso!");
        setEditingSupplier(null);
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/suppliers`, supplierForm);
        toast.success("Fornecedor criado com sucesso!");
      }
      setSupplierForm({ name: "", email: "", phone: "", document: "" });
      setShowSupplierForm(false);
      fetchData();
    } catch (error) {
      toast.error(editingSupplier ? "Erro ao atualizar fornecedor" : "Erro ao criar fornecedor");
    }
  };

  const startEditSupplier = (supplier) => {
    setSupplierForm({
      name: supplier.name || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      document: supplier.document || ""
    });
    setEditingSupplier(supplier);
    setShowSupplierForm(true);
  };

  const deleteSupplier = async (supplierId) => {
    if (window.confirm("Tem certeza que deseja excluir este fornecedor?")) {
      try {
        await axios.delete(`${API}/companies/${selectedCompany.id}/suppliers/${supplierId}`);
        toast.success("Fornecedor excluído com sucesso!");
        fetchData();
      } catch (error) {
        toast.error("Erro ao excluir fornecedor");
      }
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const serviceData = {
        ...serviceForm,
        price: parseFloat(serviceForm.price)
      };
      
      if (editingService) {
        await axios.put(`${API}/companies/${selectedCompany.id}/services/${editingService.id}`, serviceData);
        toast.success("Serviço atualizado com sucesso!");
        setEditingService(null);
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/services`, serviceData);
        toast.success("Serviço criado com sucesso!");
      }
      setServiceForm({ name: "", price: "", description: "" });
      setShowServiceForm(false);
      fetchData();
    } catch (error) {
      toast.error(editingService ? "Erro ao atualizar serviço" : "Erro ao criar serviço");
    }
  };

  const startEditService = (service) => {
    setServiceForm({
      name: service.name || "",
      price: service.price?.toString() || "",
      description: service.description || ""
    });
    setEditingService(service);
    setShowServiceForm(true);
  };

  const deleteServiceItem = async (serviceId) => {
    if (window.confirm("Tem certeza que deseja excluir este serviço?")) {
      try {
        await axios.delete(`${API}/companies/${selectedCompany.id}/services/${serviceId}`);
        toast.success("Serviço excluído com sucesso!");
        fetchData();
      } catch (error) {
        toast.error("Erro ao excluir serviço");
      }
    }
  };

  const handleServiceClientSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/companies/${selectedCompany.id}/service-clients`, serviceClientForm);
      toast.success("Serviço vinculado ao cliente com sucesso!");
      setServiceClientForm({ service_id: "", client_id: "", status: "iniciado", notes: "", start_date: "", end_date: "" });
      setShowServiceClientForm(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao vincular serviço ao cliente");
    }
  };

  const updateServiceClientStatus = async (serviceClientId, newStatus, notes = "", startDate = "", endDate = "") => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      if (notes) formData.append('notes', notes);
      if (startDate) formData.append('start_date', startDate);
      if (endDate) formData.append('end_date', endDate);

      await axios.put(`${API}/companies/${selectedCompany.id}/service-clients/${serviceClientId}`, formData);
      toast.success("Status atualizado com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const startEditServiceClient = async (serviceClient) => {
    setEditingServiceClient(serviceClient);
    setServiceClientForm({
      service_id: serviceClient.service_id,
      client_id: serviceClient.client_id,
      status: serviceClient.status,
      notes: serviceClient.notes || "",
      start_date: serviceClient.start_date ? serviceClient.start_date.split('T')[0] : "",
      end_date: serviceClient.end_date ? serviceClient.end_date.split('T')[0] : ""
    });
    setShowServiceClientForm(true);
  };

  const handleEditServiceClient = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('service_id', serviceClientForm.service_id);
      formData.append('client_id', serviceClientForm.client_id);
      formData.append('status', serviceClientForm.status);
      if (serviceClientForm.notes) formData.append('notes', serviceClientForm.notes);
      if (serviceClientForm.start_date) formData.append('start_date', serviceClientForm.start_date);
      if (serviceClientForm.end_date) formData.append('end_date', serviceClientForm.end_date);

      await axios.put(`${API}/companies/${selectedCompany.id}/service-clients/${editingServiceClient.id}/edit`, formData);
      toast.success("Serviço atualizado com sucesso!");
      setEditingServiceClient(null);
      setServiceClientForm({ service_id: "", client_id: "", status: "iniciado", notes: "", start_date: "", end_date: "" });
      setShowServiceClientForm(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar serviço");
    }
  };

  const handleClientDocumentUpload = async (clientId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/companies/${selectedCompany.id}/clients/${clientId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success("Documento anexado com sucesso!");
      fetchClientDocuments(clientId);
    } catch (error) {
      toast.error("Erro ao anexar documento: " + (error.response?.data?.detail || "Erro desconhecido"));
    }
  };

  const fetchClientDocuments = async (clientId) => {
    try {
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/clients/${clientId}/documents`);
      setClientDocuments(response.data);
    } catch (error) {
      toast.error("Erro ao buscar documentos do cliente");
    }
  };

  const handleServiceClientDocumentUpload = async (serviceClientId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/companies/${selectedCompany.id}/service-clients/${serviceClientId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success("Documento anexado com sucesso!");
      fetchServiceClientDocuments(serviceClientId);
    } catch (error) {
      toast.error("Erro ao anexar documento: " + (error.response?.data?.detail || "Erro desconhecido"));
    }
  };

  const fetchServiceClientDocuments = async (serviceClientId) => {
    try {
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/service-clients/${serviceClientId}/documents`);
      setServiceClientDocuments(response.data);
    } catch (error) {
      toast.error("Erro ao buscar documentos do serviço");
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Editar usuário existente
        const formData = new FormData();
        formData.append('role', userForm.role);
        formData.append('permissions', JSON.stringify(userForm.permissions));
        formData.append('is_active', true);
        
        await axios.put(
          `${API}/companies/${selectedCompany.id}/users/${editingUser.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success("Usuário atualizado com sucesso!");
        setEditingUser(null);
      } else {
        // Criar novo usuário
        await axios.post(`${API}/companies/${selectedCompany.id}/users`, {
          ...userForm,
          company_id: selectedCompany.id
        });
        toast.success("Usuário criado com sucesso!");
      }
      setUserForm({ name: "", email: "", password: "", role: "user", permissions: [] });
      setShowUserForm(false);
      fetchCompanyUsers();
    } catch (error) {
      toast.error("Erro ao salvar usuário: " + (error.response?.data?.detail || "Erro desconhecido"));
    }
  };

  const startEditUser = (companyUser) => {
    setEditingUser(companyUser);
    setUserForm({
      name: companyUser.name,
      email: companyUser.email,
      password: "",
      role: companyUser.role || "user",
      permissions: companyUser.permissions || []
    });
    setShowUserForm(true);
  };

  const cancelEditUser = () => {
    setEditingUser(null);
    setUserForm({ name: "", email: "", password: "", role: "user", permissions: [] });
    setShowUserForm(false);
  };

  const handlePermissionToggle = (permissionCode) => {
    console.log("Toggle permission:", permissionCode);
    console.log("Current permissions:", userForm.permissions);
    setUserForm(prev => {
      const currentPerms = prev.permissions || [];
      const newPerms = currentPerms.includes(permissionCode)
        ? currentPerms.filter(p => p !== permissionCode)
        : [...currentPerms, permissionCode];
      console.log("New permissions:", newPerms);
      return {
        ...prev,
        permissions: newPerms
      };
    });
  };

  // ============ FUNÇÕES PROCESSO JUDICIÁRIO ============

  const resetLegalClientForm = () => {
    setLegalClientForm({
      nome: "",
      cpf: "",
      telefone: "",
      email: "",
      senha_gov: "",
      vendedor: "",
      exames_realizados: [],
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: ""
    });
    setEditingLegalClient(null);
  };

  const resetLegalProcessForm = () => {
    setLegalProcessForm({
      client_id: "",
      numero_protocolo: "",
      valor_honorario: "",
      tipo_processo: "loas",
      detalhamento: "",
      andamento: "em_analise",
      etapa_atual: "",
      prazo_inicio: "",
      prazo_final: "",
      status_prorrogacao: "",
      categoria: "administrativo",
      valor_processo: "",
      origem_administrativo_id: "",
      honorario_previsao_pagamento: "",
      honorario_pago: false,
      rpv_valor: "",
      rpv_protocolo: "",
      rpv_recebido: false,
      peticao_inicial_status: "pendente",
      peticao_inicial_data: "",
      peticao_inicial_obs: "",
      contestacao_status: "pendente",
      contestacao_data: "",
      contestacao_obs: "",
      contra_razoes_status: "pendente",
      contra_razoes_data: "",
      contra_razoes_obs: "",
      recurso_inominato_status: "pendente",
      recurso_inominato_data: "",
      recurso_inominato_obs: "",
      replica_contestacao_status: "pendente",
      replica_contestacao_data: "",
      replica_contestacao_obs: "",
      observacoes: ""
    });
    setEditingLegalProcess(null);
    setLegalClientSearch("");
    setLegalClientDropdownOpen(false);
  };

  const handleLegalClientSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...legalClientForm,
        exames_realizados: legalClientForm.exames_realizados || []
      };

      if (editingLegalClient) {
        await axios.put(`${API}/companies/${selectedCompany.id}/legal-clients/${editingLegalClient.id}`, data);
        toast.success("Cliente atualizado com sucesso!");
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/legal-clients`, data);
        toast.success("Cliente cadastrado com sucesso!");
      }
      
      resetLegalClientForm();
      setShowLegalClientForm(false);
      fetchData();
    } catch (error) {
      toast.error("Erro: " + (error.response?.data?.detail || "Erro ao salvar cliente"));
    }
  };

  const handleLegalProcessSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...legalProcessForm,
        valor_honorario: parseFloat(legalProcessForm.valor_honorario) || 0,
        valor_processo: legalProcessForm.valor_processo ? parseFloat(legalProcessForm.valor_processo) : null,
        rpv_valor: legalProcessForm.rpv_valor ? parseFloat(legalProcessForm.rpv_valor) : null,
        origem_administrativo_id: legalProcessForm.origem_administrativo_id || null,
      };

      if (editingLegalProcess) {
        await axios.put(`${API}/companies/${selectedCompany.id}/legal-processes/${editingLegalProcess.id}`, data);
        toast.success("Processo atualizado com sucesso!");
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/legal-processes`, data);
        toast.success("Processo cadastrado com sucesso!");
      }
      
      resetLegalProcessForm();
      setShowLegalProcessForm(false);
      fetchData();
    } catch (error) {
      toast.error("Erro: " + (error.response?.data?.detail || "Erro ao salvar processo"));
    }
  };

  const startEditLegalClient = (client) => {
    setEditingLegalClient(client);
    setLegalClientForm({
      nome: client.nome || "",
      cpf: client.cpf || "",
      telefone: client.telefone || "",
      email: client.email || "",
      senha_gov: client.senha_gov || "",
      vendedor: client.vendedor || "",
      exames_realizados: client.exames_realizados || [],
      cep: client.cep || "",
      logradouro: client.logradouro || "",
      numero: client.numero || "",
      complemento: client.complemento || "",
      bairro: client.bairro || "",
      cidade: client.cidade || "",
      estado: client.estado || ""
    });
    setShowLegalClientForm(true);
  };

  const startEditLegalProcess = (process) => {
    setEditingLegalProcess(process);
    setLegalProcessForm({
      client_id: process.client_id || "",
      numero_protocolo: process.numero_protocolo || "",
      valor_honorario: process.valor_honorario || "",
      tipo_processo: process.tipo_processo || "urbano",
      detalhamento: process.detalhamento || "",
      andamento: process.andamento || "em_analise",
      etapa_atual: process.etapa_atual || "",
      prazo_inicio: process.prazo_inicio || "",
      prazo_final: process.prazo_final || "",
      status_prorrogacao: process.status_prorrogacao || "",
      categoria: process.categoria || "administrativo",
      valor_processo: process.valor_processo || "",
      origem_administrativo_id: process.origem_administrativo_id || "",
      honorario_previsao_pagamento: process.honorario_previsao_pagamento || "",
      honorario_pago: !!process.honorario_pago,
      rpv_valor: process.rpv_valor || "",
      rpv_protocolo: process.rpv_protocolo || "",
      rpv_recebido: !!process.rpv_recebido,
      peticao_inicial_status: process.peticao_inicial_status || "pendente",
      peticao_inicial_data: process.peticao_inicial_data || "",
      peticao_inicial_obs: process.peticao_inicial_obs || "",
      contestacao_status: process.contestacao_status || "pendente",
      contestacao_data: process.contestacao_data || "",
      contestacao_obs: process.contestacao_obs || "",
      contra_razoes_status: process.contra_razoes_status || "pendente",
      contra_razoes_data: process.contra_razoes_data || "",
      contra_razoes_obs: process.contra_razoes_obs || "",
      recurso_inominato_status: process.recurso_inominato_status || "pendente",
      recurso_inominato_data: process.recurso_inominato_data || "",
      recurso_inominato_obs: process.recurso_inominato_obs || "",
      replica_contestacao_status: process.replica_contestacao_status || "pendente",
      replica_contestacao_data: process.replica_contestacao_data || "",
      replica_contestacao_obs: process.replica_contestacao_obs || "",
      observacoes: process.observacoes || ""
    });
    setShowLegalProcessForm(true);
  };

  const deleteLegalClient = async (clientId) => {
    if (!window.confirm("Tem certeza que deseja excluir este cliente?")) return;
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/legal-clients/${clientId}`);
      toast.success("Cliente excluído com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro: " + (error.response?.data?.detail || "Erro ao excluir cliente"));
    }
  };

  const deleteLegalProcess = async (processId) => {
    if (!window.confirm("Tem certeza que deseja excluir este processo?")) return;
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/legal-processes/${processId}`);
      toast.success("Processo excluído com sucesso!");
      setSelectedLegalProcess(null);
      fetchData();
    } catch (error) {
      toast.error("Erro: " + (error.response?.data?.detail || "Erro ao excluir processo"));
    }
  };

  const payLegalHonorario = (processId) => {
    const proc = legalProcesses.find(p => p.id === processId);
    setPaymentDialog({
      open: true,
      kind: "honorario",
      processId,
      valor: parseFloat(proc?.valor_honorario) || 0,
      label: `Honorário - ${proc?.numero_protocolo || ""}`,
      parcelas: 1,
      first_due_date: new Date().toISOString().split('T')[0],
      interval_days: 30,
    });
  };

  const receiveLegalRPV = (processId) => {
    const proc = legalProcesses.find(p => p.id === processId);
    setPaymentDialog({
      open: true,
      kind: "rpv",
      processId,
      valor: parseFloat(proc?.rpv_valor) || 0,
      label: `RPV - ${proc?.rpv_protocolo || proc?.numero_protocolo || ""}`,
      parcelas: 1,
      first_due_date: new Date().toISOString().split('T')[0],
      interval_days: 30,
    });
  };

  const confirmPaymentDialog = async () => {
    if (!paymentDialog.open || !paymentDialog.processId) return;
    const parcelas = Math.max(1, parseInt(paymentDialog.parcelas) || 1);
    // À vista (1x) também envia first_due_date (data de previsão), pois agora gera venda pendente
    const body = parcelas > 1
      ? { parcelas, first_due_date: paymentDialog.first_due_date, interval_days: parseInt(paymentDialog.interval_days) || 30 }
      : { parcelas: 1, first_due_date: paymentDialog.first_due_date };
    try {
      const endpoint = paymentDialog.kind === "honorario" ? "pay-honorario" : "receive-rpv";
      const res = await axios.post(`${API}/companies/${selectedCompany.id}/legal-processes/${paymentDialog.processId}/${endpoint}`, body);
      toast.success(res.data?.message || "Registrado com sucesso");
      setPaymentDialog({ open: false });
      fetchData();
    } catch (error) {
      toast.error("Erro: " + (error.response?.data?.detail || "Erro ao registrar"));
    }
  };

  const addExame = () => {
    setLegalClientForm(prev => ({
      ...prev,
      exames_realizados: [...prev.exames_realizados, { nome: "", data_realizacao: "", data_validade: "" }]
    }));
  };

  const updateExame = (index, field, value) => {
    setLegalClientForm(prev => {
      const newExames = [...prev.exames_realizados];
      newExames[index] = { ...newExames[index], [field]: value };
      return { ...prev, exames_realizados: newExames };
    });
  };

  const removeExame = (index) => {
    setLegalClientForm(prev => ({
      ...prev,
      exames_realizados: prev.exames_realizados.filter((_, i) => i !== index)
    }));
  };

  const uploadLegalDocument = async (processId, documentType, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      
      await axios.post(
        `${API}/companies/${selectedCompany.id}/legal-processes/${processId}/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success("Documento anexado com sucesso!");
      fetchData();
      
      // Atualizar o processo selecionado
      const updatedProcess = await axios.get(`${API}/companies/${selectedCompany.id}/legal-processes/${processId}`);
      setSelectedLegalProcess(updatedProcess.data);
    } catch (error) {
      toast.error("Erro ao anexar documento");
    }
  };

  const downloadLegalDocument = async (processId, documentId, filename) => {
    try {
      const response = await axios.get(
        `${API}/companies/${selectedCompany.id}/legal-processes/${processId}/documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erro ao baixar documento");
    }
  };

  const deleteLegalDocument = async (processId, documentId) => {
    if (!window.confirm("Tem certeza que deseja excluir este documento?")) return;
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/legal-processes/${processId}/documents/${documentId}`);
      toast.success("Documento excluído com sucesso!");
      
      // Atualizar o processo selecionado
      const updatedProcess = await axios.get(`${API}/companies/${selectedCompany.id}/legal-processes/${processId}`);
      setSelectedLegalProcess(updatedProcess.data);
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir documento");
    }
  };

  const getAndamentoLabel = (andamento) => {
    const labels = {
      em_analise: "Em Análise",
      exigencia: "Exigência",
      concedido: "Concedido",
      negado: "Negado",
      justica: "Justiça"
    };
    return labels[andamento] || andamento;
  };

  const getAndamentoColor = (andamento) => {
    const colors = {
      em_analise: "bg-yellow-100 text-yellow-800",
      exigencia: "bg-orange-100 text-orange-800",
      concedido: "bg-green-100 text-green-800",
      negado: "bg-red-100 text-red-800",
      justica: "bg-purple-100 text-purple-800"
    };
    return colors[andamento] || "bg-gray-100 text-gray-800";
  };

  const getTipoProcessoLabel = (tipo) => {
    const labels = {
      // Administrativos
      loas: "LOAS",
      apos_idade: "Aposentadoria Por Idade",
      aux_doenca: "Auxílio Doença",
      pensao_morte: "Pensão Por Morte",
      aux_reclusao: "Auxílio Reclusão",
      apos_especial: "Aposentadoria Especial",
      // Legacy (manter compatibilidade)
      urbano: "Urbano",
      rural: "Rural",
      salario_maternidade: "Salário Maternidade",
      auxilio_doenca: "Auxílio Doença",
      aposentadoria: "Aposentadoria",
      // Judiciais
      processo_civil: "Processo Civil",
      processo_penal: "Processo Penal/Criminal",
      processo_trabalhista: "Processo Trabalhista",
      processo_previdenciario: "Processo Previdenciário",
      execucao_penal: "Processo de Execução Penal",
      conhecimento: "Processo de Conhecimento",
      execucao: "Processo de Execução",
      cautelar: "Processo Cautelar ou de Urgência",
      procedimento_comum: "Procedimento Comum",
      procedimento_especial: "Procedimento Especial",
      sumarissimo: "Sumaríssimo",
      criminal: "Criminal",
      civil: "Civil",
      trabalhista: "Trabalhista"
    };
    return labels[tipo] || tipo;
  };

  const getEtapaStatusLabel = (status) => {
    const labels = {
      pendente: "Pendente",
      em_andamento: "Em Andamento",
      concluido: "Concluído"
    };
    return labels[status] || status;
  };

  const getEtapaStatusColor = (status) => {
    const colors = {
      pendente: "bg-gray-100 text-gray-800",
      em_andamento: "bg-yellow-100 text-yellow-800",
      concluido: "bg-green-100 text-green-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getEtapaAtualLabel = (etapa) => {
    const labels = {
      nenhuma: "Nenhuma etapa iniciada",
      peticao_inicial: "Petição Inicial",
      contestacao: "Contestação",
      contra_razoes: "Contra Razões",
      recurso_inominato: "Recurso Inominado",
      replica_contestacao: "Réplica da Contestação",
      aguardando_sentenca: "Aguardando Sentença",
      sentenca_proferida: "Sentença Proferida",
      recurso: "Em Recurso",
      audiencia: "Audiência",
      rpv: "RPV",
      suspenso: "Suspenso",
      concluso_decisao: "Concluso para Decisão",
      concluso_despacho: "Concluso para Despacho",
      resposta_acusacao: "Resposta Acusação",
      revisao_criminal: "Revisão Criminal",
      transitado_julgado: "Transitado em Julgado",
      cumprimento_sentenca: "Cumprimento de Sentença",
      finalizado: "Finalizado"
    };
    return labels[etapa] || etapa || "Não definida";
  };

  // Fetch do Dashboard de Processo Judiciário
  const fetchLegalDashboard = async (dias = 7) => {
    if (!selectedCompany?.id) return;
    try {
      const res = await axios.get(
        `${API}/companies/${selectedCompany.id}/legal-processes/dashboard?dias=${dias}`
      );
      setLegalDashboard(res.data);
    } catch (e) {
      console.error("Erro ao carregar dashboard jurídico:", e);
    }
  };

  // Funções de Agendamento
  const resetAgendamentoForm = () => {
    setAgendamentoForm({
      process_id: "",
      tipo: "audiencia",
      titulo: "",
      data_hora: "",
      local: "",
      advogado_responsavel_id: "",
      descricao: "",
      notificar_email: true
    });
    setEditingAgendamento(null);
  };

  const handleAgendamentoSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAgendamento) {
        await axios.put(`${API}/companies/${selectedCompany.id}/agendamentos/${editingAgendamento.id}`, agendamentoForm);
        toast.success("Agendamento atualizado!");
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/agendamentos`, agendamentoForm);
        toast.success("Agendamento criado! Notificação enviada.");
      }
      resetAgendamentoForm();
      setShowAgendamentoForm(false);
      fetchData();
    } catch (error) {
      toast.error("Erro: " + (error.response?.data?.detail || "Erro ao salvar"));
    }
  };

  const startEditAgendamento = (ag) => {
    setEditingAgendamento(ag);
    setAgendamentoForm({
      process_id: ag.process_id || "",
      tipo: ag.tipo || "audiencia",
      titulo: ag.titulo || "",
      data_hora: ag.data_hora || "",
      local: ag.local || "",
      advogado_responsavel_id: ag.advogado_responsavel_id || "",
      descricao: ag.descricao || "",
      notificar_email: ag.notificar_email ?? true
    });
    setShowAgendamentoForm(true);
  };

  const deleteAgendamento = async (id) => {
    if (!window.confirm("Excluir este agendamento?")) return;
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/agendamentos/${id}`);
      toast.success("Agendamento excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const updateAgendamentoStatus = async (id, status) => {
    try {
      await axios.put(`${API}/companies/${selectedCompany.id}/agendamentos/${id}/status?status=${status}`);
      toast.success("Status atualizado!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const getTipoAgendamentoLabel = (tipo) => {
    const labels = { audiencia: 'Audiência', pericia_medica: 'Perícia Médica', consultar_social: 'Consultar Social' };
    return labels[tipo] || tipo;
  };

  const getStatusAgendamentoColor = (status) => {
    const colors = {
      pendente: "bg-yellow-100 text-yellow-800",
      realizado: "bg-green-100 text-green-800",
      cancelado: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  // ============ FIM FUNÇÕES PROCESSO JUDICIÁRIO ============

  // Construction functions
  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/companies/${selectedCompany.id}/construction/projects`, {
        ...projectForm,
        approved_value: parseFloat(projectForm.approved_value),
        estimated_cost: projectForm.estimated_cost ? parseFloat(projectForm.estimated_cost) : null
      });
      toast.success("Projeto criado com sucesso!");
      setProjectForm({ name: "", client_id: "", approved_value: "", estimated_cost: "", description: "" });
      setShowProjectForm(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao criar projeto");
    }
  };

  const startEditProject = (project) => {
    setEditingProject(project);
    setEditProjectForm({
      name: project.name,
      approved_value: project.approved_value.toString(),
      estimated_cost: project.estimated_cost ? project.estimated_cost.toString() : "",
      description: project.description || "",
      status: project.status || "planejamento"
    });
    setShowEditProjectForm(true);
  };

  const handleEditProjectSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCompany?.id || !editingProject?.id) return;
    try {
      const formData = new FormData();
      formData.append('name', editProjectForm.name);
      formData.append('approved_value', parseFloat(editProjectForm.approved_value));
      if (editProjectForm.estimated_cost) {
        formData.append('estimated_cost', parseFloat(editProjectForm.estimated_cost));
      }
      formData.append('status', editProjectForm.status);
      if (editProjectForm.description) {
        formData.append('description', editProjectForm.description);
      }

      await axios.put(`${API}/companies/${selectedCompany.id}/construction/projects/${editingProject.id}`, formData);
      toast.success("Projeto atualizado com sucesso!");
      setEditingProject(null);
      setEditProjectForm({ name: "", approved_value: "", estimated_cost: "", description: "", status: "planejamento" });
      setShowEditProjectForm(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar projeto");
    }
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    try {
      const materialData = {
        ...materialForm,
        unit_price: parseFloat(materialForm.unit_price)
      };
      
      if (editingMaterial) {
        await axios.put(`${API}/companies/${selectedCompany.id}/construction/materials/${editingMaterial.id}`, materialData);
        toast.success("Material atualizado com sucesso!");
        setEditingMaterial(null);
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/construction/materials`, materialData);
        toast.success("Material criado com sucesso!");
      }
      
      const wasEditing = !!editingMaterial;
      setMaterialForm({ name: "", unit: "unidade", unit_price: "", description: "", kind: "material" });
      // Mantém o dialog aberto para cadastrar mais itens em sequência (apenas no fluxo de criação)
      if (wasEditing) {
        setShowMaterialForm(false);
      }
      fetchData();
    } catch (error) {
      toast.error(editingMaterial ? "Erro ao atualizar material" : "Erro ao criar material");
    }
  };

  const startEditMaterial = (material) => {
    setMaterialForm({
      name: material.name,
      unit: material.unit,
      unit_price: material.unit_price.toString(),
      description: material.description || "",
      kind: material.kind || "material"
    });
    setEditingMaterial(material);
    setShowMaterialForm(true);
  };

  const deleteMaterial = async (materialId) => {
    if (window.confirm("Tem certeza que deseja excluir este material?")) {
      try {
        await axios.delete(`${API}/companies/${selectedCompany.id}/construction/materials/${materialId}`);
        toast.success("Material excluído com sucesso!");
        fetchData();
      } catch (error) {
        toast.error("Erro ao excluir material");
      }
    }
  };

  // ==================== PEDIDOS DE MATERIAL ====================
  
  const fetchMaterialOrders = async () => {
    if (!selectedCompany?.id) return;
    try {
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/material-orders`);
      setMaterialOrders(response.data);
    } catch (error) {
      console.error("Erro ao buscar pedidos de material:", error);
    }
  };

  // ==================== ESTOQUE ====================
  const fetchStockItems = async () => {
    if (!selectedCompany?.id) return;
    try {
      const params = new URLSearchParams();
      if (stockFilters.search) params.append("search", stockFilters.search);
      if (stockFilters.lowStockOnly) params.append("low_stock", "true");
      const res = await axios.get(`${API}/companies/${selectedCompany.id}/stock/items?${params.toString()}`);
      setStockItems(res.data);
    } catch (e) { console.error("Erro stock items", e); }
  };
  const fetchStockMovements = async () => {
    if (!selectedCompany?.id) return;
    try {
      const res = await axios.get(`${API}/companies/${selectedCompany.id}/stock/movements`);
      setStockMovements(res.data);
    } catch (e) { console.error("Erro stock movements", e); }
  };
  const fetchStockExits = async () => {
    if (!selectedCompany?.id) return;
    try {
      const res = await axios.get(`${API}/companies/${selectedCompany.id}/stock/exits`);
      setStockExits(res.data);
    } catch (e) { console.error("Erro stock exits", e); }
  };
  useEffect(() => {
    if (selectedCompany?.id) {
      fetchStockItems();
      fetchStockMovements();
      fetchStockExits();
    }
  }, [selectedCompany?.id]);

  const submitStockExit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        item_id: stockExitForm.item_id,
        quantity: parseFloat(stockExitForm.quantity) || 0,
        requester_name: stockExitForm.requester_name,
        project_id: stockExitForm.project_id,
        phase_id: stockExitForm.phase_id,
        observations: stockExitForm.observations,
      };
      await axios.post(`${API}/companies/${selectedCompany.id}/stock/exits`, payload);
      toast.success("Solicitação de saída criada — aguardando aprovação técnica");
      setShowStockExitForm(false);
      setStockExitForm({ item_id: "", quantity: "", requester_name: "", project_id: "", phase_id: "", observations: "" });
      fetchStockExits();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar solicitação");
    }
  };
  const approveStockExit = async (exitId) => {
    if (!window.confirm("Aprovar esta saída? O item será descontado do estoque e adicionado à medição.")) return;
    try {
      await axios.post(`${API}/companies/${selectedCompany.id}/stock/exits/${exitId}/approve`);
      toast.success("Saída aprovada");
      fetchStockExits();
      fetchStockItems();
      fetchStockMovements();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao aprovar");
    }
  };
  const rejectStockExit = async (exitId) => {
    const reason = window.prompt("Motivo da rejeição (opcional):");
    if (reason === null) return;
    try {
      await axios.post(`${API}/companies/${selectedCompany.id}/stock/exits/${exitId}/reject`, { reason });
      toast.success("Saída rejeitada");
      fetchStockExits();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao rejeitar");
    }
  };
  const submitStockAdjust = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        item_id: stockAdjustForm.item_id,
        quantity: parseFloat(stockAdjustForm.quantity) || 0,
        reason: stockAdjustForm.reason,
      };
      await axios.post(`${API}/companies/${selectedCompany.id}/stock/adjustments`, payload);
      toast.success("Ajuste registrado");
      setShowStockAdjustForm(false);
      setStockAdjustForm({ item_id: "", quantity: "", reason: "" });
      fetchStockItems();
      fetchStockMovements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro no ajuste");
    }
  };
  const updateStockItem = async (e) => {
    e.preventDefault();
    if (!editingStockItem) return;
    try {
      const payload = {
        name: editingStockItem.name,
        unit: editingStockItem.unit,
        supplier_id: editingStockItem.supplier_id || null,
        min_quantity: parseFloat(editingStockItem.min_quantity) || 0,
        unit_price: parseFloat(editingStockItem.unit_price) || 0,
        kind: editingStockItem.kind || "material",
      };
      await axios.put(`${API}/companies/${selectedCompany.id}/stock/items/${editingStockItem.id}`, payload);
      toast.success("Item atualizado");
      setShowStockEditForm(false);
      setEditingStockItem(null);
      fetchStockItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };
  const deleteStockItem = async (itemId) => {
    if (!window.confirm("Excluir este item? Só é possível se o saldo for zero.")) return;
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/stock/items/${itemId}`);
      toast.success("Item excluído");
      fetchStockItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    }
  };



  const handleMaterialOrderSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCompany?.id) return;
    
    // Validar itens
    const validItems = materialOrderForm.items.filter(item => 
      item.description && item.quantity && item.unit_price
    );
    
    if (validItems.length === 0) {
      toast.error("Adicione pelo menos um item ao pedido");
      return;
    }
    
    try {
      const payload = {
        project_id: materialOrderForm.destination === "estoque" ? null : materialOrderForm.project_id,
        phase_id: materialOrderForm.destination === "estoque" ? null : materialOrderForm.phase_id,
        supplier_id: materialOrderForm.supplier_id,
        observations: materialOrderForm.observations,
        kind: materialOrderForm.kind || "material",
        destination: materialOrderForm.destination || "obra",
        items: validItems.map(item => {
          const qty = parseFloat(item.quantity) || 0;
          const unitPrice = parseFloat(item.unit_price) || 0;
          const discount = parseFloat(item.discount) || 0;
          return {
            description: item.description,
            unit: item.unit,
            quantity: qty,
            unit_price: unitPrice,
            discount: discount,
            total_price: Math.max(qty * unitPrice - discount, 0)
          };
        })
      };
      
      if (editingMaterialOrder && selectedMaterialOrder?.id) {
        await axios.put(`${API}/companies/${selectedCompany.id}/material-orders/${selectedMaterialOrder.id}`, payload);
        toast.success("Pedido atualizado com sucesso!");
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/material-orders`, payload);
        toast.success("Pedido criado com sucesso!");
      }
      setShowMaterialOrderForm(false);
      setEditingMaterialOrder(false);
      setMaterialOrderForm({
        project_id: "",
        phase_id: "",
        supplier_id: "",
        observations: "",
        kind: "material",
        destination: "obra",
        items: [{ id: Date.now().toString(), description: "", unit: "unidade", quantity: "", unit_price: "", discount: "" }]
      });
      // Mantém o usuário na aba "pedidos" do módulo construção
      setCmActiveTab("pedidos");
      fetchMaterialOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar pedido");
    }
  };

  const addOrderItem = () => {
    setMaterialOrderForm({
      ...materialOrderForm,
      items: [...materialOrderForm.items, { 
        id: Date.now().toString(), 
        description: "", 
        unit: "unidade", 
        quantity: "", 
        unit_price: "",
        discount: ""
      }]
    });
  };

  const removeOrderItem = (itemId) => {
    if (materialOrderForm.items.length > 1) {
      setMaterialOrderForm({
        ...materialOrderForm,
        items: materialOrderForm.items.filter(item => item.id !== itemId)
      });
    }
  };

  const updateOrderItem = (itemId, field, value) => {
    setMaterialOrderForm({
      ...materialOrderForm,
      items: materialOrderForm.items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    });
  };

  const handleOrderDocumentUpload = async (orderId, documentType, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    
    setOrderDocumentUploading(true);
    try {
      await axios.post(
        `${API}/companies/${selectedCompany.id}/material-orders/${orderId}/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success("Documento anexado com sucesso!");
      fetchMaterialOrders();
      // Atualizar o pedido selecionado
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/material-orders/${orderId}`);
      setSelectedMaterialOrder(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao anexar documento");
    } finally {
      setOrderDocumentUploading(false);
    }
  };

  const approveTechnical = async (orderId) => {
    try {
      const response = await axios.post(`${API}/companies/${selectedCompany.id}/material-orders/${orderId}/approve-technical`);
      toast.success(response.data.message);
      fetchMaterialOrders();
      fetchData(); // Atualizar materiais e contas a pagar
      if (selectedMaterialOrder?.id === orderId) {
        const orderRes = await axios.get(`${API}/companies/${selectedCompany.id}/material-orders/${orderId}`);
        setSelectedMaterialOrder(orderRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao aprovar pedido");
    }
  };

  // Abre o modal de aprovação financeira
  const openFinancialApproval = (orderId) => {
    // Resetar formulário com valores padrão
    const today = new Date().toISOString().split('T')[0];
    setFinancialApprovalForm({
      due_date: today,
      expense_type: "material",
      is_installment: false,
      total_installments: 1,
      installments: []
    });
    setShowFinancialApprovalForm(true);
  };

  // Processa a aprovação financeira com os dados do formulário
  const submitFinancialApproval = async () => {
    const orderId = selectedMaterialOrder?.id;
    if (!orderId) return;

    try {
      let payload = {
        due_date: financialApprovalForm.due_date,
        expense_type: financialApprovalForm.expense_type,
        is_installment: financialApprovalForm.is_installment,
        total_installments: financialApprovalForm.total_installments,
        installments: financialApprovalForm.installments
      };

      const isEditMode = !!financialApprovalForm._edit_mode;
      const response = isEditMode
        ? await axios.put(`${API}/companies/${selectedCompany.id}/material-orders/${orderId}/payment`, payload)
        : await axios.post(`${API}/companies/${selectedCompany.id}/material-orders/${orderId}/approve-financial`, payload);
      toast.success(response.data.message);
      setShowFinancialApprovalForm(false);
      fetchMaterialOrders();
      fetchData(); // Atualizar contas a pagar
      
      // Atualizar pedido selecionado
      const orderRes = await axios.get(`${API}/companies/${selectedCompany.id}/material-orders/${orderId}`);
      setSelectedMaterialOrder(orderRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar forma de pagamento");
    }
  };

  // Abre o dialog em modo edição de forma de pagamento (pedido já aprovado)
  const openEditPayment = () => {
    const order = selectedMaterialOrder;
    if (!order) return;
    const info = order.payment_info || {};
    const today = new Date().toISOString().split('T')[0];
    setFinancialApprovalForm({
      _edit_mode: true,
      due_date: today,
      expense_type: info.expense_type || "material",
      is_installment: !!info.is_installment,
      total_installments: info.total_installments || 1,
      installments: [],
    });
    setShowFinancialApprovalForm(true);
  };

  // Gerar parcelas automaticamente
  const generateInstallments = (numInstallments, totalAmount, startDate) => {
    const installments = [];
    const installmentAmount = parseFloat((totalAmount / numInstallments).toFixed(2));
    
    for (let i = 0; i < numInstallments; i++) {
      const dueDate = new Date(startDate);
      dueDate.setMonth(dueDate.getMonth() + i);
      
      installments.push({
        amount: i === numInstallments - 1 
          ? parseFloat((totalAmount - (installmentAmount * (numInstallments - 1))).toFixed(2))
          : installmentAmount,
        due_date: dueDate.toISOString().split('T')[0]
      });
    }
    
    return installments;
  };

  // Atualiza parcela individual
  const updateInstallment = (index, field, value) => {
    const newInstallments = [...financialApprovalForm.installments];
    newInstallments[index] = { ...newInstallments[index], [field]: value };
    setFinancialApprovalForm({ ...financialApprovalForm, installments: newInstallments });
  };

  const rejectOrder = async (orderId) => {
    if (!window.confirm("Tem certeza que deseja rejeitar este pedido?")) return;
    try {
      const res = await axios.post(`${API}/companies/${selectedCompany.id}/material-orders/${orderId}/reject`);
      const removed = res?.data?.materials_removed;
      toast.success(removed ? `Pedido rejeitado. ${removed} item(ns) removido(s) da medição.` : "Pedido rejeitado");
      fetchMaterialOrders();
      fetchData();
      setSelectedMaterialOrder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao rejeitar pedido");
    }
  };

  // Função para iniciar edição do pedido de material
  const startEditMaterialOrder = (order) => {
    setMaterialOrderForm({
      orderId: order.id,
      project_id: order.project_id || "",
      phase_id: order.phase_id || "",
      supplier_id: order.supplier_id || "",
      observations: order.observations || "",
      kind: order.kind || "material",
      destination: order.destination || "obra",
      items: order.items?.map((item, idx) => ({
        id: idx.toString(),
        description: item.description,
        unit: item.unit,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount || 0
      })) || [{ id: "0", description: "", unit: "unidade", quantity: "", unit_price: "", discount: "" }]
    });
    setEditingMaterialOrder(true);
    setSelectedMaterialOrder(null);
    setShowMaterialOrderForm(true);
  };

  // Função para atualizar pedido de material
  const updateMaterialOrder = async (e) => {
    e.preventDefault();
    if (!selectedMaterialOrder && !editingMaterialOrder) return;
    
    const orderId = editingMaterialOrder ? materialOrderForm.orderId : selectedMaterialOrder.id;
    
    const validItems = materialOrderForm.items.filter(item => 
      item.description && item.quantity && item.unit_price
    );
    
    if (validItems.length === 0) {
      toast.error("Adicione pelo menos um item válido ao pedido");
      return;
    }
    
    try {
      const response = await axios.put(
        `${API}/companies/${selectedCompany.id}/material-orders/${orderId}`,
        {
          project_id: materialOrderForm.project_id,
          phase_id: materialOrderForm.phase_id,
          supplier_id: materialOrderForm.supplier_id,
          observations: materialOrderForm.observations,
          items: validItems.map(item => ({
            description: item.description,
            unit: item.unit,
            quantity: parseFloat(item.quantity) || 0,
            unit_price: parseFloat(item.unit_price) || 0,
            discount: parseFloat(item.discount) || 0
          }))
        }
      );
      toast.success("Pedido atualizado com sucesso!");
      setShowMaterialOrderForm(false);
      setEditingMaterialOrder(false);
      setMaterialOrderForm({
        project_id: "",
        phase_id: "",
        supplier_id: "",
        observations: "",
        items: [{ id: Date.now().toString(), description: "", unit: "unidade", quantity: "", unit_price: "", discount: "" }]
      });
      fetchMaterialOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar pedido");
    }
  };

  // Função para excluir documento do pedido
  const deleteOrderDocument = async (orderId, documentId) => {
    if (!window.confirm("Tem certeza que deseja excluir este documento?")) return;
    
    try {
      await axios.delete(
        `${API}/companies/${selectedCompany.id}/material-orders/${orderId}/documents/${documentId}`
      );
      toast.success("Documento excluído com sucesso!");
      
      // Atualizar o pedido selecionado
      const orderRes = await axios.get(`${API}/companies/${selectedCompany.id}/material-orders/${orderId}`);
      setSelectedMaterialOrder(orderRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir documento");
    }
  };

  const downloadOrderDocument = async (orderId, documentId, filename) => {
    try {
      const response = await axios.get(
        `${API}/companies/${selectedCompany.id}/material-orders/${orderId}/documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      
      // Verificar se recebeu dados
      if (!response.data || response.data.size === 0) {
        toast.error("Arquivo vazio ou não encontrado");
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro download:", error);
      if (error.response?.status === 404) {
        toast.error("Arquivo não encontrado no servidor");
      } else {
        toast.error("Erro ao baixar documento: " + (error.message || "Erro desconhecido"));
      }
    }
  };

  // Buscar fases pendentes de um projeto
  const getPendingPhases = (projectId) => {
    return projectSchedule.filter(
      phase => phase.project_id === projectId && phase.status !== 'concluido'
    );
  };

  const fetchProjectDetails = async (projectId) => {
    if (!selectedCompany?.id || !projectId) return;
    try {
      console.log(`Fetching project details for project: ${projectId}`);
      const [scheduleRes, usageRes, documentsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${projectId}/schedule`),
        axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${projectId}/material-usage`),
        axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${projectId}/documents`),
        axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${projectId}/services`)
      ]);
      console.log(`Materiais recebidos:`, usageRes.data);
      console.log(`Total de materiais: ${usageRes.data.length}`);
      setProjectSchedule(scheduleRes.data);
      setMaterialUsage([...usageRes.data]); // Força nova referência para garantir re-render
      setProjectDocuments(documentsRes.data);
      setPhaseServices(servicesRes.data);
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
      toast.error("Erro ao buscar detalhes do projeto");
    }
  };

  const selectProject = (project) => {
    setSelectedProject(project);
    fetchProjectDetails(project.id);
  };

  const handlePhaseSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCompany?.id || !selectedProject?.id) return;
    try {
      await axios.post(`${API}/companies/${selectedCompany.id}/construction/schedule`, {
        ...phaseForm,
        project_id: selectedProject.id,
        estimated_cost: phaseForm.estimated_cost ? parseFloat(phaseForm.estimated_cost) : null,
        start_date: phaseForm.start_date || null,
        end_date: phaseForm.end_date || null,
        order: parseInt(phaseForm.order)
      });
      toast.success("Fase criada com sucesso!");
      setPhaseForm({ phase_name: "", description: "", estimated_cost: "", start_date: "", end_date: "", order: 1 });
      setShowPhaseForm(false);
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error("Erro ao criar fase");
    }
  };

  const startEditPhase = (phase) => {
    setEditingPhase(phase);
    setPhaseForm({
      phase_name: phase.phase_name,
      description: phase.description || "",
      estimated_cost: phase.estimated_cost ? phase.estimated_cost.toString() : "",
      start_date: phase.start_date ? phase.start_date.split('T')[0] : "",
      end_date: phase.end_date ? phase.end_date.split('T')[0] : "",
      order: phase.order.toString()
    });
    setShowEditPhaseForm(true);
  };

  const handleEditPhaseSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCompany?.id || !editingPhase?.id || !selectedProject?.id) return;
    try {
      const formData = new FormData();
      formData.append('phase_name', phaseForm.phase_name);
      if (phaseForm.description) {
        formData.append('description', phaseForm.description);
      }
      if (phaseForm.estimated_cost) {
        formData.append('estimated_cost', parseFloat(phaseForm.estimated_cost));
      }
      if (phaseForm.start_date) {
        formData.append('start_date', phaseForm.start_date);
      }
      if (phaseForm.end_date) {
        formData.append('end_date', phaseForm.end_date);
      }
      formData.append('status', 'pendente');

      await axios.put(`${API}/companies/${selectedCompany.id}/construction/schedule/${editingPhase.id}`, formData);
      toast.success("Fase atualizada com sucesso!");
      setEditingPhase(null);
      setPhaseForm({ phase_name: "", description: "", estimated_cost: "", start_date: "", end_date: "", order: 1 });
      setShowEditPhaseForm(false);
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error("Erro ao atualizar fase");
    }
  };

  // Função para encerrar fase e calcular custo real
  const closePhase = async (phaseId) => {
    if (!window.confirm("Tem certeza que deseja encerrar esta fase? O custo será calculado e a fase será marcada como concluída.")) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/companies/${selectedCompany.id}/construction/schedule/${phaseId}/close`);
      const result = response.data;
      
      // Mostrar resumo do encerramento
      if (result.within_budget) {
        toast.success(
          `Fase "${result.phase_name}" encerrada com sucesso!\n` +
          `Custo estimado: ${formatCurrency(result.estimated_cost)}\n` +
          `Custo realizado: ${formatCurrency(result.actual_cost)}\n` +
          `Economia: ${formatCurrency(result.estimated_cost - result.actual_cost)}`
        );
      } else {
        toast.warning(
          `Fase "${result.phase_name}" encerrada!\n` +
          `Custo estimado: ${formatCurrency(result.estimated_cost)}\n` +
          `Custo realizado: ${formatCurrency(result.actual_cost)}\n` +
          `Excedente: ${formatCurrency(result.actual_cost - result.estimated_cost)} (${result.variance_percentage}%)`
        );
      }
      
      // Recarregar dados do projeto
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error("Erro ao encerrar fase: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleMaterialUsageSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCompany?.id || !selectedProject?.id || !selectedPhase?.id) {
      console.error("Missing IDs:", { company: selectedCompany?.id, project: selectedProject?.id, phase: selectedPhase?.id });
      return;
    }
    try {
      const payload = {
        ...materialUsageForm,
        project_id: selectedProject.id,
        phase_id: selectedPhase.id,
        quantity: parseFloat(materialUsageForm.quantity)
      };
      console.log("Enviando material:", payload);
      const response = await axios.post(`${API}/companies/${selectedCompany.id}/construction/material-usage`, payload);
      console.log("Material adicionado:", response.data);
      toast.success("Material adicionado à fase com sucesso!");
      setMaterialUsageForm({ material_id: "", quantity: "", observations: "" });
      setShowMaterialUsageForm(false);
      // Aguardar um pequeno delay antes de recarregar os dados
      setTimeout(() => {
        fetchProjectDetails(selectedProject.id);
      }, 100);
    } catch (error) {
      console.error("Erro ao adicionar material:", error);
      toast.error("Erro ao adicionar material à fase");
    }
  };

  const handleServiceUsageSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCompany?.id || !selectedProject?.id || !selectedPhase?.id) return;
    try {
      const serviceData = {
        phase_id: selectedPhase.id,
        service_name: serviceUsageForm.service_name,
        unit: serviceUsageForm.unit,
        quantity: parseFloat(serviceUsageForm.quantity),
        unit_price: parseFloat(serviceUsageForm.unit_price),
        supplier_name: serviceUsageForm.supplier_name,
        description: serviceUsageForm.description,
        observations: serviceUsageForm.observations
      };

      if (editingPhaseService) {
        // Update existing service
        await axios.put(
          `${API}/companies/${selectedCompany.id}/construction/projects/${selectedProject.id}/phases/${selectedPhase.id}/services/${editingPhaseService.id}`,
          new URLSearchParams(serviceData)
        );
        toast.success("Serviço atualizado com sucesso!");
        setEditingPhaseService(null);
      } else {
        // Create new service
        await axios.post(`${API}/companies/${selectedCompany.id}/construction/projects/${selectedProject.id}/phases/${selectedPhase.id}/services`, serviceData);
        toast.success("Serviço adicionado à fase com sucesso!");
      }
      
      setServiceUsageForm({ 
        service_name: "", 
        unit: "horas", 
        quantity: "", 
        unit_price: "", 
        supplier_name: "", 
        description: "", 
        observations: "" 
      });
      setShowServiceUsageForm(false);
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error(editingPhaseService ? "Erro ao atualizar serviço" : "Erro ao adicionar serviço à fase");
    }
  };

  const handlePhaseDocumentUpload = async (phaseId, documentType, file) => {
    if (!selectedCompany?.id || !selectedProject?.id) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);

      await axios.post(`${API}/companies/${selectedCompany.id}/construction/projects/${selectedProject.id}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success("Documento anexado com sucesso!");
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error("Erro ao anexar documento: " + (error.response?.data?.detail || "Erro desconhecido"));
    }
  };

  const getPhaseDocuments = (documentType) => {
    return projectDocuments.filter(doc => doc.document_type === documentType);
  };

  const getPhaseUsage = (phaseId) => {
    return materialUsage.filter(usage => usage.phase_id === phaseId);
  };

  const getPhaseServices = (phaseId) => {
    return phaseServices.filter(service => service.phase_id === phaseId);
  };

  // Edit and delete functions for phase services
  const startEditPhaseService = (service) => {
    setServiceUsageForm({
      service_name: service.service_name,
      unit: service.unit,
      quantity: service.quantity.toString(),
      unit_price: service.unit_price.toString(),
      supplier_name: service.supplier_name || "",
      description: service.description || "",
      observations: service.observations || ""
    });
    setEditingPhaseService(service);
    setSelectedPhase({ id: service.phase_id, phase_name: service.phase_name });
    setShowServiceUsageForm(true);
  };

  const deletePhaseService = async (serviceId) => {
    if (!window.confirm("Tem certeza que deseja excluir este serviço?")) return;
    
    const service = phaseServices.find(s => s.id === serviceId);
    if (!service) return;

    try {
      await axios.delete(
        `${API}/companies/${selectedCompany.id}/construction/projects/${selectedProject.id}/phases/${service.phase_id}/services/${serviceId}`
      );
      toast.success("Serviço excluído com sucesso!");
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error("Erro ao excluir serviço");
    }
  };

  // Accounts Payable functions
  const handleAccountPayableSubmit = async (e) => {
    e.preventDefault();
    try {
      // Se selecionou um fornecedor, pegar o nome dele
      let creditorName = accountForm.creditor_name;
      if (accountForm.supplier_id) {
        const selectedSupplier = suppliers.find(s => s.id === accountForm.supplier_id);
        if (selectedSupplier) {
          creditorName = selectedSupplier.name;
        }
      }
      
      const formData = {
        ...accountForm,
        creditor_name: creditorName,
        amount: parseFloat(accountForm.amount)
      };
      
      if (editingAccount) {
        await axios.put(`${API}/companies/${selectedCompany.id}/accounts-payable/${editingAccount.id}`, formData);
        toast.success("Conta a pagar atualizada com sucesso!");
        setEditingAccount(null);
      } else {
        await axios.post(`${API}/companies/${selectedCompany.id}/accounts-payable`, formData);
        toast.success("Conta a pagar criada com sucesso!");
      }
      
      setAccountForm({ creditor_name: "", supplier_id: "", amount: "", due_date: "", expense_type: "", description: "" });
      setShowAccountPayableForm(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar conta a pagar");
    }
  };

  const startEditAccount = (account) => {
    setEditingAccount(account);
    setAccountForm({
      creditor_name: account.creditor_name,
      supplier_id: account.supplier_id || "",
      amount: account.amount.toString(),
      due_date: account.due_date,
      expense_type: account.expense_type,
      description: account.description || ""
    });
    setShowAccountPayableForm(true);
  };

  // Aprovar pagamento (apenas admins) — agora não exige data
  const openApprovalDialog = (account) => {
    setApprovalAccount(account);
    setScheduledPaymentDate(""); // vazia — usuário pode aprovar sem programar
    setShowApprovalDialog(true);
  };

  const approvePayment = async () => {
    if (!approvalAccount) return;
    
    try {
      const payload = scheduledPaymentDate ? { scheduled_payment_date: scheduledPaymentDate } : {};
      const res = await axios.patch(
        `${API}/companies/${selectedCompany.id}/accounts-payable/${approvalAccount.id}/approve`,
        payload
      );
      toast.success(res.data?.message || "Pagamento aprovado!");
      setShowApprovalDialog(false);
      setApprovalAccount(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao aprovar pagamento");
    }
  };

  // Verificar se usuário é admin
  const isUserAdmin = () => {
    if (!user) return false;
    if (user.role === "admin") return true;
    // Verificar se é dono da empresa
    if (selectedCompany && user.companies?.includes(selectedCompany.id)) {
      // Se criou a empresa, é admin
      return true;
    }
    return false;
  };

  // Abrir modal de relatório
  const openReportDialog = () => {
    setReportFilters({
      status: "all",
      dateFrom: "",
      dateTo: ""
    });
    setShowReportDialog(true);
  };

  // Gerar relatório PDF
  const generateAccountsReport = async () => {
    const params = new URLSearchParams();
    if (reportFilters.status !== "all") {
      params.append("status", reportFilters.status);
    }
    if (reportFilters.dateFrom) {
      params.append("date_from", reportFilters.dateFrom);
    }
    if (reportFilters.dateTo) {
      params.append("date_to", reportFilters.dateTo);
    }
    
    try {
      const response = await axios.get(
        `${API}/companies/${selectedCompany.id}/accounts-payable/report/pdf?${params.toString()}`,
        { responseType: 'text' }
      );
      
      // Abrir HTML em nova janela para impressão/PDF usando Blob URL (evita document.write XSS vector)
      const blob = new Blob([response.data], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, '_blank');
      // Auto print após o HTML carregar
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          setTimeout(() => {
            printWindow.print();
            // Liberar a URL do blob depois de um tempo
            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
          }, 300);
        });
      }
      
      setShowReportDialog(false);
    } catch (error) {
      toast.error("Erro ao gerar relatório");
    }
  };

  const markAsPaid = async (accountId) => {
    // Abre o dialog para selecionar a conta bancária
    const account = accountsPayable.find(a => a.id === accountId);
    if (!account) return toast.error("Conta não encontrada");
    const defaultBank = bankAccounts.find(b => b.is_default);
    setPayDialog({ open: true, account, bank_account_id: defaultBank?.id || (bankAccounts[0]?.id || "") });
  };

  const confirmPayAccount = async () => {
    if (!payDialog.bank_account_id) return toast.error("Selecione a conta bancária");
    try {
      const response = await axios.patch(
        `${API}/companies/${selectedCompany.id}/accounts-payable/${payDialog.account.id}/pay?bank_account_id=${payDialog.bank_account_id}`
      );
      toast.success(`Conta paga · saída de ${formatCurrency(response.data.transaction_amount)} debitada da conta selecionada.`);
      setPayDialog({ open: false, account: null, bank_account_id: "" });
      fetchData();
      // Refresh bank accounts for updated balance
      try {
        const bankRes = await axios.get(`${API}/companies/${selectedCompany.id}/bank-accounts`);
        setBankAccounts(bankRes.data || []);
      } catch (e) { /* silencioso */ }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Erro ao marcar conta como paga");
    }
  };

  // Pagar várias contas selecionadas em lote (abre dialog para escolher conta bancária)
  const payBatch = async () => {
    const ids = Array.from(programadosSelecionados);
    if (ids.length === 0) return;
    const total = accountsPayable
      .filter(a => ids.includes(a.id))
      .reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    const defaultBank = bankAccounts.find(b => b.is_default);
    setBatchPayDialog({
      open: true,
      ids,
      total,
      bank_account_id: defaultBank?.id || (bankAccounts[0]?.id || "")
    });
  };

  const confirmBatchPay = async () => {
    if (!batchPayDialog.bank_account_id) return toast.error("Selecione a conta bancária");
    try {
      const res = await axios.post(`${API}/companies/${selectedCompany.id}/accounts-payable/pay-batch`, {
        account_ids: batchPayDialog.ids,
        bank_account_id: batchPayDialog.bank_account_id,
      });
      toast.success(res.data.message || `${batchPayDialog.ids.length} contas pagas`);
      if (res.data.errors && res.data.errors.length > 0) {
        toast.error(`${res.data.errors.length} erro(s) ao processar algumas contas`);
      }
      setProgramadosSelecionados(new Set());
      setBatchPayDialog({ open: false, ids: [], bank_account_id: "", total: 0 });
      fetchData();
      try {
        const bankRes = await axios.get(`${API}/companies/${selectedCompany.id}/bank-accounts`);
        setBankAccounts(bankRes.data || []);
      } catch (e) { /* silencioso */ }
    } catch (error) {
      toast.error("Erro ao processar pagamento em lote: " + (error.response?.data?.detail || ""));
    }
  };

  // Programar/reprogramar data de pagamento de uma conta
  const openProgramDialog = (account) => {
    const current = account.scheduled_payment_date ? new Date(account.scheduled_payment_date).toISOString().split('T')[0] : "";
    setProgramDialog({
      open: true,
      accountId: account.id,
      currentDate: current,
      date: current || new Date().toISOString().split('T')[0],
      creditor: account.creditor_name,
      amount: account.amount,
    });
  };
  const submitProgram = async () => {
    if (!programDialog.accountId || !programDialog.date) {
      toast.error("Selecione uma data");
      return;
    }
    try {
      await axios.patch(
        `${API}/companies/${selectedCompany.id}/accounts-payable/${programDialog.accountId}/schedule`,
        { scheduled_payment_date: programDialog.date }
      );
      toast.success(programDialog.currentDate ? "Data reprogramada" : "Pagamento programado");
      setProgramDialog({ open: false, accountId: null, currentDate: "", date: "" });
      fetchData();
    } catch (error) {
      toast.error("Erro ao programar: " + (error.response?.data?.detail || ""));
    }
  };

  const deleteAccount = async (accountId) => {
    if (window.confirm("Tem certeza que deseja excluir esta conta a pagar?")) {
      try {
        await axios.delete(`${API}/companies/${selectedCompany.id}/accounts-payable/${accountId}`);
        toast.success("Conta a pagar excluída com sucesso!");
        fetchData();
      } catch (error) {
        toast.error("Erro ao excluir conta a pagar");
      }
    }
  };

  // Budget upload functions
  const handleBudgetUpload = async () => {
    if (!budgetFile || !budgetUploadProject) {
      toast.error("Selecione um arquivo de planilha");
      return;
    }

    setBudgetUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', budgetFile);

      const response = await axios.post(
        `${API}/companies/${selectedCompany.id}/construction/projects/${budgetUploadProject.id}/budget/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toast.success(`Planilha de orçamento importada com sucesso! ${response.data.items_processed} itens processados.`);
      
      // Reset form
      setBudgetFile(null);
      setShowBudgetUpload(false);
      setBudgetUploadProject(null);
      
      // Refresh data to show updated project values
      fetchData();
      
    } catch (error) {
      console.error('Error uploading budget:', error);
      toast.error("Erro ao importar planilha: " + (error.response?.data?.detail || error.message));
    } finally {
      setBudgetUploading(false);
    }
  };

  const openBudgetUpload = (project) => {
    setBudgetUploadProject(project);
    setShowBudgetUpload(true);
  };

  // Phase services functions
  const fetchPhaseServices = async (phaseId) => {
    if (!selectedProject?.id || !phaseId) return;
    try {
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${selectedProject.id}/phases/${phaseId}/services`);
      setPhaseServices(response.data);
    } catch (error) {
      console.error('Error fetching phase services:', error);
    }
  };

  const handlePhaseServiceSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPhaseForService?.id) {
      toast.error("Nenhuma fase selecionada");
      return;
    }

    if (!selectedProject?.id) {
      toast.error("Projeto não encontrado");
      return;
    }

    // Validate required fields
    if (!phaseServiceForm.service_name.trim()) {
      toast.error("Nome do serviço é obrigatório");
      return;
    }

    if (!phaseServiceForm.quantity || parseFloat(phaseServiceForm.quantity) <= 0) {
      toast.error("Quantidade deve ser maior que zero");
      return;
    }

    if (!phaseServiceForm.unit_price || parseFloat(phaseServiceForm.unit_price) <= 0) {
      toast.error("Preço unitário deve ser maior que zero");
      return;
    }

    try {
      const serviceData = {
        phase_id: selectedPhaseForService.id,
        service_name: phaseServiceForm.service_name.trim(),
        description: phaseServiceForm.description?.trim() || null,
        unit: phaseServiceForm.unit,
        quantity: parseFloat(phaseServiceForm.quantity),
        unit_price: parseFloat(phaseServiceForm.unit_price),
        supplier_name: phaseServiceForm.supplier_name?.trim() || null,
        observations: phaseServiceForm.observations?.trim() || null
      };

      console.log("Submitting service data:", serviceData);
      console.log("Project ID:", selectedProject.id);
      console.log("Company ID:", selectedCompany.id);
      console.log("Phase ID:", selectedPhaseForService.id);

      if (editingService) {
        const response = await axios.put(
          `${API}/companies/${selectedCompany.id}/construction/projects/${selectedProject.id}/phases/${selectedPhaseForService.id}/services/${editingService.id}`,
          new URLSearchParams(serviceData)
        );
        console.log("Service updated:", response.data);
        toast.success("Serviço atualizado com sucesso!");
      } else {
        const response = await axios.post(
          `${API}/companies/${selectedCompany.id}/construction/projects/${selectedProject.id}/phases/${selectedPhaseForService.id}/services`,
          serviceData,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        console.log("Service created:", response.data);
        toast.success("Serviço adicionado com sucesso!");
      }

      // Reset form
      setPhaseServiceForm({
        service_name: "",
        description: "",
        unit: "horas",
        quantity: "",
        unit_price: "",
        supplier_name: "",
        observations: ""
      });
      setEditingService(null);
      setShowServiceForm(false);  // Close the modal after success
      
      // Refresh services for the phase
      await fetchPhaseServices(selectedPhaseForService.id);
      
      console.log("Form submitted successfully, services refreshed");
      
    } catch (error) {
      console.error("Error submitting service:", error);
      console.error("Error details:", error.response?.data);
      toast.error("Erro ao salvar serviço: " + (error.response?.data?.detail || error.message));
    }
  };

  const startEditPhaseServiceItem = (service) => {
    console.log("Starting edit service:", service);
    if (!service) {
      toast.error("Erro: serviço não encontrado para edição");
      return;
    }
    
    // Ensure we have the correct phase selected
    if (service.phase_id && service.phase_id !== selectedPhaseForService?.id) {
      console.log("Setting phase for service:", service.phase_id);
      // Find the phase in the current project phases
      const phases = projectSchedule.filter(s => s.project_id === selectedProject?.id);
      const servicePhase = phases.find(p => p.id === service.phase_id);
      if (servicePhase) {
        setSelectedPhaseForService(servicePhase);
      }
    }
    
    setEditingService(service);
    setPhaseServiceForm({
      service_name: service.service_name || "",
      description: service.description || "",
      unit: service.unit || "horas",
      quantity: service.quantity ? service.quantity.toString() : "",
      unit_price: service.unit_price ? service.unit_price.toString() : "",
      supplier_name: service.supplier_name || "",
      observations: service.observations || ""
    });
    setShowServiceForm(true);
    console.log("Edit form populated:", {
      service_name: service.service_name,
      quantity: service.quantity,
      unit_price: service.unit_price,
      editing: true
    });
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm("Tem certeza que deseja excluir este serviço?")) return;
    
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/construction/projects/${selectedProject.id}/phases/${selectedPhaseForService.id}/services/${serviceId}`);
      toast.success("Serviço excluído com sucesso!");
      fetchPhaseServices(selectedPhaseForService.id);
    } catch (error) {
      toast.error("Erro ao excluir serviço");
    }
  };

  const openServiceForm = (phase) => {
    setSelectedPhaseForService(phase);
    fetchPhaseServices(phase.id);
    setShowServiceForm(true);
  };

  // Filter functions
  const filteredTransactions = transactions.filter(t => {
    const f = filters.transactions;
    const matchSearch = f.search === "" || 
      t.description?.toLowerCase().includes(f.search.toLowerCase()) ||
      t.client_name?.toLowerCase().includes(f.search.toLowerCase()) ||
      t.supplier_name?.toLowerCase().includes(f.search.toLowerCase());
    const matchType = f.type === "all" || t.type === f.type;
    const matchBank = !f.bank_account_id || f.bank_account_id === "all" || t.bank_account_id === f.bank_account_id;
    const matchDateFrom = f.dateFrom === "" || new Date(t.date) >= new Date(f.dateFrom);
    const matchDateTo = f.dateTo === "" || new Date(t.date) <= new Date(f.dateTo + "T23:59:59");
    return matchSearch && matchType && matchBank && matchDateFrom && matchDateTo;
  });

  const filteredClients = clients.filter(c => {
    const f = filters.clients;
    return f.search === "" || 
      c.name?.toLowerCase().includes(f.search.toLowerCase()) ||
      c.email?.toLowerCase().includes(f.search.toLowerCase()) ||
      c.phone?.includes(f.search);
  });

  const filteredSuppliers = suppliers.filter(s => {
    const f = filters.suppliers;
    return f.search === "" || 
      s.name?.toLowerCase().includes(f.search.toLowerCase()) ||
      s.email?.toLowerCase().includes(f.search.toLowerCase()) ||
      s.phone?.includes(f.search);
  });

  const filteredServices = services.filter(s => {
    const f = filters.services;
    return f.search === "" || 
      s.name?.toLowerCase().includes(f.search.toLowerCase()) ||
      s.description?.toLowerCase().includes(f.search.toLowerCase());
  });

  const filteredProjects = constructionProjects.filter(p => {
    const f = filters.projects;
    const matchSearch = f.search === "" || 
      p.name?.toLowerCase().includes(f.search.toLowerCase()) ||
      p.client_name?.toLowerCase().includes(f.search.toLowerCase()) ||
      p.description?.toLowerCase().includes(f.search.toLowerCase());
    const matchStatus = f.status === "all" || p.status === f.status;
    return matchSearch && matchStatus;
  });

  const filteredAccountsPayable = accountsPayable.filter(a => {
    const f = filters.accountsPayable;
    const matchSearch = f.search === "" || 
      a.creditor_name?.toLowerCase().includes(f.search.toLowerCase()) ||
      a.description?.toLowerCase().includes(f.search.toLowerCase());
    
    // Verificar status com suporte a "vencido"
    const isPaid = a.status === 'paid' || a.status === 'pago';
    const isApproved = a.status === 'approved';
    const isOverdue = !isPaid && !isApproved && new Date(a.due_date) < new Date();
    
    let matchStatus = true;
    if (f.status === "all") {
      matchStatus = true;
    } else if (f.status === "overdue") {
      matchStatus = isOverdue;
    } else if (f.status === "paid") {
      matchStatus = isPaid;
    } else if (f.status === "approved") {
      matchStatus = isApproved;
    } else if (f.status === "pending") {
      matchStatus = a.status === "pending" && !isOverdue;
    } else {
      matchStatus = a.status === f.status;
    }
    
    const matchType = f.type === "all" || a.expense_type === f.type;
    const matchObra = !f.obra || f.obra === "todas" || (f.obra === "__none__" ? !a.project_name : a.project_name === f.obra);
    
    // Filtro por data de vencimento
    let matchDateFrom = true;
    let matchDateTo = true;
    
    if (f.dateFrom) {
      const fromDate = new Date(f.dateFrom);
      const dueDate = new Date(a.due_date);
      matchDateFrom = dueDate >= fromDate;
    }
    
    if (f.dateTo) {
      const toDate = new Date(f.dateTo);
      toDate.setHours(23, 59, 59, 999);
      const dueDate = new Date(a.due_date);
      matchDateTo = dueDate <= toDate;
    }
    
    return matchSearch && matchStatus && matchType && matchObra && matchDateFrom && matchDateTo;
  });

  const updateFilter = (module, field, value) => {
    setFilters(prev => ({
      ...prev,
      [module]: { ...prev[module], [field]: value }
    }));
  };

  // Check if user has admin access (é dono ou tem role admin)
  const hasAdminAccess = myPermissions.is_owner || myPermissions.role === 'admin';
  
  // Verificar se o usuário tem permissão para um módulo específico
  const hasModulePermission = (moduleCode) => {
    // Dono ou admin tem acesso a tudo
    if (myPermissions.is_owner || myPermissions.role === 'admin') return true;
    
    const userPerms = myPermissions.permissions || [];
    
    // Verificar se tem QUALQUER permissão relacionada ao módulo
    const modulePermissionPrefixes = {
      'transactions': ['view_transactions', 'create_transactions', 'edit_transactions', 'delete_transactions'],
      'clients': ['view_clients', 'create_clients', 'edit_clients', 'delete_clients'],
      'suppliers': ['view_suppliers', 'create_suppliers', 'edit_suppliers', 'delete_suppliers'],
      'services': ['view_services', 'create_services', 'edit_services', 'delete_services'],
      'service_management': ['view_service_management', 'create_service_management', 'edit_service_management'],
      'construction': ['view_construction', 'create_construction', 'edit_construction', 'view_materials', 'manage_materials', 'view_schedule', 'manage_schedule', 'view_material_orders', 'create_material_orders', 'edit_material_orders', 'approve_orders_technical', 'approve_orders_financial'],
      'accounts_payable': ['view_accounts_payable', 'create_accounts_payable', 'edit_accounts_payable', 'approve_payments', 'mark_as_paid', 'generate_reports'],
      'legal_process': ['view_legal_clients', 'create_legal_clients', 'edit_legal_clients', 'delete_legal_clients', 'view_legal_processes', 'create_legal_processes', 'edit_legal_processes', 'delete_legal_processes', 'attach_legal_documents'],
      'distributor': ['view_distributor', 'manage_distributor_products', 'create_distributor_entries', 'create_distributor_sales', 'manage_distributor_suppliers', 'view_distributor_reports'],
      'factory': ['view_factory', 'manage_factory_materials', 'manage_factory_products', 'create_factory_production', 'create_factory_sales', 'view_factory_reports', 'approve_factory_credit', 'use_factory_pos', 'manage_factory_deliveries', 'factory_delivery_driver'],
      'ai_insights': ['view_ai_insights', 'use_ai_insights'],
      'reports': ['view_dashboard', 'view_reports', 'export_reports']
    };
    
    const relatedPermissions = modulePermissionPrefixes[moduleCode] || [];
    
    // Se tem qualquer uma das permissões do módulo, pode acessar
    return relatedPermissions.some(perm => userPerms.includes(perm));
  };

  // Filter available modules based on company configuration AND user permissions
  const getAvailableTabs = () => {
    const companyModules = selectedCompany?.active_modules || [];
    const tabs = [];
    
    // Só mostrar módulos que o usuário tem permissão
    if (companyModules.includes('transactions') && hasModulePermission('transactions')) 
      tabs.push({ key: 'transactions', label: 'Transações', icon: CreditCard });
    if (companyModules.includes('clients') && hasModulePermission('clients')) 
      tabs.push({ key: 'clients', label: 'Clientes', icon: Users });
    if (companyModules.includes('suppliers') && hasModulePermission('suppliers')) 
      tabs.push({ key: 'suppliers', label: 'Fornecedores', icon: Package });
    if (companyModules.includes('services') && hasModulePermission('services')) 
      tabs.push({ key: 'services', label: 'Serviços', icon: Cog });
    if (companyModules.includes('service_management') && hasModulePermission('service_management')) 
      tabs.push({ key: 'service-management', label: 'Gestão Serviços', icon: Settings });
    if (companyModules.includes('construction') && hasModulePermission('construction')) 
      tabs.push({ key: 'construction', label: 'Construção', icon: Building2 });
    if (companyModules.includes('construction') && hasModulePermission('construction')) 
      tabs.push({ key: 'estoque', label: 'Estoque', icon: Package });
    if (companyModules.includes('distributor') && hasModulePermission('distributor')) 
      tabs.push({ key: 'distributor', label: 'Distribuidora', icon: PackageCheck });
    if (companyModules.includes('factory') && hasModulePermission('factory')) 
      tabs.push({ key: 'factory', label: 'Fábrica', icon: Factory });
    if (companyModules.includes('accounts_payable') && hasModulePermission('accounts_payable')) 
      tabs.push({ key: 'accounts-payable', label: 'Contas a Pagar', icon: FileText });
    if (companyModules.includes('legal_process') && hasModulePermission('legal_process')) 
      tabs.push({ key: 'legal-process', label: 'Processo Judiciário', icon: Shield });
    if (companyModules.includes('ai_insights') && hasModulePermission('ai_insights')) 
      tabs.push({ key: 'ai', label: 'IA Insights', icon: Sparkles });
    if (hasModulePermission('reports')) 
      tabs.push({ key: 'reports', label: 'Relatórios', icon: BarChart3 });
    if (hasAdminAccess) 
      tabs.push({ key: 'admin', label: 'Administração', icon: UserPlus });
    
    return tabs;
  };

  // Se a aba atual não é permitida (ex.: transportador), abre a primeira permitida
  const allowedTabKeys = getAvailableTabs().map(t => t.key).join(",");
  useEffect(() => {
    const keys = allowedTabKeys ? allowedTabKeys.split(",") : [];
    if (keys.length && !keys.includes(activeTab) && activeTab !== "dashboard") setActiveTab(keys[0]);
  }, [allowedTabKeys, activeTab]);

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Carregando dados da empresa...</p>
        <p className="text-sm text-gray-400 mt-2">Verificando conectividade...</p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => {
            console.log("🔄 Usuário forçou reload");
            setLoading(false);
            fetchData();
          }}
        >
          Tentar Novamente
        </Button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
        <div className="text-gray-600 mb-4">Inicializando sistema...</div>
        <Button onClick={() => fetchData()}>Carregar Dados</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-2 py-2 min-h-16">
            <div className="flex items-center min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="mr-2 lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="sidebar-toggle-btn"
                aria-label="Abrir menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <Building2 className="h-6 w-6 md:h-8 md:w-8 text-blue-600 mr-2 md:mr-3 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-base md:text-xl font-bold text-gray-900 truncate">{selectedCompany.name}</h1>
                <p className="text-xs md:text-sm text-gray-500 truncate">Controle de Fluxo de Caixa</p>
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2">
              {/* Indicador de última atualização */}
              <div className="flex items-center text-xs md:text-sm text-gray-500">
                <RefreshCw className="h-4 w-4 mr-1 animate-spin text-green-500" style={{animationDuration: '3s'}} />
                <span className="hidden md:inline">
                  {lastUpdate 
                    ? `Atualizado: ${lastUpdate.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' })}` 
                    : 'Sincronizando...'}
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={() => setSelectedCompany(null)} className="text-xs md:text-sm">
                <Building2 className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Trocar Empresa</span>
              </Button>
              <Button variant="outline" size="sm" onClick={logout} className="text-xs md:text-sm">
                <LogOut className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Desktop — fixa à esquerda em telas >= lg */}
        <aside className="hidden lg:flex lg:flex-col lg:w-60 lg:flex-shrink-0 bg-white border-r sticky top-[65px] h-[calc(100vh-65px)] overflow-y-auto" data-testid="sidebar-desktop">
          <nav className="p-3 space-y-1">
            {getAvailableTabs().map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`sidebar-item-${tab.key}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white font-semibold shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {Icon && <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500'}`} />}
                  <span className="truncate">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Sidebar Mobile — drawer deslizante */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden" data-testid="sidebar-mobile">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setSidebarOpen(false)}
              aria-hidden="true"
            />
            <aside className="absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-white shadow-xl overflow-y-auto animate-in slide-in-from-left">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="font-bold text-sm text-gray-900 truncate">{selectedCompany.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarOpen(false)}
                  data-testid="sidebar-close-btn"
                  aria-label="Fechar menu"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <nav className="p-3 space-y-1">
                {getAvailableTabs().map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => { setActiveTab(tab.key); setSidebarOpen(false); }}
                      data-testid={`sidebar-mobile-item-${tab.key}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                        isActive
                          ? 'bg-blue-600 text-white font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {Icon && <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500'}`} />}
                      <span className="truncate">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>
          </div>
        )}

        {/* Conteúdo principal */}
        <main className="flex-1 min-w-0 px-3 sm:px-6 lg:px-8 py-4 md:py-8 max-w-[1400px] mx-auto lg:mx-0">
        {/* Stats Cards - Apenas para admin/owner */}
        {myPermissions.can_view_dashboard && (
          <DashboardKPIs stats={stats} bankAccounts={bankAccounts} />
        )}

        {/* Alerta de Parcelas Vencidas - Apenas para admin/owner */}
        {myPermissions.can_view_dashboard && stats.parcelas_vencidas > 0 && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-full bg-red-100">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 flex items-center gap-2">
                    Parcelas Vencidas
                    <Badge variant="destructive">{stats.parcelas_vencidas}</Badge>
                  </h3>
                  <p className="text-sm text-red-600 mb-3">
                    Você tem {stats.parcelas_vencidas} parcela(s) vencida(s) totalizando {formatCurrency(stats.valor_parcelas_vencidas)}
                  </p>
                  
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {stats.parcelas_vencidas_lista?.slice(0, 25).map((parcela) => (
                      <div 
                        key={parcela.id} 
                        className="flex justify-between items-center p-2 bg-white rounded border border-red-100"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {parcela.client_name || parcela.transaction_description || 'Cliente não informado'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {parcela.description} • Vencida há {parcela.days_overdue} dia(s)
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600">{formatCurrency(parcela.amount)}</p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-1 text-xs h-6"
                            onClick={() => {
                              // Buscar a transação e abrir modal de parcelas
                              const transaction = transactions.find(t => t.id === parcela.transaction_id);
                              if (transaction) {
                                setSelectedInstallmentTransaction(transaction);
                                fetchInstallments(transaction.id);
                              }
                            }}
                          >
                            Ver Parcelas
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {stats.parcelas_vencidas > 5 && (
                    <p className="text-xs text-red-500 mt-2">
                      E mais {stats.parcelas_vencidas - 5} parcela(s) vencida(s)...
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* TabsList escondida — navegação feita via Sidebar */}
          <TabsList className="hidden overflow-x-auto whitespace-nowrap">
            {getAvailableTabs().map(tab => (
              <TabsTrigger key={tab.key} value={tab.key} className="whitespace-nowrap text-xs md:text-sm px-3 md:px-4">{tab.label}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Controle de Transações</CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      variant={viewMode === "timeline" ? "default" : "outline"}
                      onClick={() => setViewMode("timeline")}
                      size="sm"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Timeline
                    </Button>
                    <Button 
                      variant={viewMode === "diary" ? "default" : "outline"}
                      onClick={() => setViewMode("diary")}
                      size="sm"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Diário
                    </Button>
                    {hasAdminAccess && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm("Executar limpeza de parcelas órfãs?\n\nIsso remove parcelas cuja venda pai foi deletada. Ação segura — não afeta vendas existentes.")) return;
                          try {
                            const res = await axios.post(`${API}/companies/${selectedCompany.id}/transactions/cleanup-orphans`);
                            toast.success(`${res.data.parcelas_removidas} parcela(s) e ${res.data.transacoes_filhas_removidas} transação(ões) órfã(s) removidas`);
                            fetchData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Erro ao limpar parcelas órfãs");
                          }
                        }}
                        data-testid="cleanup-orphans-btn"
                        className="text-orange-700 border-orange-300 hover:bg-orange-50"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Limpar Órfãs
                      </Button>
                    )}
                    <Dialog open={showTransactionForm} onOpenChange={setShowTransactionForm}>
                      <DialogTrigger asChild>
                        <Button className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700">
                          <Plus className="h-4 w-4 mr-2" />
                          Nova Transação
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-black">{editingTransaction ? 'Editar Transação' : 'Nova Transação'}</DialogTitle>
                          <DialogDescription className="text-gray-500">
                            Preencha os dados para {editingTransaction ? 'atualizar' : 'criar'} a transação
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleTransactionSubmit} className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-black">Data</label>
                            <Input
                              type="date"
                              value={transactionForm.date}
                              onChange={(e) => setTransactionForm({...transactionForm, date: e.target.value})}
                              required
                              className="text-black"
                            />
                          </div>
                          <Select 
                            value={transactionForm.type} 
                            onValueChange={(value) => setTransactionForm({...transactionForm, type: value})}
                          >
                            <SelectTrigger className="text-black">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="entrada">Entrada</SelectItem>
                              <SelectItem value="saida">Saída</SelectItem>
                              <SelectItem value="venda">Venda</SelectItem>
                            </SelectContent>
                          </Select>
                          <div>
                            <label className="text-xs font-bold text-gray-700 block mb-1">
                              Conta Bancária * {transactionForm.type === "saida" ? "(de onde sai o valor)" : "(onde o valor entra)"}
                            </label>
                            <Select
                              value={transactionForm.bank_account_id}
                              onValueChange={(v) => setTransactionForm({ ...transactionForm, bank_account_id: v })}
                            >
                              <SelectTrigger className="text-black" data-testid="tx-bank-account">
                                <SelectValue placeholder="Selecione a conta" />
                              </SelectTrigger>
                              <SelectContent>
                                {bankAccounts.map((b) => (
                                  <SelectItem key={b.id} value={b.id}>
                                    {b.name}{b.bank_name ? ` — ${b.bank_name}` : ""} · Saldo: {formatCurrency(b.current_balance)}
                                  </SelectItem>
                                ))}
                                {bankAccounts.length === 0 && <SelectItem value="__none__" disabled>Nenhuma conta cadastrada — cadastre em Contas a Pagar → Saldo de Contas</SelectItem>}
                              </SelectContent>
                            </Select>
                          </div>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Valor"
                            value={transactionForm.amount}
                            onChange={(e) => {
                              const newAmount = e.target.value;
                              const numInstallments = parseInt(transactionForm.total_installments) || 1;
                              setTransactionForm({
                                ...transactionForm, 
                                amount: newAmount,
                                installment_value: transactionForm.is_installment && newAmount ? 
                                  (parseFloat(newAmount) / numInstallments).toFixed(2) : 0
                              });
                            }}
                            required
                            className="text-black"
                          />
                          <Textarea
                            placeholder="Descrição"
                            value={transactionForm.description}
                            onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                            required
                            className="text-black"
                          />
                          {transactionForm.type === "entrada" && (
                            <Select 
                              value={transactionForm.client_id} 
                              onValueChange={(value) => setTransactionForm({...transactionForm, client_id: value})}
                            >
                              <SelectTrigger className="text-black">
                                <SelectValue placeholder="Selecione um cliente (opcional)" />
                              </SelectTrigger>
                              <SelectContent>
                                {clients.map(client => (
                                  <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}

                          {/* Bloco É Obra? — disponível apenas para Entrada (vendas têm vínculo por parcela) */}
                          {transactionForm.type === "entrada" && (
                            <div className="border p-3 rounded-lg bg-blue-50 w-full max-w-full overflow-hidden">
                              <div className="flex items-center gap-2 mb-3">
                                <input
                                  type="checkbox"
                                  id="is_obra_checkbox"
                                  checked={transactionForm.is_obra}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setTransactionForm({
                                      ...transactionForm,
                                      is_obra: checked,
                                      project_id: checked ? transactionForm.project_id : "",
                                      phase_id: checked ? transactionForm.phase_id : ""
                                    });
                                    if (!checked) setTransactionPhases([]);
                                  }}
                                  className="h-4 w-4"
                                  data-testid="is-obra-checkbox"
                                />
                                <label htmlFor="is_obra_checkbox" className="text-sm font-bold text-black">
                                  É uma obra? (vincular à Construção)
                                </label>
                              </div>

                              {transactionForm.is_obra && (
                                <div className="space-y-3 w-full">
                                  {/* Obra (filtrada pelo cliente selecionado se houver) */}
                                  <div className="w-full">
                                    <label className="text-xs font-medium text-gray-700 block mb-1">Obra *</label>
                                    <Select
                                      value={transactionForm.project_id}
                                      onValueChange={async (value) => {
                                        setTransactionForm({...transactionForm, project_id: value, phase_id: ""});
                                        try {
                                          const res = await axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${value}/schedule`);
                                          setTransactionPhases(res.data);
                                        } catch (err) {
                                          console.error("Erro ao carregar medições:", err);
                                          setTransactionPhases([]);
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="text-black w-full max-w-full" data-testid="transaction-obra-select">
                                        <SelectValue placeholder="Selecione a obra" />
                                      </SelectTrigger>
                                      <SelectContent className="max-w-[90vw]">
                                        {constructionProjects
                                          .filter(p => !transactionForm.client_id || p.client_id === transactionForm.client_id)
                                          .map(p => (
                                            <SelectItem key={p.id} value={p.id} className="max-w-full">
                                              <span className="whitespace-normal break-words block">{p.name}{p.client_name ? ` — ${p.client_name}` : ''}</span>
                                            </SelectItem>
                                          ))}
                                      </SelectContent>
                                    </Select>
                                    {transactionForm.client_id && constructionProjects.filter(p => p.client_id === transactionForm.client_id).length === 0 && (
                                      <p className="text-xs text-orange-600 mt-1">
                                        Nenhuma obra cadastrada para este cliente.
                                      </p>
                                    )}
                                  </div>

                                  {/* Medição (fase) */}
                                  <div className="w-full">
                                    <label className="text-xs font-medium text-gray-700 block mb-1">Medição (fase) *</label>
                                    <Select
                                      value={transactionForm.phase_id}
                                      onValueChange={(value) => setTransactionForm({...transactionForm, phase_id: value})}
                                      disabled={!transactionForm.project_id}
                                    >
                                      <SelectTrigger className="text-black w-full max-w-full" data-testid="transaction-phase-select">
                                        <SelectValue placeholder={transactionForm.project_id ? "Selecione a medição" : "Primeiro selecione a obra"} />
                                      </SelectTrigger>
                                      <SelectContent className="max-w-[90vw]">
                                        {transactionPhases.length === 0 ? (
                                          <SelectItem value="none" disabled>Nenhuma medição cadastrada</SelectItem>
                                        ) : (
                                          transactionPhases
                                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                                            .map(phase => (
                                              <SelectItem key={phase.id} value={phase.id} className="max-w-full">
                                                <span className="whitespace-normal break-words block">{phase.phase_name}</span>
                                              </SelectItem>
                                            ))
                                        )}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {transactionForm.type === "saida" && (
                            <Select 
                              value={transactionForm.supplier_id} 
                              onValueChange={(value) => setTransactionForm({...transactionForm, supplier_id: value})}
                            >
                              <SelectTrigger className="text-black">
                                <SelectValue placeholder="Selecione um fornecedor (opcional)" />
                              </SelectTrigger>
                              <SelectContent>
                                {suppliers.map(supplier => (
                                  <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>  
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {transactionForm.type === "venda" && (
                            <>
                              <Select 
                                value={transactionForm.client_id} 
                                onValueChange={(value) => setTransactionForm({...transactionForm, client_id: value})}
                              >
                                <SelectTrigger className="text-black">
                                  <SelectValue placeholder="Selecione um cliente" />
                                </SelectTrigger>
                                <SelectContent>
                                  {clients.map(client => (
                                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select 
                                value={transactionForm.product_id} 
                                onValueChange={(value) => setTransactionForm({...transactionForm, product_id: value})}
                              >
                                <SelectTrigger className="text-black">
                                  <SelectValue placeholder="Selecione um serviço" />
                                </SelectTrigger>
                                <SelectContent>
                                  {services.map(service => (
                                    <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              
                              {/* Opções de Parcelamento */}
                              <div className="border p-3 rounded-lg bg-gray-50">
                                <div className="flex items-center gap-2 mb-3">
                                  <input
                                    type="checkbox"
                                    id="is_installment"
                                    checked={transactionForm.is_installment}
                                    onChange={(e) => {
                                      const isInstallment = e.target.checked;
                                      if (isInstallment) {
                                        // Criar lista inicial de parcelas com datas
                                        const amount = parseFloat(transactionForm.amount) || 0;
                                        const today = new Date();
                                        const date1 = new Date(today);
                                        date1.setDate(date1.getDate() + 30);
                                        const date2 = new Date(today);
                                        date2.setDate(date2.getDate() + 60);
                                        
                                        setInstallmentsList([
                                          { installment_number: 1, amount: amount * 0.3, description: "Entrada", due_date: today.toISOString().split('T')[0] },
                                          { installment_number: 2, amount: amount * 0.7, description: "Medição 1", due_date: date1.toISOString().split('T')[0] }
                                        ]);
                                      } else {
                                        setInstallmentsList([]);
                                      }
                                      setTransactionForm({
                                        ...transactionForm, 
                                        is_installment: isInstallment,
                                        total_installments: isInstallment ? 2 : 1
                                      });
                                    }}
                                    className="h-4 w-4"
                                    data-testid="installment-checkbox"
                                  />
                                  <label htmlFor="is_installment" className="text-sm font-medium text-black">
                                    Venda Parcelada
                                  </label>
                                </div>
                                
                                {transactionForm.is_installment && (
                                  <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                      <label className="text-xs text-gray-600 font-medium">Parcelas (valores individuais)</label>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          const newNum = installmentsList.length + 1;
                                          // Calcular próxima data de vencimento (30 dias após a última)
                                          const lastDate = installmentsList.length > 0 && installmentsList[installmentsList.length - 1].due_date
                                            ? new Date(installmentsList[installmentsList.length - 1].due_date)
                                            : new Date();
                                          const nextDate = new Date(lastDate);
                                          nextDate.setDate(nextDate.getDate() + 30);
                                          
                                          setInstallmentsList([...installmentsList, {
                                            installment_number: newNum,
                                            amount: 0,
                                            description: `Medição ${newNum - 1}`,
                                            due_date: nextDate.toISOString().split('T')[0]
                                          }]);
                                          setTransactionForm({
                                            ...transactionForm,
                                            total_installments: newNum
                                          });
                                        }}
                                      >
                                        <Plus className="h-3 w-3 mr-1" /> Adicionar Parcela
                                      </Button>
                                    </div>
                                    
                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                      {installmentsList.map((inst, index) => (
                                        <div key={index} className="bg-white p-2 rounded border">
                                          <div className="flex gap-2 items-center mb-2">
                                            <span className="text-xs text-gray-500 w-6">{inst.installment_number}.</span>
                                            <Input
                                              placeholder="Descrição"
                                              value={inst.description}
                                              onChange={(e) => {
                                                const newList = [...installmentsList];
                                                newList[index].description = e.target.value;
                                                setInstallmentsList(newList);
                                              }}
                                              className="text-black text-sm flex-1"
                                            />
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="Valor"
                                              value={inst.amount}
                                              onChange={(e) => {
                                                const newList = [...installmentsList];
                                                newList[index].amount = parseFloat(e.target.value) || 0;
                                                setInstallmentsList(newList);
                                              }}
                                              className="text-black text-sm w-28"
                                            />
                                            {installmentsList.length > 2 && (
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                className="text-red-500 p-1"
                                                onClick={() => {
                                                  const newList = installmentsList.filter((_, i) => i !== index)
                                                    .map((item, i) => ({ ...item, installment_number: i + 1 }));
                                                  setInstallmentsList(newList);
                                                  setTransactionForm({
                                                    ...transactionForm,
                                                    total_installments: newList.length
                                                  });
                                                }}
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            )}
                                          </div>
                                          <div className="flex gap-2 items-center ml-6">
                                            <label className="text-xs text-gray-500">Vencimento:</label>
                                            <Input
                                              type="date"
                                              value={inst.due_date || ''}
                                              onChange={(e) => {
                                                const newList = [...installmentsList];
                                                newList[index].due_date = e.target.value;
                                                setInstallmentsList(newList);
                                              }}
                                              className="text-black text-xs h-7 w-36"
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    
                                    <div className="flex justify-between text-xs pt-2 border-t">
                                      <span className="text-gray-600">
                                        Total das parcelas: <strong className="text-black">{formatCurrency(installmentsList.reduce((sum, i) => sum + i.amount, 0))}</strong>
                                      </span>
                                      <span className={`font-medium ${
                                        Math.abs(installmentsList.reduce((sum, i) => sum + i.amount, 0) - parseFloat(transactionForm.amount || 0)) < 0.01
                                          ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {Math.abs(installmentsList.reduce((sum, i) => sum + i.amount, 0) - parseFloat(transactionForm.amount || 0)) < 0.01
                                          ? '✓ Valores conferem'
                                          : `Diferença: ${formatCurrency(parseFloat(transactionForm.amount || 0) - installmentsList.reduce((sum, i) => sum + i.amount, 0))}`
                                        }
                                      </span>
                                    </div>
                                    
                                    <p className="text-xs text-blue-600">
                                      Primeira parcela = Entrada | Demais parcelas = Conforme medições
                                    </p>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                          <Button type="submit" className="w-full">
                            {editingTransaction ? 'Atualizar Transação' : 'Criar Transação'}
                          </Button>
                          {editingTransaction && (
                            <Button 
                              type="button" 
                              variant="outline" 
                              className="w-full"
                              onClick={() => {
                                setEditingTransaction(null);
                                setTransactionForm({
                                  type: "entrada",
                                  amount: "",
                                  description: "",
                                  client_id: "",
                                  supplier_id: "",
                                  product_id: "",
                                  date: new Date().toISOString().split('T')[0],
                                  is_installment: false,
                                  total_installments: 1,
                                  installment_value: 0
                                });
                                setShowTransactionForm(false);
                              }}
                            >
                              Cancelar
                            </Button>
                          )}
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                {viewMode === "diary" && (
                  <div className="flex items-center gap-4">
                    <Input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-auto"
                    />
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {/* Filtros de Transações */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Buscar por descrição, cliente..."
                        value={filters.transactions.search}
                        onChange={(e) => updateFilter('transactions', 'search', e.target.value)}
                        className="pl-10 text-black"
                      />
                    </div>
                    <Select 
                      value={filters.transactions.type} 
                      onValueChange={(value) => updateFilter('transactions', 'type', value)}
                    >
                      <SelectTrigger className="text-black">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os tipos</SelectItem>
                        <SelectItem value="entrada">Entrada</SelectItem>
                        <SelectItem value="saida">Saída</SelectItem>
                        <SelectItem value="venda">Venda</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={filters.transactions.bank_account_id || "all"}
                      onValueChange={(value) => updateFilter('transactions', 'bank_account_id', value)}
                    >
                      <SelectTrigger className="text-black" data-testid="tx-filter-bank">
                        <SelectValue placeholder="Conta bancária" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as contas</SelectItem>
                        {bankAccounts.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name}{b.bank_name ? ` — ${b.bank_name}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="date"
                      placeholder="Data inicial"
                      value={filters.transactions.dateFrom}
                      onChange={(e) => updateFilter('transactions', 'dateFrom', e.target.value)}
                      className="text-black"
                    />
                    <Input
                      type="date"
                      placeholder="Data final"
                      value={filters.transactions.dateTo}
                      onChange={(e) => updateFilter('transactions', 'dateTo', e.target.value)}
                      className="text-black"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {filteredTransactions.length} de {transactions.length} transações
                    {filters.transactions.bank_account_id && filters.transactions.bank_account_id !== "all" && (() => {
                      const b = bankAccounts.find((x) => x.id === filters.transactions.bank_account_id);
                      const entradas = filteredTransactions.filter(t => t.type === "entrada" || t.type === "venda").reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                      const saidas = filteredTransactions.filter(t => t.type === "saida").reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                      return (
                        <span className="ml-2 text-black">
                          • Conta: <strong>{b?.name || "—"}</strong> · Entradas: <strong className="text-green-700">{formatCurrency(entradas)}</strong> · Saídas: <strong className="text-red-700">{formatCurrency(saidas)}</strong>
                        </span>
                      );
                    })()}
                  </p>
                </div>
                <div className="space-y-4">
                  {filteredTransactions.map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg bg-white shadow-sm">
                      <div className="flex items-center space-x-4">
                        <Badge 
                          variant={transaction.type === 'entrada' ? 'default' : transaction.type === 'saida' ? 'destructive' : 'secondary'}
                          className="capitalize"
                        >
                          {transaction.type}
                        </Badge>
                        {transaction.is_installment && (
                          <Badge variant="outline" className="bg-purple-100 text-purple-700">
                            {transaction.total_installments}x
                          </Badge>
                        )}
                        <div>
                          <p className="font-medium text-black">{transaction.description}</p>
                          {transaction.client_name && (
                            <p className="text-sm text-gray-500">Cliente: {transaction.client_name}</p>
                          )}
                          {transaction.supplier_name && (
                            <p className="text-sm text-gray-500">Fornecedor: {transaction.supplier_name}</p>
                          )}
                          {transaction.product_name && (
                            <p className="text-sm text-gray-500">Serviço: {transaction.product_name}</p>
                          )}
                          {transaction.project_name && (
                            <p className="text-sm text-purple-700">
                              🏗️ Obra: <strong>{transaction.project_name}</strong>
                              {transaction.phase_name && <span> · Medição: <strong>{transaction.phase_name}</strong></span>}
                            </p>
                          )}
                          <p className="text-xs text-gray-400">
                            {new Date(transaction.date).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`text-lg font-bold ${
                            transaction.type === 'entrada' || transaction.type === 'venda' 
                              ? 'text-green-600' 
                              : 'text-red-600'
                          }`}>
                            {transaction.type === 'saida' ? '-' : '+'}{formatCurrency(transaction.amount)}
                          </p>
                          {transaction.is_installment && (
                            <p className="text-xs text-purple-600">
                              {transaction.total_installments}x de {formatCurrency(transaction.installment_value)}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {transaction.is_installment && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedInstallmentTransaction(transaction);
                                fetchInstallments(transaction.id);
                              }}
                              data-testid={`view-installments-${transaction.id}`}
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              Parcelas
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEditTransaction(transaction)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => deleteTransaction(transaction.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Modal de Parcelas */}
                <Dialog open={selectedInstallmentTransaction !== null} onOpenChange={() => {
                  setSelectedInstallmentTransaction(null);
                  setEditingInstallment(null);
                }}>
                  <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-black">
                        Parcelas - {selectedInstallmentTransaction?.description}
                      </DialogTitle>
                      <DialogDescription>
                        Gerencie as parcelas desta venda (valores condicionados às medições)
                      </DialogDescription>
                    </DialogHeader>

                    {/* Formulário para adicionar nova parcela */}
                    <div className="border rounded-lg p-3 bg-blue-50 space-y-2" data-testid="add-installment-form">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-bold text-blue-900">
                          <Plus className="h-4 w-4 inline mr-1" /> Adicionar Nova Parcela
                        </label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Input
                          placeholder="Descrição (ex: Medição 4)"
                          value={newInstallmentForm.description}
                          onChange={(e) => setNewInstallmentForm({ ...newInstallmentForm, description: e.target.value })}
                          className="text-black text-sm col-span-2"
                          data-testid="new-installment-description"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Valor R$"
                          value={newInstallmentForm.amount}
                          onChange={(e) => setNewInstallmentForm({ ...newInstallmentForm, amount: e.target.value })}
                          className="text-black text-sm"
                          data-testid="new-installment-amount"
                        />
                        <Input
                          type="date"
                          value={newInstallmentForm.due_date}
                          onChange={(e) => setNewInstallmentForm({ ...newInstallmentForm, due_date: e.target.value })}
                          className="text-black text-sm"
                          data-testid="new-installment-due-date"
                        />
                      </div>
                      <Button
                        size="sm"
                        className="w-full bg-blue-500 hover:bg-blue-600"
                        onClick={addNewInstallment}
                        data-testid="add-installment-submit"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Adicionar Parcela
                      </Button>
                    </div>

                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {installments.map((installment) => (
                        <div 
                          key={installment.id} 
                          className={`p-3 rounded-lg border ${
                            installment.status === 'pago' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          {editingInstallment?.id === installment.id ? (
                            // Modo de edição
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Descrição"
                                  value={editingInstallment.description || ''}
                                  onChange={(e) => setEditingInstallment({...editingInstallment, description: e.target.value})}
                                  className="text-black text-sm flex-1"
                                />
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Valor"
                                  value={editingInstallment.amount}
                                  onChange={(e) => setEditingInstallment({...editingInstallment, amount: parseFloat(e.target.value) || 0})}
                                  className="text-black text-sm w-28"
                                />
                              </div>
                              <div className="flex gap-2 items-center">
                                <label className="text-xs text-gray-600 whitespace-nowrap">Vencimento:</label>
                                <Input
                                  type="date"
                                  value={editingInstallment.due_date ? editingInstallment.due_date.split('T')[0] : ''}
                                  onChange={(e) => setEditingInstallment({...editingInstallment, due_date: e.target.value})}
                                  className="text-black text-sm flex-1"
                                />
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingInstallment(null)}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      await axios.put(`${API}/companies/${selectedCompany.id}/installments/${editingInstallment.id}`, {
                                        amount: editingInstallment.amount,
                                        description: editingInstallment.description,
                                        due_date: editingInstallment.due_date ? new Date(editingInstallment.due_date).toISOString() : null
                                      });
                                      toast.success("Parcela atualizada!");
                                      fetchInstallments(selectedInstallmentTransaction.id);
                                      setEditingInstallment(null);
                                    } catch (error) {
                                      toast.error("Erro ao atualizar parcela");
                                    }
                                  }}
                                >
                                  Salvar
                                </Button>
                              </div>
                            </div>
                          ) : (
                            // Modo de visualização
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium text-black">
                                  {installment.description || `Parcela ${installment.installment_number}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Parcela {installment.installment_number}/{selectedInstallmentTransaction?.total_installments}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Vencimento: {new Date(installment.due_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                </p>
                                {installment.paid_date && (
                                  <p className="text-xs text-green-600">
                                    Pago em: {new Date(installment.paid_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                  </p>
                                )}
                              </div>
                              <div className="text-right flex flex-col items-end gap-1">
                                <p className="font-bold text-black">{formatCurrency(installment.amount)}</p>
                                {installment.status === 'pago' ? (
                                  <Badge className="bg-green-600">Pago</Badge>
                                ) : (
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditingInstallment({...installment})}
                                      data-testid={`edit-installment-${installment.id}`}
                                    >
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={async () => {
                                        if (!window.confirm(`Excluir Parcela ${installment.installment_number}?`)) return;
                                        try {
                                          await axios.delete(`${API}/companies/${selectedCompany.id}/installments/${installment.id}`);
                                          toast.success("Parcela excluída");
                                          fetchInstallments(selectedInstallmentTransaction.id);
                                          fetchData();
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || "Erro ao excluir parcela");
                                        }
                                      }}
                                      data-testid={`delete-installment-${installment.id}`}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => openPayInstallmentDialog(installment)}
                                      data-testid={`pay-installment-${installment.id}`}
                                    >
                                      <Check className="h-3 w-3 mr-1" />
                                      Pagar
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {installments.length === 0 && (
                        <p className="text-center text-gray-500 py-4">Carregando parcelas...</p>
                      )}
                    </div>
                    <div className="pt-3 border-t">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Pago:</span>
                        <span className="font-bold text-green-600">
                          {formatCurrency(installments.filter(i => i.status === 'pago').reduce((sum, i) => sum + i.amount, 0))}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">A Receber:</span>
                        <span className="font-bold text-orange-600">
                          {formatCurrency(installments.filter(i => i.status !== 'pago').reduce((sum, i) => sum + i.amount, 0))}
                        </span>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Clientes</CardTitle>
                  <Dialog open={showClientForm} onOpenChange={(open) => {
                    setShowClientForm(open);
                    if (!open) {
                      setEditingClient(null);
                      setClientForm({ name: "", email: "", phone: "", document: "", responsible_name: "" });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button>
                        <Users className="h-4 w-4 mr-2" />
                        Novo Cliente
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-black">{editingClient ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleClientSubmit} className="space-y-4">
                        <Input
                          placeholder="Nome"
                          value={clientForm.name}
                          onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                          required
                          className="text-black"
                        />
                        <Input
                          type="email"
                          placeholder="Email"
                          value={clientForm.email}
                          onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                          className="text-black"
                        />
                        <Input
                          placeholder="Telefone"
                          value={clientForm.phone}
                          onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                          className="text-black"
                        />
                        <Input
                          placeholder="Documento (CPF/CNPJ)"
                          value={clientForm.document}
                          onChange={(e) => {
                            const d = (e.target.value || "").replace(/\D/g, "").slice(0, 14);
                            let masked = d;
                            if (d.length <= 11) {
                              // CPF: 000.000.000-00
                              masked = d
                                .replace(/^(\d{3})(\d)/, "$1.$2")
                                .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
                                .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
                            } else {
                              // CNPJ: 00.000.000/0000-00
                              masked = d
                                .replace(/^(\d{2})(\d)/, "$1.$2")
                                .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
                                .replace(/\.(\d{3})(\d)/, ".$1/$2")
                                .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
                            }
                            setClientForm({...clientForm, document: masked});
                          }}
                          className="text-black"
                          data-testid="client-form-document"
                        />
                        {(() => {
                          const digits = (clientForm.document || "").replace(/\D/g, "");
                          return digits.length >= 14 ? (
                            <Input
                              placeholder="Nome do responsável da empresa"
                              value={clientForm.responsible_name}
                              onChange={(e) => setClientForm({...clientForm, responsible_name: e.target.value})}
                              className="text-black"
                              data-testid="client-form-responsible-name"
                            />
                          ) : null;
                        })()}
                        <Button type="submit" className="w-full">{editingClient ? 'Atualizar Cliente' : 'Criar Cliente'}</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filtro de Clientes */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar por nome, email ou telefone..."
                      value={filters.clients.search}
                      onChange={(e) => updateFilter('clients', 'search', e.target.value)}
                      className="pl-10 text-black"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {filteredClients.length} de {clients.length} clientes
                  </p>
                </div>
                <div className="grid gap-4">
                  {filteredClients.map((client) => (
                    <Card key={client.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-black">{client.name}</h3>
                            {client.email && <p className="text-sm text-gray-600">{client.email}</p>}
                            {client.phone && <p className="text-sm text-gray-600">{client.phone}</p>}
                            {client.document && <p className="text-sm text-gray-600">{client.document}</p>}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditClient(client)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => deleteClient(client.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <input
                              type="file"
                              accept=".pdf"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleClientDocumentUpload(client.id, file);
                                  e.target.value = '';
                                }
                              }}
                              style={{ display: 'none' }}
                              id={`client-file-${client.id}`}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById(`client-file-${client.id}`).click()}
                            >
                              <Paperclip className="h-4 w-4 mr-2" />
                              Anexar PDF
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowClientDocuments(client);
                                fetchClientDocuments(client.id);
                              }}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Ver Docs
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {/* Client Documents Dialog */}
                <Dialog open={showClientDocuments !== null} onOpenChange={() => setShowClientDocuments(null)}>
                  <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-black">
                        Documentos - {showClientDocuments?.name}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {clientDocuments.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-2 border rounded">
                          <div className="flex items-center">
                            <FileText className="h-4 w-4 mr-2 text-red-600" />
                            <span className="text-sm">{doc.name}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(doc.uploaded_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                          </span>
                        </div>
                      ))}
                      {clientDocuments.length === 0 && (
                        <p className="text-gray-500 text-center py-4">Nenhum documento anexado</p>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="suppliers">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-black">Fornecedores</CardTitle>
                  <Dialog open={showSupplierForm} onOpenChange={(open) => {
                    setShowSupplierForm(open);
                    if (!open) {
                      setEditingSupplier(null);
                      setSupplierForm({ name: "", email: "", phone: "", document: "" });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button>
                        <Building2 className="h-4 w-4 mr-2" />
                        Novo Fornecedor
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-black">{editingSupplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSupplierSubmit} className="space-y-4">
                        <Input
                          placeholder="Nome"
                          value={supplierForm.name}
                          onChange={(e) => setSupplierForm({...supplierForm, name: e.target.value})}
                          required
                          className="text-black"
                        />
                        <Input
                          type="email"
                          placeholder="Email"
                          value={supplierForm.email}
                          onChange={(e) => setSupplierForm({...supplierForm, email: e.target.value})}
                          className="text-black"
                        />
                        <Input
                          placeholder="Telefone"
                          value={supplierForm.phone}
                          onChange={(e) => setSupplierForm({...supplierForm, phone: e.target.value})}
                          className="text-black"
                        />
                        <Input
                          placeholder="Documento (CNPJ/CPF)"
                          value={supplierForm.document}
                          onChange={(e) => setSupplierForm({...supplierForm, document: e.target.value})}
                          className="text-black"
                        />
                        <Button type="submit" className="w-full">{editingSupplier ? 'Atualizar Fornecedor' : 'Criar Fornecedor'}</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filtro de Fornecedores */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar por nome, email ou telefone..."
                      value={filters.suppliers.search}
                      onChange={(e) => updateFilter('suppliers', 'search', e.target.value)}
                      className="pl-10 text-black"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {filteredSuppliers.length} de {suppliers.length} fornecedores
                  </p>
                </div>
                <div className="grid gap-4">
                  {filteredSuppliers.map((supplier) => (
                    <Card key={supplier.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-black">{supplier.name}</h3>
                            {supplier.email && <p className="text-sm text-gray-600">{supplier.email}</p>}
                            {supplier.phone && <p className="text-sm text-gray-600">{supplier.phone}</p>}
                            {supplier.document && <p className="text-sm text-gray-600">{supplier.document}</p>}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditSupplier(supplier)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => deleteSupplier(supplier.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-black">Serviços</CardTitle>
                  <Dialog open={showServiceForm} onOpenChange={(open) => {
                    setShowServiceForm(open);
                    if (!open) {
                      setEditingService(null);
                      setServiceForm({ name: "", price: "", description: "" });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button data-testid="new-service-btn">
                        <Package className="h-4 w-4 mr-2" />
                        Novo Serviço
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-black">{editingService ? 'Editar Serviço' : 'Novo Serviço'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleServiceSubmit} className="space-y-4">
                        <Input
                          placeholder="Nome do serviço"
                          value={serviceForm.name}
                          onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})}
                          required
                          className="text-black"
                          data-testid="service-name-input"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Preço"
                          value={serviceForm.price}
                          onChange={(e) => setServiceForm({...serviceForm, price: e.target.value})}
                          required
                          className="text-black"
                          data-testid="service-price-input"
                        />
                        <Textarea
                          placeholder="Descrição"
                          value={serviceForm.description}
                          onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                          className="text-black"
                          data-testid="service-description-input"
                        />
                        <Button type="submit" className="w-full" data-testid="submit-service-btn">
                          {editingService ? 'Atualizar Serviço' : 'Criar Serviço'}
                        </Button>
                        {editingService && (
                          <Button 
                            type="button" 
                            variant="outline" 
                            className="w-full"
                            onClick={() => {
                              setEditingService(null);
                              setServiceForm({ name: "", price: "", description: "" });
                              setShowServiceForm(false);
                            }}
                          >
                            Cancelar
                          </Button>
                        )}
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filtro de Serviços */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar por nome ou descrição..."
                      value={filters.services.search}
                      onChange={(e) => updateFilter('services', 'search', e.target.value)}
                      className="pl-10 text-black"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {filteredServices.length} de {services.length} serviços
                  </p>
                </div>
                <div className="grid gap-4">
                  {filteredServices.map((service) => (
                    <Card key={service.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-black">{service.name}</h3>
                            {service.description && <p className="text-sm text-gray-600">{service.description}</p>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">
                              {formatCurrency(service.price)}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditService(service)}
                              data-testid={`edit-service-${service.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => deleteServiceItem(service.id)}
                              data-testid={`delete-service-${service.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="service-management">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Gestão de Serviços</CardTitle>
                  <Dialog open={showServiceClientForm} onOpenChange={setShowServiceClientForm}>
                    <DialogTrigger asChild>
                      <Button>
                        <Settings className="h-4 w-4 mr-2" />
                        Vincular Serviço a Cliente
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-black">
                          {editingServiceClient ? "Editar Vinculação" : "Vincular Serviço a Cliente"}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={editingServiceClient ? handleEditServiceClient : handleServiceClientSubmit} className="space-y-4">
                        <Select 
                          value={serviceClientForm.service_id} 
                          onValueChange={(value) => setServiceClientForm({...serviceClientForm, service_id: value})}
                        >
                          <SelectTrigger className="text-black">
                            <SelectValue placeholder="Selecione um serviço" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map(service => (
                              <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select 
                          value={serviceClientForm.client_id} 
                          onValueChange={(value) => setServiceClientForm({...serviceClientForm, client_id: value})}
                        >
                          <SelectTrigger className="text-black">
                            <SelectValue placeholder="Selecione um cliente" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map(client => (
                              <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select 
                          value={serviceClientForm.status} 
                          onValueChange={(value) => setServiceClientForm({...serviceClientForm, status: value})}
                        >
                          <SelectTrigger className="text-black">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="iniciado">Iniciado</SelectItem>
                            <SelectItem value="em_execucao">Em Execução</SelectItem>
                            <SelectItem value="finalizado">Finalizado</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Data de Início</label>
                            <Input
                              type="date"
                              value={serviceClientForm.start_date}
                              onChange={(e) => setServiceClientForm({...serviceClientForm, start_date: e.target.value})}
                              className="text-black"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Data Final</label>
                            <Input
                              type="date"
                              value={serviceClientForm.end_date}
                              onChange={(e) => setServiceClientForm({...serviceClientForm, end_date: e.target.value})}
                              className="text-black"
                            />
                          </div>
                        </div>
                        <Textarea
                          placeholder="Observações (opcional)"
                          value={serviceClientForm.notes}
                          onChange={(e) => setServiceClientForm({...serviceClientForm, notes: e.target.value})}
                          className="text-black"
                        />
                        <Button type="submit" className="w-full">Vincular Serviço</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {serviceClients.map((serviceClient) => {
                    const today = new Date();
                    const endDate = serviceClient.end_date ? new Date(serviceClient.end_date) : null;
                    const isOverdue = endDate && endDate < today && serviceClient.status !== 'finalizado';
                    const isNearDeadline = endDate && !isOverdue && 
                      Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)) <= 3 && 
                      serviceClient.status !== 'finalizado';
                    
                    return (
                      <Card 
                        key={serviceClient.id} 
                        className={`border-l-4 ${
                          isOverdue ? 'border-l-red-500 bg-red-50' : 
                          isNearDeadline ? 'border-l-yellow-500 bg-yellow-50' : 
                          'border-l-blue-500'
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-lg">{serviceClient.service_name}</h3>
                                {isOverdue && (
                                  <Badge variant="destructive" className="text-xs">
                                    ATRASADO
                                  </Badge>
                                )}
                                {isNearDeadline && (
                                  <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-700">
                                    URGENTE
                                  </Badge>
                                )}
                              </div>
                              <p className="text-gray-600">Cliente: {serviceClient.client_name}</p>
                              {serviceClient.notes && (
                                <p className="text-sm text-gray-500 mt-1">Obs: {serviceClient.notes}</p>
                              )}
                              <div className="flex gap-4 mt-2 text-sm text-gray-500">
                                {serviceClient.start_date && (
                                  <div>
                                    <span className="font-medium">Início: </span>
                                    {new Date(serviceClient.start_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                  </div>
                                )}
                                {serviceClient.end_date && (
                                  <div className={isOverdue ? 'text-red-600 font-medium' : ''}>
                                    <span className="font-medium">Final: </span>
                                    {new Date(serviceClient.end_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-2">
                                Criado em: {new Date(serviceClient.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                              </p>
                            </div>
                          <div className="flex flex-col items-end space-y-2">
                            <Badge 
                              variant={
                                serviceClient.status === 'finalizado' ? 'default' : 
                                serviceClient.status === 'em_execucao' ? 'secondary' : 
                                'outline'
                              }
                              className={
                                serviceClient.status === 'finalizado' ? 'bg-green-100 text-green-800' :
                                serviceClient.status === 'em_execucao' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }
                            >
                              {serviceClient.status === 'iniciado' && 'Iniciado'}
                              {serviceClient.status === 'em_execucao' && 'Em Execução'}
                              {serviceClient.status === 'finalizado' && 'Finalizado'}
                            </Badge>
                            <div className="flex gap-1 flex-wrap">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => startEditServiceClient(serviceClient)}
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Editar
                              </Button>
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    handleServiceClientDocumentUpload(serviceClient.id, file);
                                    e.target.value = '';
                                  }
                                }}
                                style={{ display: 'none' }}
                                id={`service-file-${serviceClient.id}`}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => document.getElementById(`service-file-${serviceClient.id}`).click()}
                              >
                                <Paperclip className="h-3 w-3 mr-1" />
                                PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setShowServiceClientDocuments(serviceClient);
                                  fetchServiceClientDocuments(serviceClient.id);
                                }}
                              >
                                <FileText className="h-3 w-3 mr-1" />
                                Docs
                              </Button>
                              {serviceClient.status !== 'em_execucao' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => updateServiceClientStatus(serviceClient.id, 'em_execucao')}
                                >
                                  Em Execução
                                </Button>
                              )}
                              {serviceClient.status !== 'finalizado' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => updateServiceClientStatus(serviceClient.id, 'finalizado')}
                                  className="bg-green-50 hover:bg-green-100"
                                >
                                  Finalizar
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    );
                  })}
                </div>
                
                {/* Service Client Documents Dialog */}
                <Dialog open={showServiceClientDocuments !== null} onOpenChange={() => setShowServiceClientDocuments(null)}>
                  <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-black">
                        Documentos - {showServiceClientDocuments?.service_name} ({showServiceClientDocuments?.client_name})
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {serviceClientDocuments.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-2 border rounded">
                          <div className="flex items-center">
                            <FileText className="h-4 w-4 mr-2 text-red-600" />
                            <span className="text-sm">{doc.name}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(doc.uploaded_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                          </span>
                        </div>
                      ))}
                      {serviceClientDocuments.length === 0 && (
                        <p className="text-gray-500 text-center py-4">Nenhum documento anexado</p>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ai">
            <AIInsights companyId={selectedCompany.id} />
          </TabsContent>

          <TabsContent value="construction">
            <div className="space-y-6 min-w-0 max-w-full">
              {!selectedProject ? (
                <>
                <Tabs value={cmActiveTab} onValueChange={setCmActiveTab} className="w-full">
                  <TabsList className="inline-flex w-full mb-4 h-auto overflow-x-auto whitespace-nowrap">
                    <TabsTrigger value="tendencia" data-testid="cm-tab-tendencia">Tendência Financeira</TabsTrigger>
                    <TabsTrigger value="projetos" data-testid="cm-tab-projetos">Projetos</TabsTrigger>
                    <TabsTrigger value="pedidos" data-testid="cm-tab-pedidos">Material/Serviço</TabsTrigger>
                  </TabsList>

                  <TabsContent value="tendencia">
                {/* Tendência Financeira da Obra (selecionável) */}
                <Card data-testid="construction-trend-selector-card">
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <CardTitle className="text-black flex items-center">
                        <BarChart3 className="h-5 w-5 mr-2 text-gray-700" />
                        Tendência Financeira da Obra
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">Obra:</label>
                        <Select
                          value={selectedTrendProjectId || (constructionProjects[0]?.id || "")}
                          onValueChange={setSelectedTrendProjectId}
                        >
                          <SelectTrigger className="w-72 text-black" data-testid="construction-trend-project-selector">
                            <SelectValue placeholder="Selecione uma obra" />
                          </SelectTrigger>
                          <SelectContent>
                            {constructionProjects.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {constructionProjects.length === 0 ? (
                      <p className="text-center text-gray-500 py-6">Cadastre uma obra para visualizar a tendência</p>
                    ) : (
                      <ProjectFinancialTrend
                        companyId={selectedCompany.id}
                        projectId={selectedTrendProjectId || constructionProjects[0].id}
                        formatCurrency={formatCurrency}
                      />
                    )}
                  </CardContent>
                </Card>
                  </TabsContent>

                  <TabsContent value="projetos">
                {/* Projects overview */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Projetos de Construção</CardTitle>
                      <div className="flex gap-2">
                        <Dialog open={showMaterialForm} onOpenChange={(open) => {
                          setShowMaterialForm(open);
                          if (!open) {
                            setEditingMaterial(null);
                            setMaterialForm({ name: "", unit: "unidade", unit_price: "", description: "", kind: "material" });
                          }
                        }}>
                          <DialogTrigger asChild>
                            <Button variant="outline">
                              <Package className="h-4 w-4 mr-2" />
                              Gerenciar Materiais
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto w-[95vw]">
                            <DialogHeader>
                              <DialogTitle className="text-black">
                                {editingMaterial ? 'Editar Material' : 'Gerenciar Materiais'}
                              </DialogTitle>
                            </DialogHeader>
                            
                            {/* Lista de materiais cadastrados */}
                            {!editingMaterial && materials.length > 0 && (
                              <div className="mb-4">
                                <h4 className="font-medium text-black mb-2">Materiais Cadastrados:</h4>
                                {/* Campo de busca */}
                                <div className="relative mb-2">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                  <Input
                                    placeholder="Buscar material..."
                                    value={materialSearchTerm || ''}
                                    onChange={(e) => setMaterialSearchTerm(e.target.value)}
                                    className="pl-10 text-black"
                                  />
                                </div>
                                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                                  {materials
                                    .filter(m => !materialSearchTerm || 
                                      m.name.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
                                      m.unit.toLowerCase().includes(materialSearchTerm.toLowerCase())
                                    )
                                    .map((material) => (
                                    <div key={material.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                      <div className="text-black flex items-center gap-2">
                                        <Badge className={material.kind === "servico" ? "bg-purple-100 text-purple-800 border border-purple-300" : "bg-blue-100 text-blue-800 border border-blue-300"}>
                                          {material.kind === "servico" ? "Serviço" : "Material"}
                                        </Badge>
                                        <span className="font-medium">{material.name}</span>
                                        <span className="text-gray-600">
                                          ({material.unit}) - {formatCurrency(material.unit_price)}
                                        </span>
                                      </div>
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => startEditMaterial(material)}
                                        >
                                          <Edit className="h-4 w-4 text-black" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => deleteMaterial(material.id)}
                                        >
                                          <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Formulário de novo material */}
                            <div className="border-t pt-4">
                              <h4 className="font-medium text-black mb-2">
                                {editingMaterial ? 'Editar Material' : 'Adicionar Novo Material'}
                              </h4>
                              <form onSubmit={handleMaterialSubmit} className="space-y-4">
                                {/* Tipo: Material ou Serviço */}
                                <div>
                                  <label className="text-sm font-bold text-black block mb-1">Tipo *</label>
                                  <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        checked={(materialForm.kind || "material") === "material"}
                                        onChange={() => setMaterialForm({...materialForm, kind: "material", unit: "unidade"})}
                                        data-testid="material-kind-material"
                                      />
                                      <span className="text-black">Material</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        checked={materialForm.kind === "servico"}
                                        onChange={() => setMaterialForm({...materialForm, kind: "servico", unit: "hora"})}
                                        data-testid="material-kind-servico"
                                      />
                                      <span className="text-black">Serviço</span>
                                    </label>
                                  </div>
                                </div>
                                <Input
                                  placeholder={materialForm.kind === "servico" ? "Nome do serviço" : "Nome do material"}
                                  value={materialForm.name}
                                  onChange={(e) => setMaterialForm({...materialForm, name: e.target.value})}
                                  required
                                  className="text-black"
                                />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <Select 
                                    value={materialForm.unit} 
                                    onValueChange={(value) => setMaterialForm({...materialForm, unit: value})}
                                  >
                                    <SelectTrigger className="text-black">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {materialForm.kind === "servico" ? (
                                        <>
                                          <SelectItem value="hora">Hora</SelectItem>
                                          <SelectItem value="diaria">Diária</SelectItem>
                                          <SelectItem value="empreitada">Empreitada</SelectItem>
                                          <SelectItem value="mensal">Mensal</SelectItem>
                                          <SelectItem value="m²">m² (área)</SelectItem>
                                          <SelectItem value="m³">m³ (volume)</SelectItem>
                                          <SelectItem value="unidade">Unidade</SelectItem>
                                        </>
                                      ) : (
                                        <>
                                          <SelectItem value="unidade">Unidade</SelectItem>
                                          <SelectItem value="m³">m³</SelectItem>
                                          <SelectItem value="m²">m²</SelectItem>
                                          <SelectItem value="kg">kg</SelectItem>
                                          <SelectItem value="saco">Saco</SelectItem>
                                          <SelectItem value="litro">Litro</SelectItem>
                                        </>
                                      )}
                                    </SelectContent>
                                  </Select>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    placeholder={materialForm.kind === "servico" ? "Valor por unidade" : "Preço unitário"}
                                    value={materialForm.unit_price}
                                    onChange={(e) => setMaterialForm({...materialForm, unit_price: e.target.value})}
                                    required
                                    className="text-black"
                                  />
                                </div>
                                <Textarea
                                  placeholder="Descrição"
                                  value={materialForm.description}
                                  onChange={(e) => setMaterialForm({...materialForm, description: e.target.value})}
                                  className="text-black"
                                />
                                <Button type="submit" className="w-full">
                                  {editingMaterial ? 'Atualizar Material' : 'Criar Material'}
                                </Button>
                                {editingMaterial && (
                                  <Button 
                                    type="button" 
                                    variant="outline" 
                                    className="w-full"
                                    onClick={() => {
                                      setEditingMaterial(null);
                                      setMaterialForm({ name: "", unit: "unidade", unit_price: "", description: "", kind: "material" });
                                    }}
                                  >
                                    Cancelar Edição
                                  </Button>
                                )}
                              </form>
                            </div>
                          </DialogContent>
                        </Dialog>
                        
                        {/* Botão Pedido de Material/Serviço — abre Dialog que está montado fora das Tabs */}
                        <Button
                          variant="outline"
                          className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                          data-testid="btn-novo-pedido-material-servico"
                          onClick={() => {
                            setEditingMaterialOrder(false);
                            setMaterialOrderForm({
                              project_id: "",
                              phase_id: "",
                              supplier_id: "",
                              observations: "",
                              kind: "material",
                              items: [{ id: Date.now().toString(), description: "", unit: "unidade", quantity: "", unit_price: "" }]
                            });
                            setShowMaterialOrderForm(true);
                          }}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Pedido de Material/Serviço
                        </Button>
                        
                        <Dialog open={showProjectForm} onOpenChange={setShowProjectForm}>
                          <DialogTrigger asChild>
                            <Button>
                              <Plus className="h-4 w-4 mr-2" />
                              Novo Projeto
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="text-black">Novo Projeto de Construção</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleProjectSubmit} className="space-y-4">
                              <Input
                                placeholder="Nome do projeto"
                                value={projectForm.name}
                                onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
                                required
                                className="text-black"
                              />
                              <Select 
                                value={projectForm.client_id} 
                                onValueChange={(value) => setProjectForm({...projectForm, client_id: value})}
                              >
                                <SelectTrigger className="text-black">
                                  <SelectValue placeholder="Selecione um cliente" />
                                </SelectTrigger>
                                <SelectContent>
                                  {clients.map(client => (
                                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Valor aprovado"
                                  value={projectForm.approved_value}
                                  onChange={(e) => setProjectForm({...projectForm, approved_value: e.target.value})}
                                  required
                                  className="text-black"
                                />
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Custo estimado"
                                  value={projectForm.estimated_cost}
                                  onChange={(e) => setProjectForm({...projectForm, estimated_cost: e.target.value})}
                                  className="text-black"
                                />
                              </div>
                              <Textarea
                                placeholder="Descrição do projeto"
                                value={projectForm.description}
                                onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                                className="text-black"
                              />
                              <Button type="submit" className="w-full">Criar Projeto</Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                        
                        {/* Budget Upload Dialog */}
                        <Dialog open={showBudgetUpload} onOpenChange={setShowBudgetUpload}>
                          <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="text-black">
                                Importar Planilha de Orçamento - {budgetUploadProject?.name}
                              </DialogTitle>
                              <DialogDescription>
                                Importe uma planilha Excel (.xlsx ou .xls) com o orçamento do projeto. 
                                A planilha deve conter colunas como: Item, Código, Descrição, Quantidade, Preço Unitário, etc.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Selecione a planilha de orçamento:
                                </label>
                                <Input
                                  type="file"
                                  accept=".xlsx,.xls"
                                  onChange={(e) => setBudgetFile(e.target.files[0])}
                                  className="text-black"
                                  disabled={budgetUploading}
                                />
                              </div>
                              
                              {budgetFile && (
                                <div className="p-3 bg-blue-50 rounded-md">
                                  <p className="text-sm text-blue-700">
                                    <strong>Arquivo selecionado:</strong> {budgetFile.name}
                                  </p>
                                  <p className="text-sm text-blue-600">
                                    Tamanho: {(budgetFile.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              )}
                              
                              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                                <div className="flex">
                                  <div className="ml-3">
                                    <p className="text-sm text-yellow-700">
                                      <strong>Importante:</strong> A planilha deve seguir o padrão:
                                    </p>
                                    <ul className="mt-2 text-sm text-yellow-600 list-disc list-inside">
                                      <li>Colunas: ITEM, CÓDIGO, DESCRIÇÃO, UNID, QUANTIDADE, PREÇO UNITÁRIO</li>
                                      <li>Dados organizados em seções (ADMINISTRAÇÃO, PRAÇA, QUIOSQUE, etc.)</li>
                                      <li>Valores numéricos nas colunas de quantidade e preço</li>
                                      <li>Informação de BDI (se disponível)</li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex gap-2 pt-4">
                                <Button
                                  onClick={handleBudgetUpload}
                                  disabled={!budgetFile || budgetUploading}
                                  className="flex-1"
                                >
                                  {budgetUploading ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                      Processando...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="h-4 w-4 mr-2" />
                                      Importar Planilha
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setShowBudgetUpload(false);
                                    setBudgetFile(null);
                                    setBudgetUploadProject(null);
                                  }}
                                  disabled={budgetUploading}
                                >
                                  Cancelar
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Filtro de Projetos: busca + sub-abas por status */}
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Buscar por nome ou cliente..."
                          value={filters.projects.search}
                          onChange={(e) => updateFilter('projects', 'search', e.target.value)}
                          className="pl-10 text-black"
                          data-testid="projects-search"
                        />
                      </div>
                      {(() => {
                        const counts = {
                          all: constructionProjects.length,
                          planejamento: constructionProjects.filter(p => p.status === 'planejamento').length,
                          em_andamento: constructionProjects.filter(p => p.status === 'em_andamento').length,
                          pausado: constructionProjects.filter(p => p.status === 'pausado').length,
                          concluido: constructionProjects.filter(p => p.status === 'concluido').length,
                          cancelado: constructionProjects.filter(p => p.status === 'cancelado').length,
                        };
                        return (
                          <Tabs value={filters.projects.status} onValueChange={(v) => updateFilter('projects', 'status', v)}>
                            <TabsList className="inline-flex w-full h-auto overflow-x-auto whitespace-nowrap">
                              <TabsTrigger value="all" data-testid="proj-status-all">Todos <Badge variant="outline" className="ml-1">{counts.all}</Badge></TabsTrigger>
                              <TabsTrigger value="planejamento" data-testid="proj-status-planejamento" className="data-[state=active]:bg-gray-200">Planejamento <Badge variant="outline" className="ml-1">{counts.planejamento}</Badge></TabsTrigger>
                              <TabsTrigger value="em_andamento" data-testid="proj-status-andamento" className="data-[state=active]:bg-blue-100 data-[state=active]:text-blue-800">Em Andamento <Badge variant="outline" className="ml-1">{counts.em_andamento}</Badge></TabsTrigger>
                              <TabsTrigger value="pausado" data-testid="proj-status-pausado" className="data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-800">Pausado <Badge variant="outline" className="ml-1">{counts.pausado}</Badge></TabsTrigger>
                              <TabsTrigger value="concluido" data-testid="proj-status-concluido" className="data-[state=active]:bg-green-100 data-[state=active]:text-green-800">Concluído <Badge variant="outline" className="ml-1">{counts.concluido}</Badge></TabsTrigger>
                              <TabsTrigger value="cancelado" data-testid="proj-status-cancelado" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-800">Cancelado <Badge variant="outline" className="ml-1">{counts.cancelado}</Badge></TabsTrigger>
                            </TabsList>
                          </Tabs>
                        );
                      })()}
                      <p className="text-sm text-gray-500 mt-2">
                        Mostrando {filteredProjects.length} de {constructionProjects.length} projetos
                      </p>
                    </div>
                    <div className="grid gap-4">
                      {filteredProjects.map((project) => (
                        <Card key={project.id} className="cursor-pointer hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg">{project.name}</h3>
                                <p className="text-gray-600">Cliente: {project.client_name}</p>
                                {project.description && (
                                  <p className="text-sm text-gray-500 mt-1">{project.description}</p>
                                )}
                                <div className="flex gap-4 mt-2">
                                  <span className="text-sm">
                                    <strong>Valor Aprovado:</strong> {formatCurrency(project.approved_value)}
                                  </span>
                                  {project.estimated_cost && (
                                    <span className="text-sm">
                                      <strong>Custo Estimado:</strong> {formatCurrency(project.estimated_cost)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <Badge 
                                  variant={project.status === 'concluido' ? 'default' : 'outline'}
                                  className={
                                    project.status === 'concluido' ? 'bg-green-100 text-green-800 border-green-300' :
                                    project.status === 'em_andamento' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                    project.status === 'pausado' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                    project.status === 'cancelado' ? 'bg-red-100 text-red-800 border-red-300' :
                                    'bg-gray-100 text-gray-800'
                                  }
                                >
                                  {project.status === 'planejamento' && 'Planejamento'}
                                  {project.status === 'em_andamento' && 'Em Andamento'}
                                  {project.status === 'concluido' && 'Concluído'}
                                  {project.status === 'pausado' && 'Pausado'}
                                  {project.status === 'cancelado' && 'Cancelado'}
                                </Badge>
                                <div className="flex gap-2">
                                  <Button 
                                    onClick={() => openBudgetUpload(project)}
                                    size="sm"
                                    variant="outline"
                                    className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                                  >
                                    <Upload className="h-4 w-4 mr-2" />
                                    Importar Planilha
                                  </Button>
                                  <Button 
                                    onClick={() => startEditProject(project)}
                                    size="sm"
                                    variant="outline"
                                    className="bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100"
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Editar
                                  </Button>
                                  <Button 
                                    onClick={() => selectProject(project)}
                                    size="sm"
                                  >
                                    Gerenciar Projeto
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                  </TabsContent>

                  <TabsContent value="pedidos">
                {/* Lista de Pedidos de Material com sub-tabs por status */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-black">Pedidos de Material/Serviço</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      // Aplica filtros de data
                      const filterByDates = (orders) => {
                        const q = (moFilters.search || "").trim().toLowerCase();
                        return orders.filter(o => {
                          // Filtro mês/ano (criado em)
                          if (moFilters.year && o.created_at) {
                            const dt = new Date(o.created_at);
                            if (dt.getFullYear() !== parseInt(moFilters.year)) return false;
                            if (moFilters.month !== "all" && (dt.getMonth() + 1) !== parseInt(moFilters.month)) return false;
                          }
                          // Filtro data solicitação (created_at)
                          if (moFilters.solic_inicio && o.created_at && new Date(o.created_at) < new Date(moFilters.solic_inicio)) return false;
                          if (moFilters.solic_fim && o.created_at && new Date(o.created_at) > new Date(moFilters.solic_fim + "T23:59:59")) return false;
                          // Filtro data aprovação (technical OR financial)
                          const aprov = o.financial_approval_date || o.technical_approval_date;
                          if ((moFilters.aprov_inicio || moFilters.aprov_fim) && !aprov) return false;
                          if (moFilters.aprov_inicio && aprov && new Date(aprov) < new Date(moFilters.aprov_inicio)) return false;
                          if (moFilters.aprov_fim && aprov && new Date(aprov) > new Date(moFilters.aprov_fim + "T23:59:59")) return false;
                          // Busca livre (nº pedido, projeto, medição, fornecedor, observações, descrição dos itens)
                          if (q) {
                            const haystack = [
                              o.order_number, o.project_name, o.phase_name, o.supplier_name,
                              o.observations, o.destination, o.kind,
                              ...(o.items || []).map(it => `${it.description} ${it.unit}`),
                            ].filter(Boolean).join(" \n ").toLowerCase();
                            if (!haystack.includes(q)) return false;
                          }
                          return true;
                        });
                      };

                      const allFiltered = filterByDates(materialOrders);
                      const pendentes = allFiltered.filter(o => o.status === "pendente");
                      const emAndamento = allFiltered.filter(o => o.status === "aprovado_tecnico");
                      const concluidos = allFiltered.filter(o => ["concluido", "aprovado_financeiro"].includes(o.status));
                      const rejeitados = allFiltered.filter(o => o.status === "rejeitado");

                      const currentList = moStatusTab === "pendente" ? pendentes
                        : moStatusTab === "em_andamento" ? emAndamento
                        : moStatusTab === "concluido" ? concluidos
                        : rejeitados;

                      const currentYear = new Date().getFullYear();
                      const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
                      const months = [
                        { v: "all", l: "Todos" },
                        { v: "1", l: "Jan" }, { v: "2", l: "Fev" }, { v: "3", l: "Mar" }, { v: "4", l: "Abr" },
                        { v: "5", l: "Mai" }, { v: "6", l: "Jun" }, { v: "7", l: "Jul" }, { v: "8", l: "Ago" },
                        { v: "9", l: "Set" }, { v: "10", l: "Out" }, { v: "11", l: "Nov" }, { v: "12", l: "Dez" },
                      ];

                      return (
                        <div className="space-y-4">
                          {/* Busca livre */}
                          <div className="bg-gray-50 border rounded-lg p-3">
                            <label className="text-xs font-bold text-black block mb-1">Pesquisar</label>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                type="text"
                                placeholder="Nº do pedido, obra, medição, fornecedor, item, observação..."
                                value={moFilters.search}
                                onChange={(e) => setMoFilters({ ...moFilters, search: e.target.value })}
                                className="text-black h-9 pl-9 pr-9"
                                data-testid="mo-filter-search"
                              />
                              {moFilters.search && (
                                <button
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                                  onClick={() => setMoFilters({ ...moFilters, search: "" })}
                                  data-testid="mo-filter-search-clear"
                                  aria-label="Limpar busca"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Filtros */}
                          <div className="bg-gray-50 border rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            <div>
                              <label className="text-xs font-bold text-black block mb-1">Mês</label>
                              <Select value={moFilters.month} onValueChange={(v) => setMoFilters({...moFilters, month: v})}>
                                <SelectTrigger className="text-black h-9" data-testid="mo-filter-month"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {months.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-xs font-bold text-black block mb-1">Ano</label>
                              <Select value={String(moFilters.year)} onValueChange={(v) => setMoFilters({...moFilters, year: parseInt(v)})}>
                                <SelectTrigger className="text-black h-9" data-testid="mo-filter-year"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-xs font-bold text-black block mb-1">Solicitação De</label>
                              <Input type="date" value={moFilters.solic_inicio} onChange={(e) => setMoFilters({...moFilters, solic_inicio: e.target.value})} className="text-black h-9" data-testid="mo-filter-solic-de" />
                            </div>
                            <div>
                              <label className="text-xs font-bold text-black block mb-1">Solicitação Até</label>
                              <Input type="date" value={moFilters.solic_fim} onChange={(e) => setMoFilters({...moFilters, solic_fim: e.target.value})} className="text-black h-9" data-testid="mo-filter-solic-ate" />
                            </div>
                            <div>
                              <label className="text-xs font-bold text-black block mb-1">Aprovação De</label>
                              <Input type="date" value={moFilters.aprov_inicio} onChange={(e) => setMoFilters({...moFilters, aprov_inicio: e.target.value})} className="text-black h-9" data-testid="mo-filter-aprov-de" />
                            </div>
                            <div>
                              <label className="text-xs font-bold text-black block mb-1">Aprovação Até</label>
                              <Input type="date" value={moFilters.aprov_fim} onChange={(e) => setMoFilters({...moFilters, aprov_fim: e.target.value})} className="text-black h-9" data-testid="mo-filter-aprov-ate" />
                            </div>
                          </div>
                          {(moFilters.month !== "all" || moFilters.solic_inicio || moFilters.solic_fim || moFilters.aprov_inicio || moFilters.aprov_fim || moFilters.search) && (
                            <Button size="sm" variant="outline" onClick={() => setMoFilters({ search: "", month: "all", year: new Date().getFullYear(), solic_inicio: "", solic_fim: "", aprov_inicio: "", aprov_fim: "" })} data-testid="mo-clear-filters">
                              Limpar Filtros
                            </Button>
                          )}

                          {/* Sub-tabs de Status */}
                          <Tabs value={moStatusTab} onValueChange={setMoStatusTab}>
                            <TabsList className="inline-flex w-full h-auto overflow-x-auto whitespace-nowrap">
                              <TabsTrigger value="pendente" data-testid="mo-status-pendente" className="text-yellow-700 data-[state=active]:bg-yellow-100">
                                Pendentes <Badge variant="outline" className="ml-2">{pendentes.length}</Badge>
                              </TabsTrigger>
                              <TabsTrigger value="em_andamento" data-testid="mo-status-andamento" className="text-blue-700 data-[state=active]:bg-blue-100">
                                Em Andamento <Badge variant="outline" className="ml-2">{emAndamento.length}</Badge>
                              </TabsTrigger>
                              <TabsTrigger value="concluido" data-testid="mo-status-concluido" className="text-green-700 data-[state=active]:bg-green-100">
                                Concluídos <Badge variant="outline" className="ml-2">{concluidos.length}</Badge>
                              </TabsTrigger>
                              <TabsTrigger value="rejeitado" data-testid="mo-status-rejeitado" className="text-red-700 data-[state=active]:bg-red-100">
                                Rejeitados <Badge variant="outline" className="ml-2">{rejeitados.length}</Badge>
                              </TabsTrigger>
                            </TabsList>
                          </Tabs>

                          {/* Lista filtrada */}
                          {currentList.length === 0 ? (
                            <p className="text-center text-gray-500 py-6">Nenhum pedido encontrado</p>
                          ) : (
                            <div className="space-y-3 max-h-[700px] overflow-y-auto">
                              {currentList.map(order => (
                                <div
                                  key={order.id}
                                  className={`p-4 rounded-lg border ${
                                    order.status === 'pendente' ? 'bg-yellow-50 border-yellow-200' :
                                    order.status === 'aprovado_tecnico' ? 'bg-blue-50 border-blue-200' :
                                    order.status === 'concluido' || order.status === 'aprovado_financeiro' ? 'bg-green-50 border-green-200' :
                                    'bg-red-50 border-red-200'
                                  }`}
                                  data-testid={`mo-card-${order.id}`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        {order.order_number && (
                                          <Badge className="bg-gray-800 text-white font-mono">
                                            #{String(order.order_number).padStart(4, '0')}
                                          </Badge>
                                        )}
                                        <h4 className="font-semibold text-black">{order.project_name}</h4>
                                        <Badge className={(order.kind || "material") === "servico" ? "bg-purple-100 text-purple-800 border border-purple-300" : "bg-blue-100 text-blue-800 border border-blue-300"}>
                                          {(order.kind || "material") === "servico" ? "Serviço" : "Material"}
                                        </Badge>
                                        <Badge className={
                                          order.status === 'pendente' ? 'bg-yellow-600' :
                                          order.status === 'aprovado_tecnico' ? 'bg-blue-600' :
                                          order.status === 'concluido' || order.status === 'aprovado_financeiro' ? 'bg-green-600' :
                                          'bg-red-600'
                                        }>
                                          {order.status === 'pendente' ? 'Pendente' :
                                           order.status === 'aprovado_tecnico' ? 'Em Andamento (Aguard. Financeiro)' :
                                           order.status === 'concluido' || order.status === 'aprovado_financeiro' ? 'Concluído' :
                                           'Rejeitado'}
                                        </Badge>
                                      </div>
                                      <p className="text-sm text-gray-600">
                                        Medição: {order.phase_name} • Fornecedor: {order.supplier_name}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {order.items?.length || 0} itens • Total: <strong className="text-black">{formatCurrency(order.total_amount)}</strong>
                                      </p>
                                      <div className="text-xs text-gray-500 flex flex-wrap gap-3 mt-1">
                                        <span><strong>Solicitado:</strong> {order.created_at ? new Date(order.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-'}</span>
                                        {order.technical_approval_date && <span><strong>Aprov. Técnico:</strong> {new Date(order.technical_approval_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>}
                                        {order.financial_approval_date && <span><strong>Aprov. Financeiro:</strong> {new Date(order.financial_approval_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>}
                                        {order.status === 'rejeitado' && order.rejected_at && (
                                          <span className="text-red-700"><strong>Rejeitado ({order.rejected_by_stage === 'financeiro' ? 'Financeiro' : 'Técnico'}):</strong> {new Date(order.rejected_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}{order.rejected_by_user_name ? ` por ${order.rejected_by_user_name}` : ''}</span>
                                        )}
                                      </div>
                                    </div>
                                    <Button size="sm" variant="outline" onClick={() => setSelectedMaterialOrder(order)}>
                                      <Eye className="h-4 w-4 mr-1" /> Detalhes
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
                  </TabsContent>
                </Tabs>

                {/* Modal de Detalhes do Pedido */}
                <Dialog open={selectedMaterialOrder !== null} onOpenChange={() => setSelectedMaterialOrder(null)}>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw]">
                    <DialogHeader>
                      <DialogTitle className="text-black">
                        {selectedMaterialOrder?.order_number ? `Pedido #${String(selectedMaterialOrder.order_number).padStart(4, '0')} - ` : 'Pedido de Material - '}{selectedMaterialOrder?.project_name}
                      </DialogTitle>
                      <DialogDescription>
                        {selectedMaterialOrder?.phase_name} • {selectedMaterialOrder?.supplier_name}
                      </DialogDescription>
                    </DialogHeader>
                    
                    {selectedMaterialOrder && (
                      <div className="space-y-4">
                        {/* Status e Aprovações */}
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-center">
                            <div>
                              <Badge className={
                                selectedMaterialOrder.status === 'pendente' ? 'bg-yellow-600' :
                                selectedMaterialOrder.status === 'aprovado_tecnico' ? 'bg-blue-600' :
                                selectedMaterialOrder.status === 'concluido' ? 'bg-green-600' :
                                'bg-red-600'
                              }>
                                {selectedMaterialOrder.status === 'pendente' ? 'Pendente Aprovação Técnica' :
                                 selectedMaterialOrder.status === 'aprovado_tecnico' ? 'Aprovado - Aguardando Financeiro' :
                                 selectedMaterialOrder.status === 'concluido' ? 'Concluído' :
                                 'Rejeitado'}
                              </Badge>
                              {selectedMaterialOrder.approved_by_technical && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Aprovado por: {selectedMaterialOrder.approved_by_technical} em {new Date(selectedMaterialOrder.technical_approval_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                </p>
                              )}
                              {selectedMaterialOrder.approved_by_financial && (
                                <p className="text-xs text-gray-500">
                                  Financeiro: {selectedMaterialOrder.approved_by_financial} em {new Date(selectedMaterialOrder.financial_approval_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-black">{formatCurrency(selectedMaterialOrder.total_amount)}</p>
                            </div>
                          </div>
                        </div>

                        {/* Itens do Pedido */}
                        <div>
                          <h4 className="font-medium text-black mb-2">Itens do Pedido:</h4>
                          <div className="space-y-2 border rounded-lg p-2">
                            {selectedMaterialOrder.items?.map((item, index) => (
                              <div key={item.id || `${item.description}-${index}`} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                                <div>
                                  <span className="font-medium text-black">{item.description}</span>
                                  <span className="text-gray-500 ml-2">
                                    {item.quantity} {item.unit} × {formatCurrency(item.unit_price)}
                                  </span>
                                </div>
                                <span className="font-bold text-black">{formatCurrency(item.total_price)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Documentos */}
                        <div>
                          <h4 className="font-medium text-black mb-2">Documentos Anexados:</h4>
                          {selectedMaterialOrder.documents?.length > 0 ? (
                            <div className="space-y-2">
                              {selectedMaterialOrder.documents.map((doc) => (
                                <div key={doc.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-red-500" />
                                    <span className="text-sm text-black">{doc.filename}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {doc.doc_type === 'boleto' || doc.document_type === 'boleto' ? 'Boleto' : 'Nota Fiscal'}
                                    </Badge>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => downloadOrderDocument(selectedMaterialOrder.id, doc.id, doc.filename)}
                                    >
                                      Download
                                    </Button>
                                    {selectedMaterialOrder.status === 'pendente' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-red-600 hover:bg-red-50"
                                        onClick={() => deleteOrderDocument(selectedMaterialOrder.id, doc.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">Nenhum documento anexado</p>
                          )}

                          {/* Upload de Documentos (se pendente) */}
                          {selectedMaterialOrder.status === 'pendente' && (
                            <div className="mt-3 p-3 border-2 border-dashed rounded-lg">
                              <p className="text-sm text-gray-600 mb-2">Anexar documento (PDF):</p>
                              <div className="flex gap-2 items-center">
                                <Select 
                                  value={uploadDocumentType}
                                  onValueChange={setUploadDocumentType}
                                >
                                  <SelectTrigger className="w-40 text-black">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="boleto">Boleto</SelectItem>
                                    <SelectItem value="nota_fiscal">Nota Fiscal</SelectItem>
                                  </SelectContent>
                                </Select>
                                <div className="flex-1">
                                  <input
                                    type="file"
                                    accept=".pdf"
                                    id="order-doc-upload"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleOrderDocumentUpload(selectedMaterialOrder.id, uploadDocumentType, file);
                                        e.target.value = ''; // Reset input
                                      }
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full"
                                    disabled={orderDocumentUploading}
                                    onClick={() => document.getElementById('order-doc-upload').click()}
                                  >
                                    {orderDocumentUploading ? 'Enviando...' : <><Paperclip className="h-4 w-4 mr-2" /> Anexar PDF</>}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Observações */}
                        {selectedMaterialOrder.observations && (
                          <div>
                            <h4 className="font-medium text-black mb-1">Observações:</h4>
                            <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{selectedMaterialOrder.observations}</p>
                          </div>
                        )}

                        {/* Botões de Ação */}
                        <div className="flex gap-2 pt-4 border-t">
                          {selectedMaterialOrder.status === 'pendente' && (
                            <>
                              <Button
                                variant="outline"
                                onClick={() => startEditMaterialOrder(selectedMaterialOrder)}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </Button>
                              <Button
                                className="flex-1 bg-green-500 hover:bg-green-600"
                                onClick={() => approveTechnical(selectedMaterialOrder.id)}
                                disabled={!selectedMaterialOrder.documents || selectedMaterialOrder.documents.length === 0}
                              >
                                <Check className="h-4 w-4 mr-2" />
                                Aprovar (Resp. Técnico)
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => rejectOrder(selectedMaterialOrder.id)}
                              >
                                Rejeitar
                              </Button>
                            </>
                          )}
                          {selectedMaterialOrder.status === 'aprovado_tecnico' && (
                            <>
                              <Button
                                className="flex-1 bg-blue-500 hover:bg-blue-600"
                                onClick={() => openFinancialApproval(selectedMaterialOrder.id)}
                                data-testid="approve-financial-btn"
                              >
                                <Check className="h-4 w-4 mr-2" />
                                Aprovar (Financeiro)
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => rejectOrder(selectedMaterialOrder.id)}
                                data-testid="reject-financial-btn"
                              >
                                <X className="h-4 w-4 mr-2" />
                                Rejeitar
                              </Button>
                            </>
                          )}
                          {selectedMaterialOrder.status === 'concluido' && (
                            <Button
                              variant="outline"
                              className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                              onClick={openEditPayment}
                              data-testid="edit-payment-btn"
                            >
                              <CreditCard className="h-4 w-4 mr-2" />
                              Editar Forma de Pagamento
                            </Button>
                          )}
                        </div>

                        {selectedMaterialOrder.status === 'pendente' && (!selectedMaterialOrder.documents || selectedMaterialOrder.documents.length === 0) && (
                          <p className="text-sm text-orange-600 text-center">
                            ⚠️ É necessário anexar pelo menos um documento (boleto ou nota fiscal) antes da aprovação técnica
                          </p>
                        )}
                      </div>
                    )}
                  </DialogContent>
                </Dialog>

                {/* Modal de Aprovação Financeira */}
                <Dialog open={showFinancialApprovalForm} onOpenChange={setShowFinancialApprovalForm}>
                  <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-black">
                        {financialApprovalForm._edit_mode ? "Editar Forma de Pagamento" : "Aprovação Financeira"}
                      </DialogTitle>
                      <DialogDescription>
                        {financialApprovalForm._edit_mode
                          ? "Reorganize parcelas, datas ou forma de pagamento. As contas pendentes antigas serão substituídas."
                          : "Defina as condições de pagamento para este pedido de material"}
                      </DialogDescription>
                    </DialogHeader>
                    
                    {selectedMaterialOrder && (
                      <div className="space-y-4">
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-black font-medium">{selectedMaterialOrder.project_name}</p>
                          <p className="text-sm text-gray-600">{selectedMaterialOrder.phase_name} • {selectedMaterialOrder.supplier_name}</p>
                          <p className="text-lg font-bold text-black mt-1">{formatCurrency(selectedMaterialOrder.total_amount)}</p>
                        </div>

                        {/* Tipo de Despesa */}
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Tipo de Despesa</label>
                          <Select 
                            value={financialApprovalForm.expense_type}
                            onValueChange={(value) => setFinancialApprovalForm({...financialApprovalForm, expense_type: value})}
                          >
                            <SelectTrigger className="text-black">
                              <SelectValue placeholder="Selecione o tipo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="material">Material de Construção</SelectItem>
                              <SelectItem value="mao_de_obra">Mão de Obra</SelectItem>
                              <SelectItem value="equipamento">Equipamento</SelectItem>
                              <SelectItem value="servico">Serviço</SelectItem>
                              <SelectItem value="transporte">Transporte</SelectItem>
                              <SelectItem value="outros">Outros</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Parcelamento */}
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="is_installment"
                            checked={financialApprovalForm.is_installment}
                            onChange={(e) => {
                              const isInstallment = e.target.checked;
                              if (isInstallment) {
                                const installments = generateInstallments(
                                  2, 
                                  selectedMaterialOrder.total_amount,
                                  financialApprovalForm.due_date
                                );
                                setFinancialApprovalForm({
                                  ...financialApprovalForm,
                                  is_installment: true,
                                  total_installments: 2,
                                  installments
                                });
                              } else {
                                setFinancialApprovalForm({
                                  ...financialApprovalForm,
                                  is_installment: false,
                                  total_installments: 1,
                                  installments: []
                                });
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <label htmlFor="is_installment" className="text-sm font-medium text-black">
                            Pagamento Parcelado
                          </label>
                        </div>

                        {financialApprovalForm.is_installment ? (
                          <>
                            {/* Número de Parcelas */}
                            <div>
                              <label className="block text-sm font-medium text-black mb-1">Número de Parcelas</label>
                              <Select 
                                value={financialApprovalForm.total_installments.toString()}
                                onValueChange={(value) => {
                                  const num = parseInt(value);
                                  const installments = generateInstallments(
                                    num, 
                                    selectedMaterialOrder.total_amount,
                                    financialApprovalForm.due_date || new Date().toISOString().split('T')[0]
                                  );
                                  setFinancialApprovalForm({
                                    ...financialApprovalForm,
                                    total_installments: num,
                                    installments
                                  });
                                }}
                              >
                                <SelectTrigger className="text-black">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                                    <SelectItem key={n} value={n.toString()}>{n}x</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Lista de Parcelas Editável */}
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              <label className="block text-sm font-medium text-black">Parcelas:</label>
                              {financialApprovalForm.installments.map((inst, index) => (
                                <div key={index} className="flex gap-2 items-center p-2 bg-gray-50 rounded">
                                  <span className="text-sm font-medium text-black w-8">{index + 1}ª</span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={inst.amount}
                                    onChange={(e) => updateInstallment(index, 'amount', parseFloat(e.target.value) || 0)}
                                    className="w-28 text-black"
                                  />
                                  <Input
                                    type="date"
                                    value={inst.due_date}
                                    onChange={(e) => updateInstallment(index, 'due_date', e.target.value)}
                                    className="flex-1 text-black"
                                  />
                                </div>
                              ))}
                              <div className="text-right text-sm font-bold text-black">
                                Total: {formatCurrency(financialApprovalForm.installments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0))}
                              </div>
                            </div>
                          </>
                        ) : (
                          /* Data de Vencimento (pagamento à vista) */
                          <div>
                            <label className="block text-sm font-medium text-black mb-1">Data de Vencimento</label>
                            <Input
                              type="date"
                              value={financialApprovalForm.due_date}
                              onChange={(e) => setFinancialApprovalForm({...financialApprovalForm, due_date: e.target.value})}
                              className="text-black"
                            />
                          </div>
                        )}

                        <div className="flex gap-2 pt-4 border-t">
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => setShowFinancialApprovalForm(false)}
                          >
                            Cancelar
                          </Button>
                          <Button 
                            className="flex-1 bg-green-500 hover:bg-green-600"
                            onClick={submitFinancialApproval}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Aprovar e Criar Conta a Pagar
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                </>
              ) : (
                // Project management view
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button variant="outline" onClick={() => setSelectedProject(null)}>
                        ← Voltar aos Projetos
                      </Button>
                      <h2 className="text-xl md:text-2xl font-bold">{selectedProject.name}</h2>
                    </div>
                    <Dialog open={showPhaseForm} onOpenChange={setShowPhaseForm}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Nova Fase
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-black">Nova Fase do Cronograma</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handlePhaseSubmit} className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                              placeholder="Nome da fase"
                              value={phaseForm.phase_name}
                              onChange={(e) => setPhaseForm({...phaseForm, phase_name: e.target.value})}
                              required
                              className="text-black"
                            />
                            <Input
                              type="number"
                              placeholder="Ordem"
                              value={phaseForm.order}
                              onChange={(e) => setPhaseForm({...phaseForm, order: e.target.value})}
                              className="text-black"
                            />
                          </div>
                          <Textarea
                            placeholder="Descrição da fase"
                            value={phaseForm.description}
                            onChange={(e) => setPhaseForm({...phaseForm, description: e.target.value})}
                            className="text-black"
                          />
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Custo estimado"
                              value={phaseForm.estimated_cost}
                              onChange={(e) => setPhaseForm({...phaseForm, estimated_cost: e.target.value})}
                              className="text-black"
                            />
                            <div>
                              <label className="text-sm font-medium text-gray-700 mb-1 block">Data Início</label>
                              <Input
                                type="date"
                                value={phaseForm.start_date}
                                onChange={(e) => setPhaseForm({...phaseForm, start_date: e.target.value})}
                                className="text-black"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700 mb-1 block">Data Fim</label>
                              <Input
                                type="date"
                                value={phaseForm.end_date}
                                onChange={(e) => setPhaseForm({...phaseForm, end_date: e.target.value})}
                                className="text-black"
                              />
                            </div>
                          </div>
                          <Button type="submit" className="w-full">Criar Fase</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Project Phases */}
                  <div className="grid gap-6 min-w-0 max-w-full overflow-hidden">
                    {/* Project Info Card */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Informações do Projeto</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p><strong>Cliente:</strong> {selectedProject.client_name}</p>
                            <p><strong>Valor Aprovado:</strong> {formatCurrency(selectedProject.approved_value)}</p>
                            {selectedProject.estimated_cost && (
                              <p><strong>Custo Estimado:</strong> {formatCurrency(selectedProject.estimated_cost)}</p>
                            )}
                            <p><strong>Status:</strong> {selectedProject.status}</p>
                          </div>
                          <div>
                            {selectedProject.description && (
                              <p><strong>Descrição:</strong> {selectedProject.description}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Mini-gráfico: Recebido x Gasto últimos 6 meses */}
                    <ProjectFinancialTrend
                      companyId={selectedCompany.id}
                      projectId={selectedProject.id}
                      formatCurrency={formatCurrency}
                    />

                    {/* Cronograma Físico de Obra (Gantt) */}
                    <ProjectSchedule
                      companyId={selectedCompany.id}
                      projectId={selectedProject.id}
                      projectName={selectedProject.name}
                    />

                    {/* Phases Management */}
                    {projectSchedule.length > 0 ? (
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold">Cronograma de Fases</h3>
                        {projectSchedule.sort((a, b) => a.order - b.order).map((phase) => (
                          <Card key={phase.id} className="border-l-4 border-l-blue-500">
                            <CardContent className="p-6">
                              <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                  <h4 className="text-lg font-semibold">{phase.phase_name}</h4>
                                  {phase.description && (
                                    <p className="text-gray-600 mt-1">{phase.description}</p>
                                  )}
                                  <div className="flex gap-6 mt-2 text-sm text-gray-500">
                                    {phase.start_date && (
                                      <span>Início: {new Date(phase.start_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>
                                    )}
                                    {phase.end_date && (
                                      <span>Fim: {new Date(phase.end_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>
                                    )}
                                    {phase.estimated_cost && (
                                      <span>Custo estimado: {formatCurrency(phase.estimated_cost)}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge 
                                    variant={phase.status === 'concluido' ? 'default' : 'outline'}
                                    className={
                                      phase.status === 'concluido' ? 'bg-green-100 text-green-800' :
                                      phase.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                                    }
                                  >
                                    {phase.status === 'pendente' && 'Pendente'}
                                    {phase.status === 'em_andamento' && 'Em Andamento'}
                                    {phase.status === 'concluido' && 'Concluído'}
                                  </Badge>
                                </div>
                              </div>

                              {/* Phase Actions */}
                              <div className="flex gap-2 mb-4 flex-wrap">
                                <input
                                  type="file"
                                  accept=".pdf"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      handlePhaseDocumentUpload(phase.id, phase.phase_name.toLowerCase(), file);
                                      e.target.value = '';
                                    }
                                  }}
                                  style={{ display: 'none' }}
                                  id={`phase-file-${phase.id}`}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => document.getElementById(`phase-file-${phase.id}`).click()}
                                >
                                  <Paperclip className="h-4 w-4 mr-2" />
                                  Anexar Documento
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedPhase(phase);
                                    setShowMaterialUsageForm(true);
                                  }}
                                >
                                  <Package className="h-4 w-4 mr-2" />
                                  Adicionar Material
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                                  onClick={() => {
                                    setSelectedPhase(phase);
                                    setShowServiceUsageForm(true);
                                  }}
                                >
                                  <Users className="h-4 w-4 mr-2" />
                                  Adicionar Serviço
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                                  onClick={() => startEditPhase(phase)}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar Fase
                                </Button>
                                {phase.status !== 'concluido' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
                                    onClick={() => closePhase(phase.id)}
                                    data-testid={`close-phase-${phase.id}`}
                                  >
                                    <Check className="h-4 w-4 mr-2" />
                                    Encerrar Fase
                                  </Button>
                                )}
                              </div>

                              {/* Resumo de Custo da Fase (se concluída) */}
                              {phase.status === 'concluido' && phase.actual_cost !== undefined && (
                                <div className={`mb-4 p-3 rounded-lg ${
                                  phase.actual_cost <= (phase.estimated_cost || 0) 
                                    ? 'bg-green-50 border border-green-200' 
                                    : 'bg-red-50 border border-red-200'
                                }`}>
                                  <h5 className="font-medium mb-2 flex items-center gap-2">
                                    {phase.actual_cost <= (phase.estimated_cost || 0) 
                                      ? <Check className="h-4 w-4 text-green-600" />
                                      : <AlertCircle className="h-4 w-4 text-red-600" />
                                    }
                                    Resumo de Custos
                                  </h5>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="text-gray-600">Estimado:</span>
                                      <p className="font-bold">{formatCurrency(phase.estimated_cost || 0)}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Realizado:</span>
                                      <p className={`font-bold ${
                                        phase.actual_cost <= (phase.estimated_cost || 0) ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {formatCurrency(phase.actual_cost)}
                                      </p>
                                    </div>
                                    <div className="col-span-2">
                                      <span className="text-gray-600">
                                        {phase.actual_cost <= (phase.estimated_cost || 0) 
                                          ? 'Economia: ' 
                                          : 'Excedente: '
                                        }
                                      </span>
                                      <span className={`font-bold ${
                                        phase.actual_cost <= (phase.estimated_cost || 0) ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {formatCurrency(Math.abs((phase.estimated_cost || 0) - phase.actual_cost))}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Materiais e Serviços Utilizados na Fase */}
                              {getPhaseUsage(phase.id).length > 0 && (
                                <div className="bg-gray-50 p-4 rounded-lg">
                                  <h5 className="font-medium mb-2">Materiais e Serviços Utilizados:</h5>
                                  <div className="space-y-2">
                                    {getPhaseUsage(phase.id).map((usage) => (
                                      <div key={usage.id} className="flex justify-between items-center text-sm gap-2 flex-wrap">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <Badge className={(usage.kind || "material") === "servico" ? "bg-purple-100 text-purple-800 border border-purple-300" : "bg-blue-100 text-blue-800 border border-blue-300"}>
                                            {(usage.kind || "material") === "servico" ? "Serviço" : "Material"}
                                          </Badge>
                                          <span>{usage.material_name} - {usage.quantity} {usage.material_unit}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">
                                            {formatCurrency(usage.total_cost || 0)}
                                          </span>
                                          {(phase.status || "pendente") !== "concluido" && (
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="text-red-600 hover:text-red-800 p-1 h-auto"
                                              onClick={async () => {
                                                if (!window.confirm(`Remover "${usage.material_name}" desta medição?`)) return;
                                                try {
                                                  await axios.delete(`${API}/companies/${selectedCompany.id}/construction/material-usage/${usage.id}`);
                                                  toast.success("Item removido");
                                                  fetchData();
                                                } catch (e) {
                                                  toast.error(e.response?.data?.detail || "Erro ao remover");
                                                }
                                              }}
                                              data-testid={`delete-usage-${usage.id}`}
                                              title="Remover item (medição pendente)"
                                            >
                                              <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="border-t pt-2 mt-2">
                                    <div className="flex justify-between font-medium text-gray-700">
                                      <span>Total Materiais + Serviços:</span>
                                      <span>
                                        {formatCurrency(
                                          getPhaseUsage(phase.id).reduce((sum, usage) => 
                                            sum + (usage.total_cost || 0), 0
                                          )
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Serviços Efetuados na Fase */}
                              {getPhaseServices(phase.id).length > 0 && (
                                <div className="bg-blue-50 p-4 rounded-lg">
                                  <h5 className="font-medium mb-2">Serviços Efetuados:</h5>
                                  <div className="space-y-2">
                                    {getPhaseServices(phase.id).map((service) => (
                                      <div key={service.id} className="flex justify-between items-center text-sm">
                                        <div className="flex-1">
                                          <div className="font-medium">{service.service_name}</div>
                                          <div className="text-xs text-gray-600">
                                            {service.quantity} {service.unit} × {formatCurrency(service.unit_price)}
                                            {service.supplier_name && (
                                              <span> • {service.supplier_name}</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">
                                            {formatCurrency(service.total_cost || 0)}
                                          </span>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0"
                                            onClick={() => startEditPhaseService(service)}
                                          >
                                            <Edit className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                            onClick={() => deletePhaseService(service.id)}
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="border-t pt-2 mt-2">
                                    <div className="flex justify-between font-medium text-blue-700">
                                      <span>Total Serviços:</span>
                                      <span>
                                        {formatCurrency(
                                          getPhaseServices(phase.id).reduce((sum, service) => 
                                            sum + (service.total_cost || 0), 0
                                          )
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* TOTAL GASTO NA FASE - Sempre por último */}
                              {(getPhaseUsage(phase.id).length > 0 || getPhaseServices(phase.id).length > 0) && (
                                <div className="bg-green-100 border-2 border-green-500 p-4 rounded-lg mt-2">
                                  <div className="flex justify-between items-center mb-2">
                                    <h5 className="font-bold text-green-800 text-lg">TOTAL GASTO NA FASE:</h5>
                                    <span className="font-bold text-xl text-green-800">
                                      {formatCurrency(
                                        getPhaseUsage(phase.id).reduce((sum, usage) => sum + (usage.total_cost || 0), 0) +
                                        getPhaseServices(phase.id).reduce((sum, service) => sum + (service.total_cost || 0), 0)
                                      )}
                                    </span>
                                  </div>
                                  <div className="text-sm text-green-700 border-t border-green-300 pt-2">
                                    <div className="flex justify-between">
                                      <span>Total Materiais:</span>
                                      <span className="font-medium">
                                        {formatCurrency(
                                          getPhaseUsage(phase.id).reduce((sum, usage) => sum + (usage.total_cost || 0), 0)
                                        )}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Total Serviços:</span>
                                      <span className="font-medium">
                                        {formatCurrency(
                                          getPhaseServices(phase.id).reduce((sum, service) => sum + (service.total_cost || 0), 0)
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Phase Documents */}
                              {getPhaseDocuments(phase.phase_name.toLowerCase()).length > 0 && (
                                <div className="bg-blue-50 p-4 rounded-lg mt-4">
                                  <h5 className="font-medium mb-2">Documentos:</h5>
                                  <div className="space-y-1">
                                    {getPhaseDocuments(phase.phase_name.toLowerCase()).map((doc) => (
                                      <div key={doc.id} className="flex items-center text-sm">
                                        <FileText className="h-4 w-4 mr-2 text-blue-600" />
                                        <span>{doc.name}</span>
                                        <span className="text-gray-500 ml-2">
                                          ({new Date(doc.uploaded_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })})
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <Card>
                        <CardContent className="text-center py-12">
                          <p className="text-gray-500 mb-4">Nenhuma fase criada ainda</p>
                          <Button onClick={() => setShowPhaseForm(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Criar Primeira Fase
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Material Usage Dialog */}
            <Dialog open={showMaterialUsageForm} onOpenChange={setShowMaterialUsageForm}>
              <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-black">
                    Adicionar Material - {selectedPhase?.phase_name}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleMaterialUsageSubmit} className="space-y-4">
                  <Select 
                    value={materialUsageForm.material_id} 
                    onValueChange={(value) => setMaterialUsageForm({...materialUsageForm, material_id: value})}
                  >
                    <SelectTrigger className="text-black">
                      <SelectValue placeholder="Selecione um material" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials.map(material => (
                        <SelectItem key={material.id} value={material.id}>
                          {material.name} - {formatCurrency(material.unit_price)}/{material.unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Quantidade utilizada"
                    value={materialUsageForm.quantity}
                    onChange={(e) => setMaterialUsageForm({...materialUsageForm, quantity: e.target.value})}
                    required
                    className="text-black"
                  />
                  <Textarea
                    placeholder="Observações (opcional)"
                    value={materialUsageForm.observations}
                    onChange={(e) => setMaterialUsageForm({...materialUsageForm, observations: e.target.value})}
                    className="text-black"
                  />
                  <Button type="submit" className="w-full">Adicionar Material</Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Service Usage Dialog */}
            <Dialog open={showServiceUsageForm} onOpenChange={(open) => {
              setShowServiceUsageForm(open);
              if (!open) {
                setEditingPhaseService(null);
                setServiceUsageForm({
                  service_name: "",
                  unit: "horas",
                  quantity: "",
                  unit_price: "",
                  supplier_name: "",
                  description: "",
                  observations: ""
                });
              }
            }}>
              <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-black">
                    {editingPhaseService ? 'Editar' : 'Adicionar'} Serviço - {selectedPhase?.phase_name}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleServiceUsageSubmit} className="space-y-4">
                  <Input
                    placeholder="Nome do Serviço"
                    value={serviceUsageForm.service_name}
                    onChange={(e) => setServiceUsageForm({...serviceUsageForm, service_name: e.target.value})}
                    required
                    className="text-black"
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select 
                      value={serviceUsageForm.unit} 
                      onValueChange={(value) => setServiceUsageForm({...serviceUsageForm, unit: value})}
                    >
                      <SelectTrigger className="text-black">
                        <SelectValue placeholder="Unidade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="horas">Horas</SelectItem>
                        <SelectItem value="diarias">Diárias</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Quantidade"
                      value={serviceUsageForm.quantity}
                      onChange={(e) => setServiceUsageForm({...serviceUsageForm, quantity: e.target.value})}
                      required
                      className="text-black"
                    />
                  </div>
                  
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Valor (R$)"
                    value={serviceUsageForm.unit_price}
                    onChange={(e) => setServiceUsageForm({...serviceUsageForm, unit_price: e.target.value})}
                    required
                    className="text-black"
                  />
                  
                  <Input
                    placeholder="Nome do Prestador de Serviços"
                    value={serviceUsageForm.supplier_name}
                    onChange={(e) => setServiceUsageForm({...serviceUsageForm, supplier_name: e.target.value})}
                    className="text-black"
                  />
                  
                  <Textarea
                    placeholder="Descrição do Serviço"
                    value={serviceUsageForm.description}
                    onChange={(e) => setServiceUsageForm({...serviceUsageForm, description: e.target.value})}
                    className="text-black"
                    rows={2}
                  />
                  
                  <Textarea
                    placeholder="Observações (opcional)"
                    value={serviceUsageForm.observations}
                    onChange={(e) => setServiceUsageForm({...serviceUsageForm, observations: e.target.value})}
                    className="text-black"
                    rows={2}
                  />
                  
                  <Button type="submit" className="w-full">
                    {editingPhaseService ? 'Atualizar' : 'Adicionar'} Serviço
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Edit Project Dialog */}
            <Dialog open={showEditProjectForm} onOpenChange={setShowEditProjectForm}>
              <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-black">Editar Projeto</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEditProjectSubmit} className="space-y-4">
                  <Input
                    placeholder="Nome do projeto"
                    value={editProjectForm.name}
                    onChange={(e) => setEditProjectForm({...editProjectForm, name: e.target.value})}
                    required
                    className="text-black"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Valor aprovado"
                      value={editProjectForm.approved_value}
                      onChange={(e) => setEditProjectForm({...editProjectForm, approved_value: e.target.value})}
                      required
                      className="text-black"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Custo estimado (opcional)"
                      value={editProjectForm.estimated_cost}
                      onChange={(e) => setEditProjectForm({...editProjectForm, estimated_cost: e.target.value})}
                      className="text-black"
                    />
                  </div>
                  <Select 
                    value={editProjectForm.status} 
                    onValueChange={(value) => setEditProjectForm({...editProjectForm, status: value})}
                  >
                    <SelectTrigger className="text-black">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planejamento">Planejamento</SelectItem>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="pausado">Pausado</SelectItem>
                      <SelectItem value="concluido">Concluído</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Descrição do projeto (opcional)"
                    value={editProjectForm.description}
                    onChange={(e) => setEditProjectForm({...editProjectForm, description: e.target.value})}
                    className="text-black"
                  />
                  <Button type="submit" className="w-full">Atualizar Projeto</Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Edit Phase Dialog */}
            <Dialog open={showEditPhaseForm} onOpenChange={setShowEditPhaseForm}>
              <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-black">Editar Fase</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEditPhaseSubmit} className="space-y-4">
                  <Input
                    placeholder="Nome da fase"
                    value={phaseForm.phase_name}
                    onChange={(e) => setPhaseForm({...phaseForm, phase_name: e.target.value})}
                    required
                    className="text-black"
                  />
                  <Textarea
                    placeholder="Descrição da fase"
                    value={phaseForm.description}
                    onChange={(e) => setPhaseForm({...phaseForm, description: e.target.value})}
                    className="text-black"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Custo estimado"
                    value={phaseForm.estimated_cost}
                    onChange={(e) => setPhaseForm({...phaseForm, estimated_cost: e.target.value})}
                    className="text-black"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Data Início</label>
                      <Input
                        type="date"
                        value={phaseForm.start_date}
                        onChange={(e) => setPhaseForm({...phaseForm, start_date: e.target.value})}
                        className="text-black"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Data Fim</label>
                      <Input
                        type="date"
                        value={phaseForm.end_date}
                        onChange={(e) => setPhaseForm({...phaseForm, end_date: e.target.value})}
                        className="text-black"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full">Atualizar Fase</Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>


          {/* ===================== MÓDULO ESTOQUE ===================== */}
          <TabsContent value="estoque">
            <div className="space-y-6">
              <Tabs value={stockActiveTab} onValueChange={setStockActiveTab}>
                <TabsList className="inline-flex w-full h-auto overflow-x-auto whitespace-nowrap">
                  <TabsTrigger value="itens" data-testid="stock-tab-itens">Itens em Estoque</TabsTrigger>
                  <TabsTrigger value="saidas" data-testid="stock-tab-saidas">
                    Saídas <Badge variant="outline" className="ml-1">{stockExits.filter(e => e.status === 'pendente').length}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="movimentacoes" data-testid="stock-tab-movimentacoes">Movimentações</TabsTrigger>
                </TabsList>

                {/* ITENS */}
                <TabsContent value="itens" className="mt-4">
                  <Card>
                    <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle>Itens em Estoque</CardTitle>
                      <div className="flex gap-2 flex-wrap">
                        <Button onClick={() => {
                          setEditingMaterialOrder(false);
                          setMaterialOrderForm({
                            project_id: "",
                            phase_id: "",
                            supplier_id: "",
                            observations: "",
                            kind: "material",
                            destination: "estoque",
                            items: [{ id: Date.now().toString(), description: "", unit: "unidade", quantity: "", unit_price: "" }]
                          });
                          setShowMaterialOrderForm(true);
                        }} className="bg-green-500 hover:bg-green-600" data-testid="btn-stock-entry-request">
                          <Plus className="h-4 w-4 mr-1" /> Solicitar Entrada
                        </Button>
                        <Button variant="outline" onClick={() => setShowStockExitForm(true)} data-testid="btn-stock-exit">
                          <Package className="h-4 w-4 mr-1" /> Nova Saída
                        </Button>
                        <Button variant="outline" onClick={() => setShowStockAdjustForm(true)} data-testid="btn-stock-adjust">
                          <RefreshCw className="h-4 w-4 mr-1" /> Ajuste
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-gray-50 border rounded-lg p-3 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="relative md:col-span-2">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="Buscar por nome..."
                            value={stockFilters.search}
                            onChange={(e) => setStockFilters({...stockFilters, search: e.target.value})}
                            className="pl-10 text-black"
                            data-testid="stock-filter-search"
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={stockFilters.lowStockOnly}
                              onChange={(e) => setStockFilters({...stockFilters, lowStockOnly: e.target.checked})}
                              data-testid="stock-filter-low"
                            />
                            <span className="text-sm text-black">Somente baixo estoque</span>
                          </label>
                          <Button size="sm" variant="outline" onClick={fetchStockItems}>Atualizar</Button>
                        </div>
                      </div>
                      <div className="border rounded-lg overflow-x-auto">
                        <table className="w-full text-sm min-w-[700px]">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="p-2 text-left text-black font-bold">Tipo</th>
                              <th className="p-2 text-left text-black font-bold">Item</th>
                              <th className="p-2 text-left text-black font-bold">Unid.</th>
                              <th className="p-2 text-right text-black font-bold">Saldo</th>
                              <th className="p-2 text-right text-black font-bold">Mínimo</th>
                              <th className="p-2 text-right text-black font-bold">Valor Unit.</th>
                              <th className="p-2 text-left text-black font-bold">Fornecedor</th>
                              <th className="p-2 text-center text-black font-bold">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stockItems.filter(i => stockFilters.kind === "all" || i.kind === stockFilters.kind).filter(i => !stockFilters.search || (i.name || "").toLowerCase().includes(stockFilters.search.toLowerCase())).filter(i => !stockFilters.lowStockOnly || (i.quantity || 0) <= (i.min_quantity || 0)).length === 0 ? (
                              <tr><td colSpan="8" className="text-center text-gray-500 py-6">Nenhum item encontrado</td></tr>
                            ) : (
                              stockItems
                                .filter(i => stockFilters.kind === "all" || i.kind === stockFilters.kind)
                                .filter(i => !stockFilters.search || (i.name || "").toLowerCase().includes(stockFilters.search.toLowerCase()))
                                .filter(i => !stockFilters.lowStockOnly || (i.quantity || 0) <= (i.min_quantity || 0))
                                .map(item => {
                                  const low = (item.quantity || 0) <= (item.min_quantity || 0);
                                  return (
                                    <tr key={item.id} className={`border-t ${low ? 'bg-red-50' : ''}`} data-testid={`stock-row-${item.id}`}>
                                      <td className="p-2"><Badge className={item.kind === 'servico' ? 'bg-purple-100 text-purple-800 border border-purple-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}>{item.kind === 'servico' ? 'Serv' : 'Mat'}</Badge></td>
                                      <td className="p-2 text-black font-medium">{item.name}</td>
                                      <td className="p-2 text-gray-700">{item.unit}</td>
                                      <td className={`p-2 text-right font-bold ${low ? 'text-red-700' : 'text-black'}`}>{item.quantity}{low && <span className="ml-1" title="Saldo abaixo do mínimo">⚠</span>}</td>
                                      <td className="p-2 text-right text-gray-700">{item.min_quantity}</td>
                                      <td className="p-2 text-right text-gray-700">{formatCurrency(item.unit_price || 0)}</td>
                                      <td className="p-2 text-gray-700">{item.supplier_name || "-"}</td>
                                      <td className="p-2 text-center">
                                        <div className="flex gap-1 justify-center">
                                          <Button size="sm" variant="outline" onClick={() => { setEditingStockItem({...item}); setShowStockEditForm(true); }}><Edit className="h-3 w-3" /></Button>
                                          <Button size="sm" variant="destructive" onClick={() => deleteStockItem(item.id)}><Trash2 className="h-3 w-3" /></Button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* SAÍDAS */}
                <TabsContent value="saidas" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Solicitações de Saída</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {stockExits.length === 0 ? (
                          <p className="text-gray-500 py-4 text-center">Nenhuma solicitação de saída</p>
                        ) : (
                          stockExits.map(ex => (
                            <div key={ex.id} className={`border rounded-lg p-3 ${ex.status === 'pendente' ? 'bg-yellow-50 border-yellow-200' : ex.status === 'aprovado' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid={`stock-exit-${ex.id}`}>
                              <div className="flex justify-between items-start gap-3 flex-wrap">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <Badge className={ex.status === 'pendente' ? 'bg-yellow-600' : ex.status === 'aprovado' ? 'bg-green-600' : 'bg-red-600'}>
                                      {ex.status === 'pendente' ? 'Pendente' : ex.status === 'aprovado' ? 'Aprovada' : 'Rejeitada'}
                                    </Badge>
                                    <h4 className="font-bold text-black">{ex.item_name}</h4>
                                    <span className="text-sm font-medium text-black">{ex.quantity} un.</span>
                                  </div>
                                  <p className="text-sm text-gray-700"><strong>Solicitante:</strong> {ex.requester_name}</p>
                                  <p className="text-sm text-gray-700"><strong>Obra/Medição:</strong> {ex.project_name} • {ex.phase_name}</p>
                                  {ex.observations && <p className="text-sm text-gray-600">{ex.observations}</p>}
                                  <p className="text-xs text-gray-500">Criado: {new Date(ex.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}{ex.approved_at && ` • Aprovado: ${new Date(ex.approved_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} por ${ex.approved_by_user_name}`}</p>
                                </div>
                                {ex.status === 'pendente' && (
                                  <div className="flex flex-col gap-2">
                                    <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white" onClick={() => approveStockExit(ex.id)} data-testid={`approve-exit-${ex.id}`}>
                                      <Check className="h-4 w-4 mr-1" /> Aprovar
                                    </Button>
                                    <Button size="sm" variant="destructive" onClick={() => rejectStockExit(ex.id)} data-testid={`reject-exit-${ex.id}`}>
                                      <X className="h-4 w-4 mr-1" /> Rejeitar
                                    </Button>
                                  </div>
                                )}
                                {ex.status === 'aprovado' && (
                                  <div className="flex flex-col gap-2">
                                    <Button
                                      size="sm"
                                      className="bg-blue-500 hover:bg-blue-600 text-white"
                                      onClick={async () => {
                                        try {
                                          const r = await axios.get(
                                            `${API}/companies/${selectedCompany.id}/stock/exits/${ex.id}/receipt`,
                                            { responseType: 'blob' }
                                          );
                                          const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                                          window.open(url, '_blank');
                                          setTimeout(() => window.URL.revokeObjectURL(url), 10000);
                                        } catch (err) {
                                          toast.error('Erro ao gerar recibo');
                                        }
                                      }}
                                      data-testid={`stock-exit-receipt-${ex.id}`}
                                    >
                                      <FileText className="h-4 w-4 mr-1" /> Recibo
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* MOVIMENTAÇÕES */}
                <TabsContent value="movimentacoes" className="mt-4">
                  <Card>
                    <CardHeader><CardTitle>Histórico de Movimentações</CardTitle></CardHeader>
                    <CardContent>
                      <div className="border rounded-lg overflow-x-auto">
                        <table className="w-full text-sm min-w-[700px]">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="p-2 text-left text-black font-bold">Data</th>
                              <th className="p-2 text-left text-black font-bold">Tipo</th>
                              <th className="p-2 text-left text-black font-bold">Item</th>
                              <th className="p-2 text-right text-black font-bold">Qtd</th>
                              <th className="p-2 text-left text-black font-bold">Detalhes</th>
                              <th className="p-2 text-left text-black font-bold">Por</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stockMovements.length === 0 ? (
                              <tr><td colSpan="6" className="text-center text-gray-500 py-6">Nenhuma movimentação</td></tr>
                            ) : (
                              stockMovements.map(m => (
                                <tr key={m.id} className="border-t" data-testid={`stock-mov-${m.id}`}>
                                  <td className="p-2 text-gray-700">{new Date(m.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</td>
                                  <td className="p-2">
                                    <Badge className={m.type === 'entrada' ? 'bg-green-100 text-green-800 border border-green-300' : m.type === 'saida' ? 'bg-blue-100 text-blue-800 border border-blue-300' : 'bg-orange-100 text-orange-800 border border-orange-300'}>
                                      {m.type === 'entrada' ? 'Entrada' : m.type === 'saida' ? 'Saída' : 'Ajuste'}
                                    </Badge>
                                  </td>
                                  <td className="p-2 text-black">{m.item_name}</td>
                                  <td className={`p-2 text-right font-bold ${m.quantity < 0 ? 'text-red-700' : 'text-green-700'}`}>{m.quantity > 0 ? '+' : ''}{m.quantity}</td>
                                  <td className="p-2 text-gray-700 text-xs">
                                    {m.source === 'manual' && 'Entrada Manual'}
                                    {m.source === 'pedido' && 'Pedido aprovado'}
                                    {m.source === 'saida_obra' && `${m.requester_name} → ${m.project_name} / ${m.phase_name}`}
                                    {m.source === 'ajuste' && `Ajuste: ${m.reason}`}
                                  </td>
                                  <td className="p-2 text-gray-700">{m.user_name || '-'}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>


          <TabsContent value="distributor">
            <DistributorModule 
              companyId={selectedCompany.id} 
              clients={clients} 
              permissions={myPermissions.permissions || []}
              isAdmin={hasAdminAccess}
              bankAccounts={bankAccounts}
            />
          </TabsContent>

          <TabsContent value="factory">
            <FactoryModule
              companyId={selectedCompany.id}
              companyName={selectedCompany.name}
              clients={clients}
              permissions={myPermissions.permissions || []}
              isAdmin={hasAdminAccess}
              bankAccounts={bankAccounts}
            />
          </TabsContent>

          <TabsContent value="accounts-payable">
            {/* Subabas horizontais de Contas a Pagar */}
            <Tabs value={apActiveTab} onValueChange={setApActiveTab} className="w-full">
              <TabsList className="inline-flex w-full mb-4 h-auto overflow-x-auto whitespace-nowrap">
                <TabsTrigger value="dashboard" data-testid="ap-tab-dashboard">Dashboard</TabsTrigger>
                <TabsTrigger value="lista" data-testid="ap-tab-lista">Lista de Contas</TabsTrigger>
                <TabsTrigger value="programados" data-testid="ap-tab-programados">Programados</TabsTrigger>
                <TabsTrigger value="agenda" data-testid="ap-tab-agenda">Agenda & Vencimentos</TabsTrigger>
                <TabsTrigger value="resumo" data-testid="ap-tab-resumo">Resumo por Tipo</TabsTrigger>
                <TabsTrigger value="saldo-contas" data-testid="ap-tab-saldo-contas">Saldo de Contas</TabsTrigger>
                <TabsTrigger value="rp" data-testid="ap-tab-rp">RP (Recurso Próprio)</TabsTrigger>
                <TabsTrigger value="folha" data-testid="ap-tab-folha">Folha de Pagamento</TabsTrigger>
              </TabsList>

              <TabsContent value="saldo-contas">
                <BankAccountsTab
                  companyId={selectedCompany.id}
                  onDataChange={async () => {
                    try {
                      const bankRes = await axios.get(`${API}/companies/${selectedCompany.id}/bank-accounts`);
                      setBankAccounts(bankRes.data || []);
                    } catch (e) { /* silencioso */ }
                  }}
                />
              </TabsContent>

              <TabsContent value="rp">
                <OwnerContributionsTab
                  companyId={selectedCompany.id}
                  bankAccounts={bankAccounts}
                  onDataChange={async () => {
                    try {
                      const bankRes = await axios.get(`${API}/companies/${selectedCompany.id}/bank-accounts`);
                      setBankAccounts(bankRes.data || []);
                    } catch (e) { /* silencioso */ }
                    fetchData();
                  }}
                />
              </TabsContent>

              <TabsContent value="folha">
                <PayrollTab
                  companyId={selectedCompany.id}
                  bankAccounts={bankAccounts}
                  currentUser={user}
                  selectedCompany={selectedCompany}
                  onDataChange={async () => {
                    try {
                      const bankRes = await axios.get(`${API}/companies/${selectedCompany.id}/bank-accounts`);
                      setBankAccounts(bankRes.data || []);
                    } catch (e) { /* silencioso */ }
                    fetchData();
                  }}
                />
              </TabsContent>

              <TabsContent value="dashboard">
            {/* Dashboard de Contas a Pagar */}
            <div className="space-y-6">

              {/* Filtro de Mês + Resumo do período */}
              {(() => {
                const monthNames = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
                // Gerar opções de ano: últimos 3 anos + próximo 1
                const currentYear = new Date().getFullYear();
                const yearOptions = [];
                for (let y = currentYear - 3; y <= currentYear + 1; y++) yearOptions.push(y);

                // Filtrar contas pelo mês/ano selecionado (baseado em due_date)
                const filteredByMonth = accountsPayable.filter(acc => {
                  if (!acc.due_date) return false;
                  const d = new Date(acc.due_date);
                  if (d.getFullYear() !== apDashboardYear) return false;
                  // Se mês = 0, aceita todos os meses do ano
                  if (apDashboardMonth !== 0 && d.getMonth() + 1 !== apDashboardMonth) return false;
                  return true;
                });

                const sum = (arr) => arr.reduce((t, a) => t + (parseFloat(a.amount) || 0), 0);
                const totalValor = sum(filteredByMonth);
                const pendentesValor = sum(filteredByMonth.filter(a => a.status === 'pending'));
                const aprovadosValor = sum(filteredByMonth.filter(a => a.status === 'approved'));
                const vencidasValor = sum(filteredByMonth.filter(a => {
                  const paga = a.status === 'paid' || a.status === 'pago';
                  const aprovada = a.status === 'approved';
                  return !paga && !aprovada && new Date(a.due_date) < new Date();
                }));
                const pagasValor = sum(filteredByMonth.filter(a => a.status === 'paid' || a.status === 'pago'));

                const periodoLabel = apDashboardMonth === 0
                  ? `Ano de ${apDashboardYear}`
                  : `${monthNames[apDashboardMonth - 1]} de ${apDashboardYear}`;

                return (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h2 className="text-xl font-bold text-black">Dashboard Contas a Pagar</h2>
                        <p className="text-sm text-gray-500">
                          {filteredByMonth.length} conta(s) · {periodoLabel}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-sm font-medium text-gray-700">Mês:</label>
                        <Select value={String(apDashboardMonth)} onValueChange={(v) => setApDashboardMonth(Number(v))}>
                          <SelectTrigger className="w-40 text-black" data-testid="ap-dashboard-month-filter">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Todos os meses</SelectItem>
                            {monthNames.map((name, i) => (
                              <SelectItem key={i + 1} value={String(i + 1)}>{name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <label className="text-sm font-medium text-gray-700 ml-2">Ano:</label>
                        <Select value={String(apDashboardYear)} onValueChange={(v) => setApDashboardYear(Number(v))}>
                          <SelectTrigger className="w-28 text-black" data-testid="ap-dashboard-year-filter">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {yearOptions.map(y => (
                              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Cards com VALORES em R$ */}
                    <div className="grid grid-cols-1 md:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                      <Card data-testid="ap-card-total" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setApDrillDown({ open: true, kind: 'total', title: 'Total — todas as contas do período', color: 'blue', accounts: filteredByMonth })}>
                        <CardContent className="p-6">
                          <div className="flex items-center">
                            <CreditCard className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-600">Total</p>
                              <p className="text-xl font-bold text-gray-900">{formatCurrency(totalValor)}</p>
                              <p className="text-xs text-gray-500 mt-1">{filteredByMonth.length} conta(s)</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-yellow-50 cursor-pointer hover:shadow-md transition-shadow" data-testid="ap-card-pendentes" onClick={() => setApDrillDown({ open: true, kind: 'pendentes', title: 'Pendentes', color: 'yellow', accounts: filteredByMonth.filter(a => a.status === 'pending') })}>
                        <CardContent className="p-6">
                          <div className="flex items-center">
                            <AlertCircle className="h-8 w-8 text-yellow-600" />
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-600">Pendentes</p>
                              <p className="text-xl font-bold text-yellow-700">{formatCurrency(pendentesValor)}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {filteredByMonth.filter(a => a.status === 'pending').length} conta(s)
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-blue-50 cursor-pointer hover:shadow-md transition-shadow" data-testid="ap-card-aprovados" onClick={() => setApDrillDown({ open: true, kind: 'aprovados', title: 'Aprovados', color: 'blue', accounts: filteredByMonth.filter(a => a.status === 'approved') })}>
                        <CardContent className="p-6">
                          <div className="flex items-center">
                            <Shield className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-600">Aprovados</p>
                              <p className="text-xl font-bold text-blue-700">{formatCurrency(aprovadosValor)}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {filteredByMonth.filter(a => a.status === 'approved').length} conta(s)
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-red-50 cursor-pointer hover:shadow-md transition-shadow" data-testid="ap-card-vencidas" onClick={() => setApDrillDown({ open: true, kind: 'vencidas', title: 'Vencidas', color: 'red', accounts: filteredByMonth.filter(a => {
                        const paga = a.status === 'paid' || a.status === 'pago';
                        const aprovada = a.status === 'approved';
                        return !paga && !aprovada && new Date(a.due_date) < new Date();
                      }) })}>
                        <CardContent className="p-6">
                          <div className="flex items-center">
                            <TrendingDown className="h-8 w-8 text-red-600" />
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-600">Vencidas</p>
                              <p className="text-xl font-bold text-red-700">{formatCurrency(vencidasValor)}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {filteredByMonth.filter(a => {
                                  const paga = a.status === 'paid' || a.status === 'pago';
                                  const aprovada = a.status === 'approved';
                                  return !paga && !aprovada && new Date(a.due_date) < new Date();
                                }).length} conta(s)
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-green-50 cursor-pointer hover:shadow-md transition-shadow" data-testid="ap-card-pagas" onClick={() => setApDrillDown({ open: true, kind: 'pagas', title: 'Pagas', color: 'green', accounts: filteredByMonth.filter(a => a.status === 'paid' || a.status === 'pago') })}>
                        <CardContent className="p-6">
                          <div className="flex items-center">
                            <Check className="h-8 w-8 text-green-600" />
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-600">Pagas</p>
                              <p className="text-xl font-bold text-green-700">{formatCurrency(pagasValor)}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {filteredByMonth.filter(a => a.status === 'paid' || a.status === 'pago').length} conta(s)
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </>
                );
              })()}

              {/* Mini Gráfico de Tendência - Pagas x Pendentes x Vencidas (últimos 6 meses) */}
              {(() => {
                const monthLabelsPt = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
                const now = new Date();
                const buckets = [];
                for (let i = 5; i >= 0; i--) {
                  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                  buckets.push({
                    key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
                    label: `${monthLabelsPt[d.getMonth()]}/${String(d.getFullYear()).slice(-2)}`,
                    pagas: 0,
                    pendentes: 0,
                    vencidas: 0,
                  });
                }
                const byKey = Object.fromEntries(buckets.map(b => [b.key, b]));
                const today = new Date();
                accountsPayable.forEach(acc => {
                  if (!acc.due_date) return;
                  const d = new Date(acc.due_date);
                  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  const b = byKey[key];
                  if (!b) return;
                  const amount = parseFloat(acc.amount) || 0;
                  const paga = acc.status === 'paid' || acc.status === 'pago';
                  const aprovada = acc.status === 'approved';
                  if (paga) b.pagas += amount;
                  else if (!aprovada && new Date(acc.due_date) < today) b.vencidas += amount;
                  else b.pendentes += amount;
                });
                const hasData = buckets.some(b => b.pagas + b.pendentes + b.vencidas > 0);

                return (
                  <Card data-testid="ap-trend-chart">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-black text-lg flex items-center">
                        <BarChart3 className="h-5 w-5 mr-2 text-gray-700" />
                        Tendência — Últimos 6 meses
                      </CardTitle>
                      <p className="text-xs text-gray-500">Pagas x Pendentes x Vencidas (em R$ por mês de vencimento)</p>
                    </CardHeader>
                    <CardContent>
                      {!hasData ? (
                        <p className="text-center text-gray-500 py-8">Sem dados nos últimos 6 meses</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={buckets} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                            <YAxis
                              tick={{ fontSize: 11 }}
                              tickFormatter={(v) => v >= 1000 ? `R$ ${(v / 1000).toFixed(0)}k` : `R$ ${v}`}
                            />
                            <Tooltip
                              formatter={(value) => formatCurrency(value)}
                              labelStyle={{ color: '#000', fontWeight: 'bold' }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="pagas" name="Pagas" fill="#10b981" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="pendentes" name="Pendentes" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="vencidas" name="Vencidas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}

            </div>
              </TabsContent>

              {/* Drill-down Dialog dos cards do Dashboard Contas a Pagar */}
              <Dialog open={apDrillDown.open} onOpenChange={(open) => setApDrillDown(prev => ({ ...prev, open }))}>
                <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-black flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Detalhamento: {apDrillDown.title}
                    </DialogTitle>
                  </DialogHeader>
                  {(() => {
                    const total = (apDrillDown.accounts || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                    return (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center bg-gray-50 border rounded-lg p-3">
                          <span className="text-sm text-gray-600">{(apDrillDown.accounts || []).length} conta(s)</span>
                          <span className={`font-bold text-lg text-${apDrillDown.color}-700`}>{formatCurrency(total)}</span>
                        </div>
                        {(apDrillDown.accounts || []).length === 0 ? (
                          <div className="text-center py-8 text-gray-500">Nenhuma conta nesta categoria</div>
                        ) : (
                          <div className="border rounded-lg overflow-x-auto">
                            <table className="w-full text-sm min-w-[700px]" data-testid="ap-drilldown-table">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="text-left p-2 text-black">Credor</th>
                                  <th className="text-left p-2 text-black">Tipo</th>
                                  <th className="text-left p-2 text-black">Vencimento</th>
                                  <th className="text-left p-2 text-black">Status</th>
                                  <th className="text-right p-2 text-black">Valor</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(apDrillDown.accounts || [])
                                  .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
                                  .map(acc => (
                                    <tr key={acc.id} className="border-t hover:bg-gray-50">
                                      <td className="p-2 text-black font-medium">{acc.creditor_name}</td>
                                      <td className="p-2 text-gray-700">{acc.expense_type || '-'}</td>
                                      <td className="p-2 text-gray-700">{new Date(acc.due_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</td>
                                      <td className="p-2">
                                        <Badge variant="outline" className="text-xs">
                                          {acc.status === 'paid' || acc.status === 'pago' ? 'Paga' :
                                           acc.status === 'approved' ? 'Aprovada' :
                                           acc.status === 'pending' ? 'Pendente' : acc.status}
                                        </Badge>
                                      </td>
                                      <td className="p-2 text-right font-bold text-black">{formatCurrency(acc.amount)}</td>
                                    </tr>
                                  ))}
                              </tbody>
                              <tfoot className="bg-gray-50 border-t-2">
                                <tr>
                                  <td colSpan={4} className="p-2 text-right font-bold text-black">Total:</td>
                                  <td className="p-2 text-right font-bold text-black">{formatCurrency(total)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </DialogContent>
              </Dialog>


              {/* Dialog de Pagar (selecionar conta bancária) */}
              <Dialog open={payDialog.open} onOpenChange={(open) => setPayDialog(prev => ({ ...prev, open }))}>
                <DialogContent className="max-w-md w-[95vw]">
                  <DialogHeader>
                    <DialogTitle className="text-black flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-green-600" /> Confirmar Pagamento
                    </DialogTitle>
                    <DialogDescription>Selecione a conta bancária de onde o valor será debitado.</DialogDescription>
                  </DialogHeader>
                  {payDialog.account && (
                    <div className="space-y-3">
                      <div className="bg-gray-50 border rounded-lg p-3">
                        <p className="text-sm text-gray-600">Credor</p>
                        <p className="font-semibold text-black">{payDialog.account.creditor_name}</p>
                        {payDialog.account.description && <p className="text-xs text-gray-600">{payDialog.account.description}</p>}
                        <p className="text-lg font-bold text-red-600 mt-1">{formatCurrency(payDialog.account.amount)}</p>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-black block mb-1">Conta Bancária de Origem *</label>
                        <Select value={payDialog.bank_account_id} onValueChange={(v) => setPayDialog(prev => ({ ...prev, bank_account_id: v }))}>
                          <SelectTrigger className="text-black" data-testid="pay-bank-account"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                          <SelectContent>
                            {bankAccounts.map((b) => (
                              <SelectItem key={b.id} value={b.id}>
                                {b.name}{b.bank_name ? ` — ${b.bank_name}` : ""} · Saldo: {formatCurrency(b.current_balance)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {payDialog.bank_account_id && (() => {
                          const b = bankAccounts.find(x => x.id === payDialog.bank_account_id);
                          const remaining = (b?.current_balance || 0) - (payDialog.account?.amount || 0);
                          return (
                            <p className={`text-xs mt-2 ${remaining < 0 ? "text-red-600" : "text-gray-600"}`}>
                              Saldo após pagamento: <strong>{formatCurrency(remaining)}</strong>
                              {remaining < 0 && " ⚠️ ficará negativo"}
                            </p>
                          );
                        })()}
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setPayDialog({ open: false, account: null, bank_account_id: "" })}>Cancelar</Button>
                        <Button className="bg-green-500 hover:bg-green-600" onClick={confirmPayAccount} data-testid="pay-confirm-btn">Confirmar Pagamento</Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Dialog de Pagamento em Lote */}
              <Dialog open={batchPayDialog.open} onOpenChange={(open) => setBatchPayDialog(prev => ({ ...prev, open }))}>
                <DialogContent className="max-w-md w-[95vw]">
                  <DialogHeader>
                    <DialogTitle className="text-black flex items-center gap-2"><DollarSign className="h-5 w-5 text-green-600" /> Pagamento em Lote</DialogTitle>
                    <DialogDescription>Confirme a conta bancária de onde sairá o total de todas as contas selecionadas.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="bg-gray-50 border rounded-lg p-3">
                      <p className="text-sm font-bold text-black">{batchPayDialog.ids.length} conta(s) selecionada(s)</p>
                      <p className="text-lg font-bold text-red-600 mt-1">Total: {formatCurrency(batchPayDialog.total)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-black block mb-1">Conta Bancária de Origem *</label>
                      <Select value={batchPayDialog.bank_account_id} onValueChange={(v) => setBatchPayDialog(prev => ({ ...prev, bank_account_id: v }))}>
                        <SelectTrigger className="text-black" data-testid="batch-pay-bank"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {bankAccounts.map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.name}{b.bank_name ? ` — ${b.bank_name}` : ""} · Saldo: {formatCurrency(b.current_balance)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {batchPayDialog.bank_account_id && (() => {
                        const b = bankAccounts.find(x => x.id === batchPayDialog.bank_account_id);
                        const remaining = (b?.current_balance || 0) - batchPayDialog.total;
                        return (
                          <p className={`text-xs mt-2 ${remaining < 0 ? "text-red-600" : "text-gray-700"}`}>
                            Saldo após pagamento: <strong>{formatCurrency(remaining)}</strong>
                            {remaining < 0 && " ⚠️ ficará negativo"}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => setBatchPayDialog({ open: false, ids: [], bank_account_id: "", total: 0 })}>Cancelar</Button>
                      <Button className="bg-green-500 hover:bg-green-600" onClick={confirmBatchPay} data-testid="batch-pay-confirm-btn">Confirmar Pagamento</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Dialog de Programar/Reprogramar Pagamento */}
              <Dialog open={programDialog.open} onOpenChange={(open) => setProgramDialog(prev => ({ ...prev, open }))}>
                <DialogContent className="max-w-md w-[95vw] max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-black flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-600" />
                      {programDialog.currentDate ? "Reprogramar Pagamento" : "Programar Pagamento"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    {programDialog.creditor && (
                      <div className="bg-gray-50 border rounded-lg p-3">
                        <p className="text-sm text-gray-600">Conta</p>
                        <p className="font-semibold text-black">{programDialog.creditor}</p>
                        <p className="text-sm font-bold text-blue-700">{formatCurrency(programDialog.amount || 0)}</p>
                      </div>
                    )}
                    {programDialog.currentDate && (
                      <div className="text-sm text-gray-600">
                        Data atual: <strong>{new Date(programDialog.currentDate).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</strong>
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-bold text-black block mb-1">Nova Data Programada *</label>
                      <Input
                        type="date"
                        value={programDialog.date}
                        onChange={(e) => setProgramDialog(prev => ({ ...prev, date: e.target.value }))}
                        className="text-black"
                        data-testid="program-date-input"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => setProgramDialog({ open: false, accountId: null, currentDate: "", date: "" })}>Cancelar</Button>
                      <Button className="bg-blue-500 hover:bg-blue-600" onClick={submitProgram} data-testid="program-save-btn">
                        <Calendar className="h-4 w-4 mr-1" /> {programDialog.currentDate ? "Reprogramar" : "Programar"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <TabsContent value="programados">
                <div className="space-y-4">
                  {(() => {
                    // Lista base: contas com status 'approved' E que JÁ FORAM PROGRAMADAS
                    const programados = accountsPayable.filter(a => a.status === 'approved' && a.scheduled_payment_date);
                    const supplierById = new Map((suppliers || []).map(s => [s.id, s]));
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const in7 = new Date(today); in7.setDate(today.getDate() + 7);
                    const in30 = new Date(today); in30.setDate(today.getDate() + 30);

                    // Métricas (sobre todos os approved, sem filtros)
                    const getDate = (a) => new Date(a.scheduled_payment_date || a.due_date);
                    const totalAprovados = programados.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                    const atrasados = programados.filter(a => getDate(a) < today);
                    const hoje = programados.filter(a => {
                      const d = getDate(a); d.setHours(0, 0, 0, 0);
                      return d.getTime() === today.getTime();
                    });
                    const proximos7 = programados.filter(a => {
                      const d = getDate(a); return d >= today && d <= in7;
                    });
                    const proximos30 = programados.filter(a => {
                      const d = getDate(a); return d >= today && d <= in30;
                    });

                    // Aplicar filtros
                    const di = programadosFiltros.data_inicio ? new Date(programadosFiltros.data_inicio + 'T00:00:00') : null;
                    const df = programadosFiltros.data_fim ? new Date(programadosFiltros.data_fim + 'T23:59:59') : null;
                    const fq = (programadosFiltros.fornecedor || "").toLowerCase().trim();
                    const matchFornecedor = (a) => {
                      if (!fq) return true;
                      if ((a.creditor_name || "").toLowerCase().includes(fq)) return true;
                      const s = a.supplier_id ? supplierById.get(a.supplier_id) : null;
                      if (s) {
                        if ((s.name || "").toLowerCase().includes(fq)) return true;
                        if ((s.cnpj || "").toLowerCase().includes(fq)) return true;
                        if ((s.cpf || "").toLowerCase().includes(fq)) return true;
                      }
                      return false;
                    };
                    const matchPeriodo = (a) => {
                      const d = getDate(a);
                      switch (programadosFiltros.periodo) {
                        case 'atrasados': return d < today;
                        case 'hoje': { const x = new Date(d); x.setHours(0,0,0,0); return x.getTime() === today.getTime(); }
                        case '7dias': return d >= today && d <= in7;
                        case '30dias': return d >= today && d <= in30;
                        default: return true;
                      }
                    };
                    const matchObra = (a) => {
                      const obra = programadosFiltros.obra || "todas";
                      if (obra === "todas") return true;
                      if (obra === "__none__") return !a.project_name;
                      return a.project_name === obra;
                    };
                    const filtered = programados
                      .filter(a => {
                        if (!matchFornecedor(a)) return false;
                        if (!matchPeriodo(a)) return false;
                        if (!matchObra(a)) return false;
                        const d = getDate(a);
                        if (di && d < di) return false;
                        if (df && d > df) return false;
                        return true;
                      })
                      .sort((a, b) => getDate(a) - getDate(b));
                    const totalFiltrado = filtered.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);

                    return (
                      <>
                        {/* Dashboard cards dos Programados */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                          <Card data-testid="prog-card-total" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setProgramadosFiltros({ ...programadosFiltros, periodo: 'todos' })}>
                            <CardContent className="p-4">
                              <p className="text-xs text-gray-600">Total Programado</p>
                              <p className="text-lg md:text-xl font-bold text-blue-700">{formatCurrency(totalAprovados)}</p>
                              <p className="text-xs text-gray-500 mt-1">{programados.length} conta(s)</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-red-50 cursor-pointer hover:shadow-md transition-shadow" data-testid="prog-card-atrasados" onClick={() => setProgramadosFiltros({ ...programadosFiltros, periodo: 'atrasados' })}>
                            <CardContent className="p-4">
                              <p className="text-xs text-gray-600">Atrasados</p>
                              <p className="text-lg md:text-xl font-bold text-red-700">{formatCurrency(atrasados.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0))}</p>
                              <p className="text-xs text-gray-500 mt-1">{atrasados.length} conta(s)</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-yellow-50 cursor-pointer hover:shadow-md transition-shadow" data-testid="prog-card-hoje" onClick={() => setProgramadosFiltros({ ...programadosFiltros, periodo: 'hoje' })}>
                            <CardContent className="p-4">
                              <p className="text-xs text-gray-600">Hoje</p>
                              <p className="text-lg md:text-xl font-bold text-yellow-700">{formatCurrency(hoje.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0))}</p>
                              <p className="text-xs text-gray-500 mt-1">{hoje.length} conta(s)</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-orange-50 cursor-pointer hover:shadow-md transition-shadow" data-testid="prog-card-7d" onClick={() => setProgramadosFiltros({ ...programadosFiltros, periodo: '7dias' })}>
                            <CardContent className="p-4">
                              <p className="text-xs text-gray-600">Próximos 7 dias</p>
                              <p className="text-lg md:text-xl font-bold text-orange-700">{formatCurrency(proximos7.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0))}</p>
                              <p className="text-xs text-gray-500 mt-1">{proximos7.length} conta(s)</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-purple-50 cursor-pointer hover:shadow-md transition-shadow" data-testid="prog-card-30d" onClick={() => setProgramadosFiltros({ ...programadosFiltros, periodo: '30dias' })}>
                            <CardContent className="p-4">
                              <p className="text-xs text-gray-600">Próximos 30 dias</p>
                              <p className="text-lg md:text-xl font-bold text-purple-700">{formatCurrency(proximos30.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0))}</p>
                              <p className="text-xs text-gray-500 mt-1">{proximos30.length} conta(s)</p>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Filtros */}
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base md:text-lg flex items-center justify-between">
                              <span className="flex items-center"><Calendar className="h-5 w-5 mr-2 text-blue-600" />Lista de Pagamentos Programados</span>
                              {programadosFiltros.periodo !== 'todos' && (
                                <Badge variant="outline" className="text-xs">
                                  Filtro: {programadosFiltros.periodo === 'atrasados' ? 'Atrasados' : programadosFiltros.periodo === 'hoje' ? 'Hoje' : programadosFiltros.periodo === '7dias' ? 'Próximos 7 dias' : 'Próximos 30 dias'}
                                  <Button variant="ghost" size="sm" className="h-auto p-0 ml-1" onClick={() => setProgramadosFiltros({ ...programadosFiltros, periodo: 'todos' })}>
                                    <X className="h-3 w-3" />
                                  </Button>
                                </Badge>
                              )}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="bg-gray-50 border rounded-lg p-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">De</label>
                                <Input type="date" value={programadosFiltros.data_inicio} onChange={(e) => setProgramadosFiltros({ ...programadosFiltros, data_inicio: e.target.value })} className="text-black h-9" data-testid="prog-filtro-data-inicio" />
                              </div>
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">Até</label>
                                <Input type="date" value={programadosFiltros.data_fim} onChange={(e) => setProgramadosFiltros({ ...programadosFiltros, data_fim: e.target.value })} className="text-black h-9" data-testid="prog-filtro-data-fim" />
                              </div>
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">Fornecedor</label>
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                  <Input type="text" placeholder="Nome, CNPJ ou CPF..." value={programadosFiltros.fornecedor} onChange={(e) => setProgramadosFiltros({ ...programadosFiltros, fornecedor: e.target.value })} className="text-black h-9 pl-8" data-testid="prog-filtro-fornecedor" />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">Obra</label>
                                <Select value={programadosFiltros.obra || "todas"} onValueChange={(v) => setProgramadosFiltros({ ...programadosFiltros, obra: v })}>
                                  <SelectTrigger className="text-black h-9" data-testid="prog-filtro-obra"><SelectValue placeholder="Todas as obras" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="todas">Todas as obras</SelectItem>
                                    <SelectItem value="__none__">— Sem obra vinculada —</SelectItem>
                                    {[...new Set(programados.map(a => a.project_name).filter(Boolean))].sort().map((p) => (
                                      <SelectItem key={p} value={p}>{p}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="flex justify-between items-center px-1 text-xs text-gray-600 mb-2">
                              <span>{filtered.length} pagamento(s) listado(s){programadosSelecionados.size > 0 ? ` · ${programadosSelecionados.size} selecionado(s)` : ""}</span>
                              <span className="font-bold text-black text-sm">Total: {formatCurrency(totalFiltrado)}</span>
                            </div>
                            {programadosSelecionados.size > 0 && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 flex items-center justify-between flex-wrap gap-2">
                                <div className="text-sm text-green-900">
                                  <strong>{programadosSelecionados.size}</strong> conta(s) selecionada(s) · Total: <strong>{formatCurrency(filtered.filter(a => programadosSelecionados.has(a.id)).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0))}</strong>
                                </div>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" onClick={() => setProgramadosSelecionados(new Set())}>
                                    <X className="h-3 w-3 mr-1" /> Limpar
                                  </Button>
                                  <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={payBatch} data-testid="prog-pay-batch-btn">
                                    <Check className="h-4 w-4 mr-1" /> Pagar Selecionados ({programadosSelecionados.size})
                                  </Button>
                                </div>
                              </div>
                            )}
                            {filtered.length === 0 ? (
                              <div className="text-center py-6 text-gray-500">
                                <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                <p>Nenhum pagamento programado encontrado</p>
                              </div>
                            ) : (
                              <div className="border rounded-lg overflow-x-auto">
                                <table className="w-full text-sm min-w-[900px]" data-testid="prog-table">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="p-2 w-8">
                                        <input
                                          type="checkbox"
                                          checked={filtered.filter(a => !a.payroll_batch_id).length > 0 && filtered.filter(a => !a.payroll_batch_id).every(a => programadosSelecionados.has(a.id))}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setProgramadosSelecionados(new Set(filtered.filter(a => !a.payroll_batch_id).map(a => a.id)));
                                            } else {
                                              setProgramadosSelecionados(new Set());
                                            }
                                          }}
                                          data-testid="prog-select-all"
                                        />
                                      </th>
                                      <th className="text-left p-2 text-black">Credor</th>
                                      <th className="text-left p-2 text-black">Descrição / Detalhes</th>
                                      <th className="text-left p-2 text-black">Obra</th>
                                      <th className="text-left p-2 text-black">Tipo</th>
                                      <th className="text-left p-2 text-black">Programado para</th>
                                      <th className="text-right p-2 text-black">Valor</th>
                                      <th className="text-center p-2 text-black">Ações</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filtered.slice(0, 200).map(acc => {
                                      const sup = acc.supplier_id ? supplierById.get(acc.supplier_id) : null;
                                      const d = getDate(acc);
                                      const isLate = d < today;
                                      const isSelected = programadosSelecionados.has(acc.id);
                                      const fromPayroll = !!acc.payroll_batch_id;
                                      return (
                                        <tr key={acc.id} className={`border-t hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : isLate ? 'bg-red-50' : ''}`}>
                                          <td className="p-2 text-center">
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              disabled={fromPayroll}
                                              title={fromPayroll ? "Despesa de Folha - pague em Folha de Pagamento" : ""}
                                              onChange={(e) => {
                                                if (fromPayroll) return;
                                                const next = new Set(programadosSelecionados);
                                                if (e.target.checked) next.add(acc.id);
                                                else next.delete(acc.id);
                                                setProgramadosSelecionados(next);
                                              }}
                                              data-testid={`prog-select-${acc.id}`}
                                            />
                                          </td>
                                          <td className="p-2 text-black font-medium">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span>{acc.creditor_name}</span>
                                              {fromPayroll && <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700">Folha</Badge>}
                                            </div>
                                            {sup && (sup.cnpj || sup.cpf) && (
                                              <div className="text-xs text-gray-500 font-normal">{sup.cnpj || sup.cpf}</div>
                                            )}
                                          </td>
                                          <td className="p-2 text-gray-700 max-w-xs">
                                            <div className="text-xs">{acc.description || '—'}</div>
                                            {acc.approved_by_name && (
                                              <div className="text-xs text-gray-500 mt-1">Aprovado por: {acc.approved_by_name}</div>
                                            )}
                                          </td>
                                          <td className="p-2 text-gray-700 text-xs">
                                            {acc.project_name ? (
                                              <div className="flex items-center gap-1">
                                                <Building2 className="h-3 w-3 text-purple-600" />
                                                <span className="truncate max-w-[150px]" title={acc.project_name}>{acc.project_name}</span>
                                              </div>
                                            ) : "—"}
                                          </td>
                                          <td className="p-2 text-gray-700 text-xs">
                                            {acc.expense_type ? <Badge variant="outline" className="text-xs">{acc.expense_type}</Badge> : "—"}
                                          </td>
                                          <td className="p-2 text-gray-700">
                                            <div className="flex items-center gap-1">
                                              {isLate && <AlertCircle className="h-3 w-3 text-red-600" />}
                                              {d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                            </div>
                                          </td>
                                          <td className="p-2 text-right font-bold text-black">{formatCurrency(acc.amount)}</td>
                                          <td className="p-2 text-center">
                                            <div className="flex gap-1 justify-center">
                                              <Button size="sm" variant="outline" onClick={async () => {
                                                setProgDetailsDlg({ open: true, account: acc });
                                                setProgDetailsConstruction(null);
                                                setProgDetailsLoading(true);
                                                try {
                                                  const r = await axios.get(`${API}/companies/${selectedCompany.id}/accounts-payable/${acc.id}/construction-info`);
                                                  setProgDetailsConstruction(r.data);
                                                } catch (err) {
                                                  setProgDetailsConstruction({ has_construction_link: false });
                                                } finally {
                                                  setProgDetailsLoading(false);
                                                }
                                              }} data-testid={`prog-details-${acc.id}`} title="Detalhes">
                                                <Eye className="h-3 w-3" />
                                              </Button>
                                              <Button size="sm" variant="outline" onClick={() => openProgramDialog(acc)} data-testid={`prog-reschedule-${acc.id}`} title="Reprogramar">
                                                <Calendar className="h-3 w-3" />
                                              </Button>
                                              {fromPayroll ? (
                                                <Button size="sm" variant="outline" disabled className="!text-amber-700 !border-amber-500 cursor-not-allowed" data-testid={`prog-pay-${acc.id}`} title="Pague em Folha de Pagamento">
                                                  Pela Folha
                                                </Button>
                                              ) : (
                                                <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => markAsPaid(acc.id)} data-testid={`prog-pay-${acc.id}`}>
                                                  <Check className="h-3 w-3 mr-1" /> Pagar
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                  <tfoot className="bg-gray-50 border-t-2">
                                    <tr>
                                      <td colSpan={6} className="p-2 text-right font-bold text-black">Total exibido:</td>
                                      <td className="p-2 text-right font-bold text-black">{formatCurrency(totalFiltrado)}</td>
                                      <td></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </>
                    );
                  })()}
                </div>
              </TabsContent>

              {/* ============ DIALOG: DETALHES DO PROGRAMADO ============ */}
              <Dialog open={progDetailsDlg.open} onOpenChange={(open) => setProgDetailsDlg({ ...progDetailsDlg, open })}>
                <DialogContent className="!max-w-[900px] w-[calc(100vw-24px)] max-h-[92vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-extrabold flex items-center gap-2 text-black">
                      <FileText className="h-5 w-5 text-blue-600" /> Detalhes do Pagamento Programado
                    </DialogTitle>
                    <DialogDescription className="text-black">
                      Todos os dados desta conta a pagar.
                    </DialogDescription>
                  </DialogHeader>
                  {progDetailsDlg.account && (() => {
                    const a = progDetailsDlg.account;
                    const sup = a.supplier_id ? (suppliers || []).find(s => s.id === a.supplier_id) : null;
                    const fmtBrDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—';
                    const fmtBrDateTime = (d) => d ? new Date(d).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—';
                    const scheduled = a.scheduled_payment_date ? new Date(a.scheduled_payment_date) : null;
                    const today = new Date(); today.setHours(0,0,0,0);
                    const isLate = scheduled && scheduled < today;
                    return (
                      <div className="space-y-4 text-black">
                        {/* Header status */}
                        <div className="flex flex-wrap gap-2 items-center">
                          <Badge className="bg-blue-600">Aprovado</Badge>
                          <Badge className={isLate ? "bg-red-600" : "bg-green-600"}>
                            {isLate ? "Atrasado" : "Programado"}
                          </Badge>
                          {a.expense_type && <Badge variant="outline">{a.expense_type}</Badge>}
                          {a.recurrence && a.recurrence !== 'nenhuma' && <Badge variant="outline">Recorrente: {a.recurrence}</Badge>}
                          {a.installment_number && a.total_installments && (
                            <Badge variant="outline">Parcela {a.installment_number}/{a.total_installments}</Badge>
                          )}
                        </div>

                        {/* Valor destaque */}
                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                          <p className="text-xs font-bold">Valor a Pagar</p>
                          <p className="text-3xl font-extrabold text-blue-800">{formatCurrency(a.amount)}</p>
                        </div>

                        {/* Credor */}
                        <div className="bg-gray-50 border rounded-lg p-3">
                          <p className="text-xs font-bold mb-2">CREDOR / FORNECEDOR</p>
                          <p className="text-base font-extrabold">{a.creditor_name || sup?.name || '—'}</p>
                          {sup && (
                            <div className="text-xs mt-1 grid grid-cols-1 md:grid-cols-2 gap-1">
                              {sup.cnpj && <p><b>CNPJ:</b> {sup.cnpj}</p>}
                              {sup.cpf && <p><b>CPF:</b> {sup.cpf}</p>}
                              {sup.phone && <p><b>Telefone:</b> {sup.phone}</p>}
                              {sup.email && <p><b>Email:</b> {sup.email}</p>}
                              {sup.address && <p className="col-span-2"><b>Endereço:</b> {sup.address}</p>}
                            </div>
                          )}
                        </div>

                        {/* Datas */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div className="bg-gray-50 border rounded p-2">
                            <p className="text-xs">Vencimento</p>
                            <p className="font-bold">{fmtBrDate(a.due_date)}</p>
                          </div>
                          <div className={`border rounded p-2 ${isLate ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
                            <p className="text-xs">Programado p/ pagto</p>
                            <p className={`font-bold ${isLate ? 'text-red-700' : ''}`}>{fmtBrDate(a.scheduled_payment_date)}</p>
                          </div>
                          <div className="bg-gray-50 border rounded p-2">
                            <p className="text-xs">Emissão</p>
                            <p className="font-bold">{fmtBrDate(a.issue_date)}</p>
                          </div>
                          <div className="bg-gray-50 border rounded p-2">
                            <p className="text-xs">Nº Documento</p>
                            <p className="font-bold">{a.document_number || '—'}</p>
                          </div>
                        </div>

                        {/* Descrição */}
                        {a.description && (
                          <div className="bg-gray-50 border rounded-lg p-3">
                            <p className="text-xs font-bold mb-1">DESCRIÇÃO</p>
                            <p className="whitespace-pre-wrap text-sm">{a.description}</p>
                          </div>
                        )}

                        {/* Vínculo com Obra */}
                        {(a.project_id || a.project_name) && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <p className="text-xs font-bold mb-1">OBRA VINCULADA</p>
                            <p className="font-bold">{a.project_name || '—'}</p>
                            {a.phase_name && <p className="text-xs mt-1"><b>Medição:</b> {a.phase_name}</p>}
                          </div>
                        )}

                        {/* Construção - Material/Serviço vinculado (Pedido de Material e Usos) */}
                        {progDetailsLoading && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-center">
                            Carregando informações da construção…
                          </div>
                        )}
                        {!progDetailsLoading && progDetailsConstruction?.has_construction_link && (
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-3">
                            <p className="text-xs font-bold text-indigo-900">
                              🏗️ MATERIAL / SERVIÇO (Construção)
                            </p>
                            {progDetailsConstruction.material_order && (() => {
                              const mo = progDetailsConstruction.material_order;
                              return (
                                <div className="bg-white border border-indigo-200 rounded p-3 space-y-2">
                                  <div className="flex flex-wrap gap-2 items-center">
                                    <Badge className="bg-indigo-600">
                                      Pedido #{mo.order_number || mo.id?.slice(0, 8)}
                                    </Badge>
                                    <Badge variant="outline">{mo.kind === 'servico' ? 'Serviço' : 'Material'}</Badge>
                                    <Badge variant="outline">Destino: {mo.destination === 'estoque' ? 'Estoque' : 'Obra'}</Badge>
                                  </div>
                                  <div className="text-sm grid grid-cols-1 md:grid-cols-2 gap-1">
                                    <p><b>Obra:</b> {mo.project_name || '—'}</p>
                                    <p><b>Medição:</b> {mo.phase_name || '—'}</p>
                                    <p className="col-span-2"><b>Fornecedor:</b> {mo.supplier_name || '—'}</p>
                                  </div>
                                  {mo.observations && (
                                    <div className="bg-yellow-50 border border-yellow-300 rounded p-2">
                                      <p className="text-xs font-bold text-yellow-900 mb-1">📝 OBSERVAÇÃO DO PEDIDO</p>
                                      <p className="text-sm whitespace-pre-wrap">{mo.observations}</p>
                                    </div>
                                  )}
                                  {mo.items && mo.items.length > 0 && (
                                    <div>
                                      <p className="text-xs font-bold mb-1">Itens do Pedido:</p>
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs border">
                                          <thead className="bg-gray-100">
                                            <tr>
                                              <th className="p-1 text-left">Descrição</th>
                                              <th className="p-1 text-center">Un.</th>
                                              <th className="p-1 text-right">Qtde</th>
                                              <th className="p-1 text-right">Preço Un.</th>
                                              <th className="p-1 text-right">Total</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {mo.items.map((it, i) => (
                                              <tr key={i} className="border-t">
                                                <td className="p-1">{it.description}</td>
                                                <td className="p-1 text-center">{it.unit}</td>
                                                <td className="p-1 text-right">{it.quantity}</td>
                                                <td className="p-1 text-right">{formatCurrency(it.unit_price)}</td>
                                                <td className="p-1 text-right font-bold">{formatCurrency(it.total_price)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                  {mo.documents && mo.documents.length > 0 && (
                                    <div>
                                      <p className="text-xs font-bold mb-1">📎 Anexos do Pedido ({mo.documents.length}):</p>
                                      <ul className="space-y-1">
                                        {mo.documents.map((doc) => (
                                          <li key={doc.id} className="flex items-center justify-between bg-gray-50 border rounded px-2 py-1">
                                            <div className="flex items-center gap-2 text-sm min-w-0 flex-1">
                                              <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                                              <span className="truncate" title={doc.filename}>{doc.filename}</span>
                                              {doc.document_type && (
                                                <Badge variant="outline" className="text-xs">{doc.document_type}</Badge>
                                              )}
                                            </div>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="ml-2 shrink-0"
                                              onClick={async () => {
                                                try {
                                                  const r = await axios.get(
                                                    `${API}/companies/${selectedCompany.id}/material-orders/${mo.id}/documents/${doc.id}/download`,
                                                    { responseType: 'blob' }
                                                  );
                                                  const url = window.URL.createObjectURL(new Blob([r.data]));
                                                  const link = document.createElement('a');
                                                  link.href = url;
                                                  link.setAttribute('download', doc.filename);
                                                  document.body.appendChild(link);
                                                  link.click();
                                                  link.remove();
                                                  setTimeout(() => window.URL.revokeObjectURL(url), 10000);
                                                } catch (err) {
                                                  toast.error('Erro ao baixar anexo');
                                                }
                                              }}
                                              data-testid={`prog-download-${doc.id}`}
                                            >
                                              <Upload className="h-3 w-3 mr-1 rotate-180" /> Baixar
                                            </Button>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        {!progDetailsLoading && progDetailsConstruction && !progDetailsConstruction.has_construction_link && (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                            Esta conta não possui pedido de material/serviço vinculado ao módulo Construção.
                          </div>
                        )}

                        {/* Anexos */}
                        {a.documents && a.documents.length > 0 && (
                          <div className="bg-gray-50 border rounded-lg p-3">
                            <p className="text-xs font-bold mb-2">DOCUMENTOS ({a.documents.length})</p>
                            <ul className="text-sm space-y-1">
                              {a.documents.map((doc, i) => (
                                <li key={i} className="flex items-center gap-2">
                                  <FileText className="h-3 w-3" /> {doc.filename || doc.name || `Documento ${i+1}`}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Aprovação */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-xs font-bold mb-2">HISTÓRICO DE APROVAÇÃO</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            <p><b>Solicitado por:</b> {a.created_by_name || '—'}</p>
                            <p><b>Criado em:</b> {fmtBrDateTime(a.created_at)}</p>
                            <p><b>Aprovado por:</b> {a.approved_by_name || '—'}</p>
                            <p><b>Aprovado em:</b> {fmtBrDateTime(a.approved_at)}</p>
                            {a.approval_notes && <p className="col-span-2"><b>Observações da aprovação:</b> {a.approval_notes}</p>}
                          </div>
                        </div>

                        {/* Info de programação */}
                        {a.scheduled_payment_notes && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-xs font-bold mb-1">OBSERVAÇÕES DA PROGRAMAÇÃO</p>
                            <p className="text-sm whitespace-pre-wrap">{a.scheduled_payment_notes}</p>
                          </div>
                        )}

                        {/* Ações */}
                        <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-2 border-t">
                          <Button variant="outline" onClick={() => setProgDetailsDlg({ open: false, account: null })}>Fechar</Button>
                          <Button variant="outline" onClick={() => { setProgDetailsDlg({ open: false, account: null }); openProgramDialog(a); }}>
                            <Calendar className="h-4 w-4 mr-1" /> Reprogramar
                          </Button>
                          <Button className="bg-green-500 hover:bg-green-600" onClick={() => { setProgDetailsDlg({ open: false, account: null }); markAsPaid(a.id); }}>
                            <Check className="h-4 w-4 mr-1" /> Marcar como Pago
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                </DialogContent>
              </Dialog>

              <TabsContent value="agenda">
            <div className="space-y-6">
              {/* Despesas Aprovadas Aguardando Programação */}
              <Card className="border-orange-300">
                <CardHeader>
                  <CardTitle className="flex items-center text-orange-700">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Despesas Aprovadas — Aguardando Programação
                  </CardTitle>
                  <p className="text-xs text-gray-600 mt-1">Despesas aprovadas que ainda não foram programadas para pagamento. Programe a data para enviar para a aba Programados.</p>
                </CardHeader>
                <CardContent>
                  {/* Filtros */}
                  <div className="bg-gray-50 border rounded-lg p-3 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-bold text-black block mb-1">Vencimento De</label>
                      <Input
                        type="date"
                        value={agendaPagamentosFiltros.data_inicio}
                        onChange={(e) => setAgendaPagamentosFiltros({ ...agendaPagamentosFiltros, data_inicio: e.target.value })}
                        className="text-black h-9"
                        data-testid="agenda-pag-filtro-data-inicio"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-black block mb-1">Até</label>
                      <Input
                        type="date"
                        value={agendaPagamentosFiltros.data_fim}
                        onChange={(e) => setAgendaPagamentosFiltros({ ...agendaPagamentosFiltros, data_fim: e.target.value })}
                        className="text-black h-9"
                        data-testid="agenda-pag-filtro-data-fim"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-black block mb-1">Fornecedor</label>
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Nome, CNPJ ou CPF..."
                          value={agendaPagamentosFiltros.fornecedor}
                          onChange={(e) => setAgendaPagamentosFiltros({ ...agendaPagamentosFiltros, fornecedor: e.target.value })}
                          className="text-black h-9 pl-8"
                          data-testid="agenda-pag-filtro-fornecedor"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {(() => {
                      const di = agendaPagamentosFiltros.data_inicio ? new Date(agendaPagamentosFiltros.data_inicio + 'T00:00:00') : null;
                      const df = agendaPagamentosFiltros.data_fim ? new Date(agendaPagamentosFiltros.data_fim + 'T23:59:59') : null;
                      const fq = (agendaPagamentosFiltros.fornecedor || "").toLowerCase().trim();
                      const supplierById = new Map((suppliers || []).map(s => [s.id, s]));
                      const matchesFornecedor = (acc) => {
                        if (!fq) return true;
                        if ((acc.creditor_name || "").toLowerCase().includes(fq)) return true;
                        const sup = acc.supplier_id ? supplierById.get(acc.supplier_id) : null;
                        if (sup) {
                          if ((sup.name || "").toLowerCase().includes(fq)) return true;
                          if ((sup.cnpj || "").toLowerCase().includes(fq)) return true;
                          if ((sup.cpf || "").toLowerCase().includes(fq)) return true;
                        }
                        return false;
                      };
                      const filtered = accountsPayable
                        .filter(acc => acc.status === 'approved' && !acc.scheduled_payment_date)
                        .filter(acc => {
                          if (!matchesFornecedor(acc)) return false;
                          const d = new Date(acc.due_date);
                          if (di && d < di) return false;
                          if (df && d > df) return false;
                          return true;
                        })
                        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
                      if (filtered.length === 0) {
                        return (
                          <div className="text-center py-4 text-gray-500">
                            <Check className="h-8 w-8 mx-auto mb-2 text-green-300" />
                            <p>Nenhuma despesa aguardando programação</p>
                            <p className="text-xs mt-1">Despesas aprovadas com data já programada aparecem na aba <strong>Programados</strong></p>
                          </div>
                        );
                      }
                      const totalFiltrado = filtered.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                      return (
                        <>
                          <div className="flex justify-between items-center px-1 text-xs text-gray-600">
                            <span>{filtered.length} despesa(s) aprovada(s) sem programação</span>
                            <span className="font-bold text-black">Total: {formatCurrency(totalFiltrado)}</span>
                          </div>
                          {filtered.slice(0, 100).map(acc => {
                            const sup = acc.supplier_id ? supplierById.get(acc.supplier_id) : null;
                            return (
                              <div key={acc.id} className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 p-3 bg-orange-50 border border-orange-100 rounded-lg">
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-black">{acc.creditor_name}</p>
                                  {sup && (sup.cnpj || sup.cpf) && (
                                    <p className="text-xs text-gray-500">{sup.cnpj || sup.cpf}</p>
                                  )}
                                  {acc.description && (
                                    <p className="text-sm text-gray-700 mt-1">{acc.description}</p>
                                  )}
                                  <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-600">
                                    {acc.expense_type && (
                                      <Badge variant="outline" className="text-xs">
                                        {acc.expense_type}
                                      </Badge>
                                    )}
                                    <span>Vencimento: {new Date(acc.due_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>
                                    {acc.approved_by_name && <span>· Aprovado por: {acc.approved_by_name}</span>}
                                  </div>
                                </div>
                                <div className="text-right flex flex-col items-end gap-2">
                                  <p className="font-bold text-blue-600 text-lg">{formatCurrency(acc.amount)}</p>
                                  <Button
                                    size="sm"
                                    className="bg-orange-500 hover:bg-orange-600 text-white"
                                    onClick={() => openProgramDialog(acc)}
                                    data-testid={`agenda-program-${acc.id}`}
                                  >
                                    <Calendar className="h-4 w-4 mr-1" /> Programar
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* Próximos Vencimentos */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    Próximos Vencimentos (7 dias)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Filtro de busca */}
                  <div className="bg-gray-50 border rounded-lg p-3 mb-4">
                    <label className="text-xs font-bold text-black block mb-1">Buscar Fornecedor</label>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Pesquisar por nome ou CNPJ/CPF do fornecedor..."
                        value={vencimentosFiltro}
                        onChange={(e) => setVencimentosFiltro(e.target.value)}
                        className="text-black h-9 pl-8"
                        data-testid="vencimentos-filtro-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {(() => {
                      const q = (vencimentosFiltro || "").toLowerCase().trim();
                      const supplierById = new Map((suppliers || []).map(s => [s.id, s]));
                      const matchesQ = (acc) => {
                        if (!q) return true;
                        if ((acc.creditor_name || "").toLowerCase().includes(q)) return true;
                        const sup = acc.supplier_id ? supplierById.get(acc.supplier_id) : null;
                        if (sup) {
                          if ((sup.name || "").toLowerCase().includes(q)) return true;
                          if ((sup.cnpj || "").toLowerCase().includes(q)) return true;
                          if ((sup.cpf || "").toLowerCase().includes(q)) return true;
                        }
                        return false;
                      };
                      const today = new Date();
                      const sevenDaysFromNow = new Date();
                      sevenDaysFromNow.setDate(today.getDate() + 7);
                      const filtered = accountsPayable
                        .filter(acc => {
                          const isPaid = acc.status === 'paid' || acc.status === 'pago';
                          if (isPaid) return false;
                          const dueDate = new Date(acc.due_date);
                          return dueDate <= sevenDaysFromNow;
                        })
                        .filter(matchesQ)
                        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
                      if (filtered.length === 0) {
                        return (
                          <div className="text-center py-4 text-gray-500">
                            <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                            <p>{q ? "Nenhum vencimento encontrado para essa busca" : "Nenhuma conta vence nos próximos 7 dias"}</p>
                          </div>
                        );
                      }
                      const totalFiltrado = filtered.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                      return (
                        <>
                          <div className="flex justify-between items-center px-1 text-xs text-gray-600">
                            <span>{filtered.length} vencimento(s)</span>
                            <span className="font-bold text-black">Total: {formatCurrency(totalFiltrado)}</span>
                          </div>
                          {filtered.slice(0, 100).map(account => {
                            const isOverdue = new Date(account.due_date) < new Date();
                            const isApproved = account.status === 'approved';
                            const sup = account.supplier_id ? supplierById.get(account.supplier_id) : null;
                            return (
                              <div key={account.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center">
                                  <div className={`w-3 h-3 rounded-full mr-3 ${
                                    isOverdue ? 'bg-red-500' : isApproved ? 'bg-blue-500' : 'bg-yellow-500'
                                  }`}></div>
                                  <div>
                                    <p className="font-medium text-black">{account.creditor_name}</p>
                                    {sup && (sup.cnpj || sup.cpf) && (
                                      <p className="text-xs text-gray-500">{sup.cnpj || sup.cpf}</p>
                                    )}
                                    <p className="text-sm text-gray-600">
                                      {new Date(account.due_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                      {isApproved && ' (Aprovado)'}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-black">{formatCurrency(account.amount)}</p>
                                  <p className="text-xs text-gray-500">{account.expense_type}</p>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* Resumo por Tipo de Despesa */}
            </div>
              </TabsContent>

              <TabsContent value="resumo">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Resumo por Tipo de Despesa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(() => {
                      const expenseTypes = {};
                      accountsPayable.forEach(acc => {
                        if (acc.status !== 'pago') {
                          if (!expenseTypes[acc.expense_type]) {
                            expenseTypes[acc.expense_type] = { count: 0, total: 0 };
                          }
                          expenseTypes[acc.expense_type].count++;
                          expenseTypes[acc.expense_type].total += acc.amount || 0;
                        }
                      });
                      
                      return Object.entries(expenseTypes).map(([type, data]) => (
                        <div key={type} className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium capitalize mb-2">{type.replace('_', ' ')}</h4>
                          <p className="text-xl md:text-2xl font-bold text-blue-600">{formatCurrency(data.total)}</p>
                          <p className="text-sm text-gray-600">{data.count} conta{data.count !== 1 ? 's' : ''}</p>
                        </div>
                      ));
                    })()}
                    {accountsPayable.filter(acc => acc.status !== 'pago').length === 0 && (
                      <div className="col-span-full text-center py-8 text-gray-500">
                        <BarChart3 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>Nenhuma conta pendente para exibir</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

            </div>
              </TabsContent>

              <TabsContent value="lista">
            {/* Lista de Contas */}
            <Card className="mt-0">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Todas as Contas a Pagar</CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={openReportDialog}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Relatório PDF
                    </Button>
                    <Button onClick={() => {
                      setEditingAccount(null);
                      setAccountForm({ creditor_name: "", supplier_id: "", amount: "", due_date: "", expense_type: "", description: "" });
                      setShowAccountPayableForm(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Nova Conta
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filtro de Contas a Pagar */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Buscar por credor..."
                        value={filters.accountsPayable.search}
                        onChange={(e) => updateFilter('accountsPayable', 'search', e.target.value)}
                        className="pl-10 text-black"
                      />
                    </div>
                    <Select 
                      value={filters.accountsPayable.status} 
                      onValueChange={(value) => updateFilter('accountsPayable', 'status', value)}
                    >
                      <SelectTrigger className="text-black">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os status</SelectItem>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="approved">Aprovado</SelectItem>
                        <SelectItem value="overdue">Vencido</SelectItem>
                        <SelectItem value="paid">Pago</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select 
                      value={filters.accountsPayable.type} 
                      onValueChange={(value) => updateFilter('accountsPayable', 'type', value)}
                    >
                      <SelectTrigger className="text-black">
                        <SelectValue placeholder="Tipo de despesa" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os tipos</SelectItem>
                        <SelectItem value="aluguel">Aluguel</SelectItem>
                        <SelectItem value="fornecedores">Fornecedores</SelectItem>
                        <SelectItem value="impostos">Impostos</SelectItem>
                        <SelectItem value="salarios">Salários</SelectItem>
                        <SelectItem value="energia">Energia</SelectItem>
                        <SelectItem value="agua">Água</SelectItem>
                        <SelectItem value="internet">Internet/Telefone</SelectItem>
                        <SelectItem value="material">Material</SelectItem>
                        <SelectItem value="outros">Outros</SelectItem>
                      </SelectContent>
                    </Select>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Vencimento De</label>
                      <Input
                        type="date"
                        value={filters.accountsPayable.dateFrom}
                        onChange={(e) => updateFilter('accountsPayable', 'dateFrom', e.target.value)}
                        className="text-black"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Vencimento Até</label>
                      <Input
                        type="date"
                        value={filters.accountsPayable.dateTo}
                        onChange={(e) => updateFilter('accountsPayable', 'dateTo', e.target.value)}
                        className="text-black"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Obra</label>
                      <Select
                        value={filters.accountsPayable.obra || "todas"}
                        onValueChange={(v) => updateFilter('accountsPayable', 'obra', v)}
                      >
                        <SelectTrigger className="text-black" data-testid="ap-filtro-obra"><SelectValue placeholder="Todas as obras" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todas">Todas as obras</SelectItem>
                          <SelectItem value="__none__">— Sem obra vinculada —</SelectItem>
                          {[...new Set(accountsPayable.map(a => a.project_name).filter(Boolean))].sort().map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {filteredAccountsPayable.length} de {accountsPayable.length} contas
                  </p>
                </div>
                <div className="space-y-4">
                  {filteredAccountsPayable.map((account) => {
                    const isPaid = account.status === 'paid' || account.status === 'pago';
                    const isApproved = account.status === 'approved';
                    const isOverdue = !isPaid && !isApproved && new Date(account.due_date) < new Date();
                    
                    return (
                    <div key={account.id} className={`p-4 border rounded-lg ${
                      isPaid ? 'bg-green-50 border-green-200' :
                      isApproved ? 'bg-blue-50 border-blue-200' :
                      isOverdue ? 'bg-red-50 border-red-200' :
                      'bg-white'
                    }`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-black">{account.creditor_name}</h3>
                            <Badge className={
                              isPaid ? 'bg-green-600' : 
                              isApproved ? 'bg-blue-600' :
                              isOverdue ? 'bg-red-600' : 'bg-yellow-600'
                            }>
                              {isPaid ? 'Pago' : 
                               isApproved ? 'Aprovado' :
                               isOverdue ? 'Vencido' : 'Pendente'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-700 mb-1">
                            <strong>Valor:</strong> {formatCurrency(account.amount)}
                          </p>
                          <p className="text-sm text-gray-700 mb-1">
                            <strong>Vencimento:</strong> {new Date(account.due_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                          </p>
                          <p className="text-sm text-gray-700 mb-1">
                            <strong>Tipo:</strong> {account.expense_type}
                          </p>
                          {account.description && (
                            <p className="text-sm text-gray-700">
                              <strong>Descrição:</strong> {account.description}
                            </p>
                          )}
                          {account.approved && (
                            <p className="text-sm text-blue-600 mt-1">
                              <strong>Aprovado por:</strong> {account.approved_by_name} em {new Date(account.approval_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                            </p>
                          )}
                          {account.scheduled_payment_date && account.status !== 'paid' && (
                            <p className="text-sm text-blue-600">
                              <strong>Pagamento Agendado:</strong> {new Date(account.scheduled_payment_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                            </p>
                          )}
                          {account.payment_date && (
                            <p className="text-sm text-green-600 mt-1">
                              <strong>Pago em:</strong> {new Date(account.payment_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          {/* Botão Aprovar - apenas para pendentes e se for admin */}
                          {account.status === 'pending' && isUserAdmin() && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                              onClick={() => openApprovalDialog(account)}
                            >
                              <Shield className="h-4 w-4 mr-2" />
                              Aprovar
                            </Button>
                          )}
                          {/* Botões para contas aprovadas */}
                          {isApproved && (
                            <>
                              <Button
                                size="sm"
                                className={account.scheduled_payment_date
                                  ? "bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100"
                                  : "bg-orange-500 hover:bg-orange-600 text-white animate-pulse"}
                                variant={account.scheduled_payment_date ? "outline" : "default"}
                                onClick={() => openProgramDialog(account)}
                                data-testid={`schedule-btn-${account.id}`}
                              >
                                <Calendar className="h-4 w-4 mr-2" />
                                {account.scheduled_payment_date ? 'Reprogramar' : 'Programar'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                                onClick={() => markAsPaid(account.id)}
                              >
                                <Check className="h-4 w-4 mr-2" />
                                Marcar como Pago
                              </Button>
                            </>
                          )}
                          {!isPaid && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEditAccount(account)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Editar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => deleteAccount(account.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                  
                  {accountsPayable.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <CreditCard className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>Nenhuma conta a pagar cadastrada</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
              </TabsContent>
            </Tabs>

            {/* Add/Edit Account Payable Dialog */}
            <Dialog open={showAccountPayableForm} onOpenChange={(open) => {
              setShowAccountPayableForm(open);
              if (!open) {
                setApSupplierSearch("");
                setApSupplierDropdownOpen(false);
              }
            }}>
              <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-black">
                    {editingAccount ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAccountPayableSubmit} className="space-y-4">
                  {/* Seleção de Fornecedor — busca por nome / CNPJ / CPF */}
                  <div className="relative">
                    <label className="text-sm font-medium text-black mb-1 block">Fornecedor</label>
                    {(() => {
                      const selected = suppliers.find(s => s.id === accountForm.supplier_id);
                      const value = apSupplierDropdownOpen
                        ? apSupplierSearch
                        : (selected ? `${selected.name}${selected.cnpj || selected.cpf ? ` - ${selected.cnpj || selected.cpf}` : ""}` : apSupplierSearch);
                      const q = apSupplierSearch.toLowerCase().trim();
                      const filtered = suppliers.filter(s => {
                        if (!q) return true;
                        return (s.name || "").toLowerCase().includes(q)
                          || (s.cnpj || "").toLowerCase().includes(q)
                          || (s.cpf || "").toLowerCase().includes(q);
                      }).slice(0, 30);
                      return (
                        <>
                          <Input
                            type="text"
                            value={value}
                            placeholder="Buscar por nome, CNPJ ou CPF (opcional)..."
                            onChange={(e) => {
                              setApSupplierSearch(e.target.value);
                              setApSupplierDropdownOpen(true);
                              if (accountForm.supplier_id) {
                                setAccountForm({ ...accountForm, supplier_id: "", creditor_name: "" });
                              }
                            }}
                            onFocus={() => setApSupplierDropdownOpen(true)}
                            onBlur={() => setTimeout(() => setApSupplierDropdownOpen(false), 200)}
                            className="text-black"
                            data-testid="ap-supplier-search-input"
                          />
                          {apSupplierDropdownOpen && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border rounded-md shadow-lg">
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setAccountForm({ ...accountForm, supplier_id: "", creditor_name: "" });
                                  setApSupplierSearch("");
                                  setApSupplierDropdownOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-gray-500 italic border-b"
                              >
                                — Nenhum (digitar manualmente) —
                              </button>
                              {filtered.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500">Nenhum fornecedor encontrado</div>
                              ) : (
                                filtered.map(s => (
                                  <button
                                    type="button"
                                    key={s.id}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      setAccountForm({ ...accountForm, supplier_id: s.id, creditor_name: s.name });
                                      setApSupplierSearch("");
                                      setApSupplierDropdownOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-black border-b last:border-b-0"
                                  >
                                    <div className="font-medium">{s.name}</div>
                                    {(s.cnpj || s.cpf) && <div className="text-xs text-gray-500">{s.cnpj || s.cpf}</div>}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  
                  {/* Nome do Credor (manual ou preenchido automaticamente) */}
                  <div>
                    <label className="text-sm font-medium text-black mb-1 block">
                      Nome do Credor {accountForm.supplier_id ? '(do fornecedor)' : ''}
                    </label>
                    <Input
                      placeholder="Nome do credor"
                      value={accountForm.creditor_name}
                      onChange={(e) => setAccountForm({...accountForm, creditor_name: e.target.value})}
                      required
                      className="text-black"
                      disabled={!!accountForm.supplier_id}
                    />
                  </div>
                  
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Valor"
                    value={accountForm.amount}
                    onChange={(e) => setAccountForm({...accountForm, amount: e.target.value})}
                    required
                    className="text-black"
                  />
                  <div>
                    <label className="text-sm font-medium text-black mb-1 block">Data de Vencimento</label>
                    <Input
                      type="date"
                      value={accountForm.due_date}
                      onChange={(e) => setAccountForm({...accountForm, due_date: e.target.value})}
                      required
                      className="text-black"
                    />
                  </div>
                  <Select 
                    value={accountForm.expense_type} 
                    onValueChange={(value) => setAccountForm({...accountForm, expense_type: value})}
                    required
                  >
                    <SelectTrigger className="text-black">
                      <SelectValue placeholder="Selecione o tipo de despesa" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aluguel">Aluguel</SelectItem>
                      <SelectItem value="fornecedores">Fornecedores</SelectItem>
                      <SelectItem value="impostos">Impostos</SelectItem>
                      <SelectItem value="salarios">Salários</SelectItem>
                      <SelectItem value="energia">Energia Elétrica</SelectItem>
                      <SelectItem value="agua">Água</SelectItem>
                      <SelectItem value="internet">Internet/Telefone</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="manutencao">Manutenção</SelectItem>
                      <SelectItem value="material">Material</SelectItem>
                      <SelectItem value="outros">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Descrição (opcional)"
                    value={accountForm.description}
                    onChange={(e) => setAccountForm({...accountForm, description: e.target.value})}
                    className="text-black"
                  />
                  <Button type="submit" className="w-full">
                    {editingAccount ? 'Atualizar Conta' : 'Criar Conta'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Dialog de Aprovação de Pagamento */}
            <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
              <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-black">Aprovar Pagamento</DialogTitle>
                  <DialogDescription>
                    Aprove a despesa. A data de pagamento pode ser definida agora ou depois através do botão &quot;Programar&quot;.
                  </DialogDescription>
                </DialogHeader>
                {approvalAccount && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="font-semibold text-black">{approvalAccount.creditor_name}</p>
                      <p className="text-sm text-gray-600">{approvalAccount.description || 'Sem descrição'}</p>
                      <p className="text-lg font-bold text-blue-600 mt-2">{formatCurrency(approvalAccount.amount)}</p>
                      <p className="text-sm text-gray-600">
                        Vencimento: {new Date(approvalAccount.due_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                      </p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-black mb-1 block">
                        Data de Pagamento Agendada <span className="text-xs text-gray-500">(opcional)</span>
                      </label>
                      <Input
                        type="date"
                        value={scheduledPaymentDate}
                        onChange={(e) => setScheduledPaymentDate(e.target.value)}
                        className="text-black"
                        data-testid="approve-scheduled-date"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Deixe em branco e use o botão &quot;Programar&quot; depois. Sem data, a conta fica como Aprovada (aguardando programação).
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => setShowApprovalDialog(false)}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        className="flex-1 bg-blue-500 hover:bg-blue-600"
                        onClick={approvePayment}
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Aprovar Pagamento
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Dialog de Seleção de Relatório */}
            <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
              <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-black">Gerar Relatório de Contas a Pagar</DialogTitle>
                  <DialogDescription>
                    Selecione o tipo de relatório e o período desejado
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-black mb-1 block">Tipo de Relatório</label>
                    <Select 
                      value={reportFilters.status} 
                      onValueChange={(value) => setReportFilters({...reportFilters, status: value})}
                    >
                      <SelectTrigger className="text-black">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as Contas</SelectItem>
                        <SelectItem value="pending">Somente Pendentes</SelectItem>
                        <SelectItem value="approved">Somente Aprovados</SelectItem>
                        <SelectItem value="paid">Somente Pagos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-black mb-1 block">Data Inicial</label>
                      <Input
                        type="date"
                        value={reportFilters.dateFrom}
                        onChange={(e) => setReportFilters({...reportFilters, dateFrom: e.target.value})}
                        className="text-black"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-black mb-1 block">Data Final</label>
                      <Input
                        type="date"
                        value={reportFilters.dateTo}
                        onChange={(e) => setReportFilters({...reportFilters, dateTo: e.target.value})}
                        className="text-black"
                      />
                    </div>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    <p><strong>Resumo:</strong></p>
                    <p>• Tipo: {reportFilters.status === 'all' ? 'Todas as contas' : 
                               reportFilters.status === 'pending' ? 'Somente pendentes' :
                               reportFilters.status === 'approved' ? 'Somente aprovados' : 'Somente pagos'}</p>
                    <p>• Período: {reportFilters.dateFrom || 'Início'} até {reportFilters.dateTo || 'Hoje'}</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setShowReportDialog(false)}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                      onClick={generateAccountsReport}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Gerar Relatório
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Processo Judiciário */}
          <TabsContent value="legal-process">
            <div className="space-y-6">
              {/* Header com tabs internas */}
              <Tabs value={legalActiveTab} onValueChange={(v) => {
                setLegalActiveTab(v);
                if (v === "dashboard") fetchLegalDashboard(legalDashboardPeriod);
              }} className="w-full">
                <TabsList className="inline-flex w-full h-auto overflow-x-auto whitespace-nowrap">
                  <TabsTrigger value="dashboard" data-testid="legal-tab-dashboard">Dashboard</TabsTrigger>
                  <TabsTrigger value="clients" data-testid="legal-tab-clients">Clientes</TabsTrigger>
                  <TabsTrigger value="processes" data-testid="legal-tab-processes">Processos</TabsTrigger>
                  <TabsTrigger value="honorarios" data-testid="legal-tab-honorarios">Honorários</TabsTrigger>
                  <TabsTrigger value="agenda" data-testid="legal-tab-agenda">Agenda</TabsTrigger>
                </TabsList>

                {/* ========== DASHBOARD JURÍDICO ========== */}
                <TabsContent value="dashboard">
                  <LegalDashboard
                    data={legalDashboard}
                    period={legalDashboardPeriod}
                    onPeriodChange={(p) => { setLegalDashboardPeriod(p); fetchLegalDashboard(p); }}
                    onRefresh={() => fetchLegalDashboard(legalDashboardPeriod)}
                    onNavigateToProcesses={() => setLegalActiveTab("processes")}
                    onNavigateToAgenda={() => setLegalActiveTab("agenda")}
                    getEtapaAtualLabel={getEtapaAtualLabel}
                    getAndamentoLabel={getAndamentoLabel}
                    getTipoProcessoLabel={getTipoProcessoLabel}
                    formatCurrency={formatCurrency}
                  />
                </TabsContent>

                {/* ========== CLIENTES JURÍDICOS ========== */}
                <TabsContent value="clients">
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-black font-bold">Clientes - Processo Judiciário</CardTitle>
                        <Dialog open={showLegalClientForm} onOpenChange={(open) => {
                          if (!open) {
                            resetLegalClientForm();
                          }
                          setShowLegalClientForm(open);
                        }}>
                          <DialogTrigger asChild>
                            <Button className="bg-blue-500 hover:bg-blue-600">
                              <Plus className="h-4 w-4 mr-2" />
                              Novo Cliente
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw]">
                            <DialogHeader>
                              <DialogTitle className="text-black font-bold">
                                {editingLegalClient ? 'Editar Cliente' : 'Novo Cliente'}
                              </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleLegalClientSubmit} className="space-y-4">
                              {/* Dados Básicos */}
                              {/* Nº Inscrição - Apenas mostra quando editando (gerado automaticamente) */}
                              {editingLegalClient && (
                                <div className="bg-blue-50 p-3 rounded">
                                  <label className="text-sm font-bold text-black block mb-1">Nº Inscrição (Gerado Automaticamente)</label>
                                  <p className="text-lg font-bold text-blue-700">{editingLegalClient.numero_inscricao}</p>
                                </div>
                              )}
                              {!editingLegalClient && (
                                <div className="bg-green-50 p-3 rounded">
                                  <p className="text-sm text-green-700 font-medium">
                                    ✓ O Nº de Inscrição será gerado automaticamente ao cadastrar
                                  </p>
                                </div>
                              )}
                              <div>
                                <label className="text-sm font-bold text-black block mb-1">Nome *</label>
                                <Input
                                  value={legalClientForm.nome}
                                  onChange={(e) => setLegalClientForm({...legalClientForm, nome: e.target.value})}
                                  required
                                  className="text-black"
                                />
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <label className="text-sm font-bold text-black block mb-1">CPF *</label>
                                  <Input
                                    value={legalClientForm.cpf}
                                    onChange={(e) => setLegalClientForm({...legalClientForm, cpf: e.target.value})}
                                    required
                                    className="text-black"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-bold text-black block mb-1">Telefone</label>
                                  <Input
                                    value={legalClientForm.telefone}
                                    onChange={(e) => setLegalClientForm({...legalClientForm, telefone: e.target.value})}
                                    className="text-black"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-bold text-black block mb-1">Email</label>
                                  <Input
                                    type="email"
                                    value={legalClientForm.email}
                                    onChange={(e) => setLegalClientForm({...legalClientForm, email: e.target.value})}
                                    className="text-black"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-bold text-black block mb-1">Senha Gov.br</label>
                                  <Input
                                    value={legalClientForm.senha_gov}
                                    onChange={(e) => setLegalClientForm({...legalClientForm, senha_gov: e.target.value})}
                                    className="text-black"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-bold text-black block mb-1">Nome do Vendedor</label>
                                  <Input
                                    value={legalClientForm.vendedor}
                                    onChange={(e) => setLegalClientForm({...legalClientForm, vendedor: e.target.value})}
                                    className="text-black"
                                    placeholder="Nome do vendedor..."
                                  />
                                </div>
                              </div>

                              {/* Endereço */}
                              <div className="border-t pt-4">
                                <h4 className="font-bold text-black mb-3">Endereço</h4>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                  <div>
                                    <label className="text-sm font-bold text-black block mb-1">CEP</label>
                                    <Input
                                      value={legalClientForm.cep}
                                      onChange={(e) => setLegalClientForm({...legalClientForm, cep: e.target.value})}
                                      className="text-black"
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <label className="text-sm font-bold text-black block mb-1">Logradouro</label>
                                    <Input
                                      value={legalClientForm.logradouro}
                                      onChange={(e) => setLegalClientForm({...legalClientForm, logradouro: e.target.value})}
                                      className="text-black"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-bold text-black block mb-1">Número</label>
                                    <Input
                                      value={legalClientForm.numero}
                                      onChange={(e) => setLegalClientForm({...legalClientForm, numero: e.target.value})}
                                      className="text-black"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">
                                  <div>
                                    <label className="text-sm font-bold text-black block mb-1">Complemento</label>
                                    <Input
                                      value={legalClientForm.complemento}
                                      onChange={(e) => setLegalClientForm({...legalClientForm, complemento: e.target.value})}
                                      className="text-black"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-bold text-black block mb-1">Bairro</label>
                                    <Input
                                      value={legalClientForm.bairro}
                                      onChange={(e) => setLegalClientForm({...legalClientForm, bairro: e.target.value})}
                                      className="text-black"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-bold text-black block mb-1">Cidade</label>
                                    <Input
                                      value={legalClientForm.cidade}
                                      onChange={(e) => setLegalClientForm({...legalClientForm, cidade: e.target.value})}
                                      className="text-black"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-bold text-black block mb-1">Estado</label>
                                    <Input
                                      value={legalClientForm.estado}
                                      onChange={(e) => setLegalClientForm({...legalClientForm, estado: e.target.value})}
                                      className="text-black"
                                      maxLength={2}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Exames Realizados */}
                              <div className="border-t pt-4">
                                <div className="flex justify-between items-center mb-3">
                                  <h4 className="font-bold text-black">Exames Realizados</h4>
                                  <Button type="button" variant="outline" size="sm" onClick={addExame}>
                                    <Plus className="h-4 w-4 mr-1" /> Adicionar Exame
                                  </Button>
                                </div>
                                {legalClientForm.exames_realizados.map((exame, index) => (
                                  <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2 p-2 bg-gray-50 rounded">
                                    <div>
                                      <Input
                                        placeholder="Nome do Exame"
                                        value={exame.nome}
                                        onChange={(e) => updateExame(index, 'nome', e.target.value)}
                                        className="text-black text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Input
                                        type="date"
                                        placeholder="Data Realização"
                                        value={exame.data_realizacao}
                                        onChange={(e) => updateExame(index, 'data_realizacao', e.target.value)}
                                        className="text-black text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Input
                                        type="date"
                                        placeholder="Data Validade"
                                        value={exame.data_validade}
                                        onChange={(e) => updateExame(index, 'data_validade', e.target.value)}
                                        className="text-black text-sm"
                                      />
                                    </div>
                                    <Button 
                                      type="button" 
                                      variant="destructive" 
                                      size="sm"
                                      onClick={() => removeExame(index)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>

                              <div className="flex gap-3 pt-4">
                                <Button type="submit" className="flex-1 bg-green-500 hover:bg-green-600">
                                  {editingLegalClient ? 'Salvar Alterações' : 'Cadastrar Cliente'}
                                </Button>
                                <Button type="button" variant="outline" onClick={() => {
                                  resetLegalClientForm();
                                  setShowLegalClientForm(false);
                                }}>
                                  Cancelar
                                </Button>
                              </div>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Filtros */}
                      <div className="mb-4">
                        <Input
                          placeholder="Buscar por nome, CPF ou nº inscrição..."
                          value={legalFilters.clients.search}
                          onChange={(e) => setLegalFilters({...legalFilters, clients: {...legalFilters.clients, search: e.target.value}})}
                          className="max-w-md text-black"
                        />
                      </div>

                      {/* Lista de Clientes */}
                      <div className="space-y-3 max-h-[600px] overflow-y-auto">
                        {legalClients
                          .filter(c => {
                            const search = legalFilters.clients.search.toLowerCase();
                            return !search || 
                              c.nome?.toLowerCase().includes(search) ||
                              c.cpf?.includes(search) ||
                              c.numero_inscricao?.includes(search);
                          })
                          .map((client) => (
                          <Card key={client.id} className="border-l-4 border-l-blue-500">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-bold text-black text-lg">{client.nome}</h4>
                                  <p className="text-sm text-gray-700">
                                    <strong>Nº Inscrição:</strong> {client.numero_inscricao} | <strong>CPF:</strong> {client.cpf}
                                  </p>
                                  {client.telefone && <p className="text-sm text-gray-600">Tel: {client.telefone}</p>}
                                  {client.email && <p className="text-sm text-gray-600">Email: {client.email}</p>}
                                  {client.vendedor && (
                                    <p className="text-sm text-gray-600">
                                      <strong>Vendedor:</strong> {client.vendedor}
                                    </p>
                                  )}
                                  {client.cidade && (
                                    <p className="text-sm text-gray-600">
                                      {client.logradouro}, {client.numero} - {client.bairro}, {client.cidade}/{client.estado}
                                    </p>
                                  )}
                                  {client.exames_realizados?.length > 0 && (
                                    <div className="mt-2">
                                      <span className="text-xs font-bold text-gray-600">Exames:</span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {client.exames_realizados.map((exame, i) => (
                                          <Badge key={`${exame.nome}-${exame.data_validade || i}`} variant="outline" className="text-xs">
                                            {exame.nome} {exame.data_validade && `(val: ${new Date(exame.data_validade).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })})`}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button variant="outline" size="sm" onClick={() => startEditLegalClient(client)}>
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button variant="destructive" size="sm" onClick={() => deleteLegalClient(client.id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        {legalClients.length === 0 && (
                          <p className="text-center text-gray-500 py-8">Nenhum cliente cadastrado</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ========== PROCESSOS JUDICIAIS ========== */}
                <TabsContent value="processes">
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-black font-bold">Processos Judiciais</CardTitle>
                        <Dialog open={showLegalProcessForm} onOpenChange={(open) => {
                          if (!open) {
                            resetLegalProcessForm();
                          }
                          setShowLegalProcessForm(open);
                        }}>
                          <DialogTrigger asChild>
                            <Button className="bg-blue-500 hover:bg-blue-600">
                              <Plus className="h-4 w-4 mr-2" />
                              Novo Processo
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto w-[95vw]">
                            <DialogHeader>
                              <DialogTitle className="text-black font-bold">
                                {editingLegalProcess ? 'Editar Processo' : 'Novo Processo'}
                              </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleLegalProcessSubmit} className="space-y-4">
                              {/* Categoria do Processo */}
                              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg border">
                                <label className="text-sm font-bold text-black block mb-2">Categoria do Processo *</label>
                                <div className="flex gap-4" data-testid="categoria-toggle">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name="categoria"
                                      value="administrativo"
                                      checked={legalProcessForm.categoria === "administrativo"}
                                      onChange={() => setLegalProcessForm({
                                        ...legalProcessForm,
                                        categoria: "administrativo",
                                        tipo_processo: "loas",
                                        origem_administrativo_id: ""
                                      })}
                                    />
                                    <span className="font-bold text-blue-800">Processo Administrativo</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name="categoria"
                                      value="judicial"
                                      checked={legalProcessForm.categoria === "judicial"}
                                      onChange={() => setLegalProcessForm({
                                        ...legalProcessForm,
                                        categoria: "judicial",
                                        tipo_processo: "processo_civil"
                                      })}
                                    />
                                    <span className="font-bold text-purple-800">Processo Judicial</span>
                                  </label>
                                </div>
                              </div>

                              {/* Se judicial: pergunta origem */}
                              {legalProcessForm.categoria === "judicial" && (
                                <div className="bg-purple-50 p-3 rounded">
                                  <label className="text-sm font-bold text-black block mb-2">Tem origem em Processo Administrativo?</label>
                                  <Select
                                    value={legalProcessForm.origem_administrativo_id || "nao"}
                                    onValueChange={(value) => {
                                      if (value === "nao") {
                                        setLegalProcessForm({...legalProcessForm, origem_administrativo_id: ""});
                                      } else {
                                        const origem = legalProcesses.find(p => p.id === value);
                                        if (origem) {
                                          setLegalProcessForm({
                                            ...legalProcessForm,
                                            origem_administrativo_id: value,
                                            client_id: origem.client_id || "",
                                            valor_processo: origem.valor_processo || "",
                                            valor_honorario: origem.valor_honorario || ""
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="text-black h-9" data-testid="origem-admin-select">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="nao">Não (novo processo)</SelectItem>
                                      {legalProcesses
                                        .filter(p => p.categoria === "administrativo")
                                        .map(p => (
                                          <SelectItem key={p.id} value={p.id}>
                                            {p.numero_protocolo} — {p.client_name || ''}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}

                              {/* Dados Principais - Sempre visível */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="relative">
                                  <label className="text-xs font-bold text-black block mb-1">Cliente *</label>
                                  {(() => {
                                    const selectedClient = legalClients.find(c => c.id === legalProcessForm.client_id);
                                    const inputValue = legalClientDropdownOpen
                                      ? legalClientSearch
                                      : (selectedClient ? `${selectedClient.nome} - ${selectedClient.cpf}` : legalClientSearch);
                                    const filtered = legalClients.filter(c => {
                                      const q = legalClientSearch.toLowerCase().trim();
                                      if (!q) return true;
                                      return (c.nome || "").toLowerCase().includes(q) || (c.cpf || "").toLowerCase().includes(q);
                                    }).slice(0, 30);
                                    return (
                                      <>
                                        <Input
                                          type="text"
                                          value={inputValue}
                                          onChange={(e) => {
                                            setLegalClientSearch(e.target.value);
                                            setLegalClientDropdownOpen(true);
                                            if (legalProcessForm.client_id) {
                                              setLegalProcessForm({...legalProcessForm, client_id: ""});
                                            }
                                          }}
                                          onFocus={() => setLegalClientDropdownOpen(true)}
                                          onBlur={() => setTimeout(() => setLegalClientDropdownOpen(false), 200)}
                                          placeholder="Buscar por nome ou CPF..."
                                          className="text-black h-9"
                                          data-testid="legal-client-search-input"
                                        />
                                        {legalClientDropdownOpen && (
                                          <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border rounded-md shadow-lg" data-testid="legal-client-dropdown">
                                            {filtered.length === 0 ? (
                                              <div className="p-3 text-sm text-gray-500">Nenhum cliente encontrado</div>
                                            ) : (
                                              filtered.map(client => (
                                                <button
                                                  type="button"
                                                  key={client.id}
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => {
                                                    setLegalProcessForm({...legalProcessForm, client_id: client.id});
                                                    setLegalClientSearch("");
                                                    setLegalClientDropdownOpen(false);
                                                  }}
                                                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-black border-b last:border-b-0"
                                                  data-testid={`legal-client-option-${client.id}`}
                                                >
                                                  <div className="font-medium">{client.nome}</div>
                                                  <div className="text-xs text-gray-500">{client.cpf}</div>
                                                </button>
                                              ))
                                            )}
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                <div>
                                  <label className="text-xs font-bold text-black block mb-1">
                                    {legalProcessForm.categoria === "judicial" ? "Nº Processo" : "Nº Protocolo"} *
                                  </label>
                                  <Input
                                    value={legalProcessForm.numero_protocolo}
                                    onChange={(e) => setLegalProcessForm({...legalProcessForm, numero_protocolo: e.target.value})}
                                    required
                                    className="text-black h-9"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-bold text-black block mb-1">
                                    {legalProcessForm.categoria === "administrativo" ? "Valor do Processo (R$)" : "Honorário (R$)"} *
                                  </label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={legalProcessForm.categoria === "administrativo" ? legalProcessForm.valor_processo : legalProcessForm.valor_honorario}
                                    onChange={(e) => {
                                      if (legalProcessForm.categoria === "administrativo") {
                                        const vp = e.target.value;
                                        const honor = vp ? (parseFloat(vp) * 0.3).toFixed(2) : "";
                                        setLegalProcessForm({...legalProcessForm, valor_processo: vp, valor_honorario: honor});
                                      } else {
                                        setLegalProcessForm({...legalProcessForm, valor_honorario: e.target.value});
                                      }
                                    }}
                                    required
                                    className="text-black h-9"
                                    data-testid="valor-principal-input"
                                  />
                                </div>
                              </div>

                              {/* Se admin, mostra honorário calculado editável */}
                              {legalProcessForm.categoria === "administrativo" && (
                                <div className="bg-yellow-50 p-2 rounded flex items-center gap-3 text-sm">
                                  <span className="text-gray-700 font-bold">Honorários (30%):</span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={legalProcessForm.valor_honorario}
                                    onChange={(e) => setLegalProcessForm({...legalProcessForm, valor_honorario: e.target.value})}
                                    className="text-black h-8 max-w-[200px]"
                                    data-testid="honorario-admin-input"
                                  />
                                  <span className="text-xs text-gray-500">Calculado automaticamente. Edite se necessário.</span>
                                </div>
                              )}

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-bold text-black block mb-1">Tipo *</label>
                                  <Select 
                                    value={legalProcessForm.tipo_processo} 
                                    onValueChange={(value) => setLegalProcessForm({...legalProcessForm, tipo_processo: value})}
                                  >
                                    <SelectTrigger className="text-black h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {legalProcessForm.categoria === "administrativo" ? (
                                        <>
                                          <SelectItem value="loas">LOAS</SelectItem>
                                          <SelectItem value="apos_idade">Aposentadoria Por Idade</SelectItem>
                                          <SelectItem value="aux_doenca">Auxílio Doença</SelectItem>
                                          <SelectItem value="pensao_morte">Pensão Por Morte</SelectItem>
                                          <SelectItem value="aux_reclusao">Auxílio Reclusão</SelectItem>
                                          <SelectItem value="apos_especial">Aposentadoria Especial</SelectItem>
                                          <SelectItem value="salario_maternidade">Salário Maternidade</SelectItem>
                                        </>
                                      ) : (
                                        <>
                                          <SelectItem value="processo_civil">Processo Civil</SelectItem>
                                          <SelectItem value="processo_penal">Processo Penal/Criminal</SelectItem>
                                          <SelectItem value="processo_trabalhista">Processo Trabalhista</SelectItem>
                                          <SelectItem value="processo_previdenciario">Processo Previdenciário</SelectItem>
                                          <SelectItem value="execucao_penal">Processo de Execução Penal</SelectItem>
                                          <SelectItem value="conhecimento">Processo de Conhecimento</SelectItem>
                                          <SelectItem value="execucao">Processo de Execução</SelectItem>
                                          <SelectItem value="cautelar">Cautelar ou de Urgência</SelectItem>
                                          <SelectItem value="procedimento_comum">Procedimento Comum</SelectItem>
                                          <SelectItem value="procedimento_especial">Procedimento Especial</SelectItem>
                                          <SelectItem value="sumarissimo">Sumaríssimo</SelectItem>
                                        </>
                                      )}
                                    </SelectContent>
                                  </Select>
                                </div>
                                {/* Andamento: APENAS para Administrativo (com opção "Negado") */}
                                {legalProcessForm.categoria === "administrativo" && (
                                  <div>
                                    <label className="text-sm font-bold text-black block mb-1">Andamento *</label>
                                    <Select 
                                      value={legalProcessForm.andamento} 
                                      onValueChange={(value) => setLegalProcessForm({...legalProcessForm, andamento: value})}
                                    >
                                      <SelectTrigger className="text-black h-9" data-testid="andamento-admin-select">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="em_analise">Em Análise</SelectItem>
                                        <SelectItem value="exigencia">Exigência</SelectItem>
                                        <SelectItem value="concedido">Concedido</SelectItem>
                                        <SelectItem value="negado">Negado</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                                {/* Etapa Atual: APENAS para Judicial (dispara bloco RPV) */}
                                {legalProcessForm.categoria === "judicial" && (
                                  <div>
                                    <label className="text-xs font-bold text-black block mb-1">Etapa Atual</label>
                                    <Select 
                                      value={legalProcessForm.etapa_atual || "nenhuma"} 
                                      onValueChange={(value) => setLegalProcessForm({...legalProcessForm, etapa_atual: value})}
                                    >
                                      <SelectTrigger className="text-black h-9" data-testid="etapa-atual-judicial-select">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="nenhuma">Nenhuma</SelectItem>
                                        <SelectItem value="peticao_inicial">Petição Inicial</SelectItem>
                                        <SelectItem value="contestacao">Contestação</SelectItem>
                                        <SelectItem value="contra_razoes">Contra Razões</SelectItem>
                                        <SelectItem value="recurso_inominato">Recurso Inominado</SelectItem>
                                        <SelectItem value="replica_contestacao">Réplica Contestação</SelectItem>
                                        <SelectItem value="aguardando_sentenca">Aguardando Sentença</SelectItem>
                                        <SelectItem value="sentenca_proferida">Sentença Proferida</SelectItem>
                                        <SelectItem value="recurso">Em Recurso</SelectItem>
                                        <SelectItem value="audiencia">Audiência</SelectItem>
                                        <SelectItem value="rpv">RPV</SelectItem>
                                        <SelectItem value="suspenso">Suspenso</SelectItem>
                                        <SelectItem value="concluso_decisao">Concluso para Decisão</SelectItem>
                                        <SelectItem value="concluso_despacho">Concluso para Despacho</SelectItem>
                                        <SelectItem value="resposta_acusacao">Resposta Acusação</SelectItem>
                                        <SelectItem value="revisao_criminal">Revisão Criminal</SelectItem>
                                        <SelectItem value="transitado_julgado">Transitado Julgado</SelectItem>
                                        <SelectItem value="cumprimento_sentenca">Cumprimento Sentença</SelectItem>
                                        <SelectItem value="finalizado">Finalizado</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>

                              {/* Campos RPV (se etapa = rpv) */}
                              {legalProcessForm.categoria === "judicial" && legalProcessForm.etapa_atual === "rpv" && (
                                <div className="bg-green-50 p-3 rounded border border-green-300">
                                  <h4 className="font-bold text-black text-sm mb-2">RPV — Requisição de Pequeno Valor</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-xs font-bold text-black block mb-1">Valor a Receber (R$) *</label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={legalProcessForm.rpv_valor}
                                        onChange={(e) => setLegalProcessForm({...legalProcessForm, rpv_valor: e.target.value})}
                                        className="text-black h-9"
                                        data-testid="rpv-valor-input"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-bold text-black block mb-1">Nº Protocolo RPV *</label>
                                      <Input
                                        value={legalProcessForm.rpv_protocolo}
                                        onChange={(e) => setLegalProcessForm({...legalProcessForm, rpv_protocolo: e.target.value})}
                                        className="text-black h-9"
                                        data-testid="rpv-protocolo-input"
                                      />
                                    </div>
                                  </div>
                                  {legalProcessForm.rpv_recebido && (
                                    <p className="text-xs text-green-700 mt-2 font-bold">✓ RPV já recebida (lançada em Entradas)</p>
                                  )}
                                </div>
                              )}

                              {/* Detalhamento */}
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">Detalhamento do Processo</label>
                                <textarea
                                  value={legalProcessForm.detalhamento}
                                  onChange={(e) => setLegalProcessForm({...legalProcessForm, detalhamento: e.target.value})}
                                  className="w-full border rounded p-2 text-black text-sm"
                                  rows={2}
                                  placeholder="Descreva os detalhes do processo..."
                                />
                              </div>

                              {/* Campos específicos para status Concedido */}
                              {legalProcessForm.andamento === 'concedido' && (
                                <div className="bg-green-50 p-3 rounded">
                                  <h4 className="font-bold text-black text-sm mb-2">Dados do Benefício Concedido</h4>
                                  {/* Campos específicos administrativo: previsão pagamento + status */}
                                  {legalProcessForm.categoria === "administrativo" && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 p-2 bg-white rounded border border-green-200">
                                      <div>
                                        <label className="text-xs font-bold text-black block mb-1">Previsão Pagamento Honorário</label>
                                        <Input
                                          type="date"
                                          value={legalProcessForm.honorario_previsao_pagamento}
                                          onChange={(e) => setLegalProcessForm({...legalProcessForm, honorario_previsao_pagamento: e.target.value})}
                                          className="text-black h-9"
                                          data-testid="honorario-previsao-input"
                                        />
                                      </div>
                                      <div className="flex items-end">
                                        {legalProcessForm.honorario_pago ? (
                                          <span className="text-green-700 font-bold text-sm">✓ Pago — Lançado em Entradas</span>
                                        ) : (
                                          <span className="text-orange-600 font-medium text-sm">⏳ Aguardando pagamento</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div>
                                      <label className="text-xs font-bold text-black block mb-1">Prazo Início</label>
                                      <Input
                                        type="date"
                                        value={legalProcessForm.prazo_inicio}
                                        onChange={(e) => setLegalProcessForm({...legalProcessForm, prazo_inicio: e.target.value})}
                                        className="text-black h-9"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-bold text-black block mb-1">Prazo Final</label>
                                      <Input
                                        type="date"
                                        value={legalProcessForm.prazo_final}
                                        onChange={(e) => setLegalProcessForm({...legalProcessForm, prazo_final: e.target.value})}
                                        className="text-black h-9"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-bold text-black block mb-1">Prorrogação</label>
                                      <Select 
                                        value={legalProcessForm.status_prorrogacao || "sem_prorrogacao"} 
                                        onValueChange={(value) => setLegalProcessForm({...legalProcessForm, status_prorrogacao: value})}
                                      >
                                        <SelectTrigger className="text-black h-9">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="sem_prorrogacao">Sem Prorrogação</SelectItem>
                                          <SelectItem value="ativa">Ativa</SelectItem>
                                          <SelectItem value="expirada">Expirada</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Etapas do Processo - Layout Compacto */}
                              {/* Removido conforme solicitação — Status das Etapas e Observações */}

                              <div className="flex gap-3 pt-2">
                                <Button type="submit" className="flex-1 bg-green-500 hover:bg-green-600">
                                  {editingLegalProcess ? 'Salvar Alterações' : 'Cadastrar Processo'}
                                </Button>
                                <Button type="button" variant="outline" onClick={() => {
                                  resetLegalProcessForm();
                                  setShowLegalProcessForm(false);
                                }}>
                                  Cancelar
                                </Button>
                              </div>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Filtros */}
                      <div className="flex gap-4 mb-4">
                        <Input
                          placeholder="Buscar por protocolo ou cliente..."
                          value={legalFilters.processes.search}
                          onChange={(e) => setLegalFilters({...legalFilters, processes: {...legalFilters.processes, search: e.target.value}})}
                          className="max-w-xs text-black"
                        />
                        <Select 
                          value={legalFilters.processes.andamento} 
                          onValueChange={(value) => setLegalFilters({...legalFilters, processes: {...legalFilters.processes, andamento: value}})}
                        >
                          <SelectTrigger className="w-40 text-black">
                            <SelectValue placeholder="Andamento" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="em_analise">Em Análise</SelectItem>
                            <SelectItem value="exigencia">Exigência</SelectItem>
                            <SelectItem value="concedido">Concedido</SelectItem>
                            <SelectItem value="justica">Justiça</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select 
                          value={legalFilters.processes.tipo} 
                          onValueChange={(value) => setLegalFilters({...legalFilters, processes: {...legalFilters.processes, tipo: value}})}
                        >
                          <SelectTrigger className="w-32 text-black">
                            <SelectValue placeholder="Tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="urbano">Urbano</SelectItem>
                            <SelectItem value="rural">Rural</SelectItem>
                            <SelectItem value="loas">LOAS</SelectItem>
                            <SelectItem value="salario_maternidade">Salário Maternidade</SelectItem>
                            <SelectItem value="auxilio_doenca">Auxílio Doença</SelectItem>
                            <SelectItem value="pensao_morte">Pensão por Morte</SelectItem>
                            <SelectItem value="aposentadoria">Aposentadoria</SelectItem>
                            <SelectItem value="criminal">Criminal</SelectItem>
                            <SelectItem value="civil">Civil</SelectItem>
                            <SelectItem value="trabalhista">Trabalhista</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Lista de Processos */}
                      <div className="space-y-3 max-h-[600px] overflow-y-auto">
                        {legalProcesses
                          .filter(p => {
                            const search = legalFilters.processes.search.toLowerCase();
                            const matchSearch = !search || 
                              p.numero_protocolo?.toLowerCase().includes(search) ||
                              p.client_name?.toLowerCase().includes(search);
                            const matchAndamento = legalFilters.processes.andamento === 'all' || p.andamento === legalFilters.processes.andamento;
                            const matchTipo = legalFilters.processes.tipo === 'all' || p.tipo_processo === legalFilters.processes.tipo;
                            return matchSearch && matchAndamento && matchTipo;
                          })
                          .map((process) => (
                          <Card key={process.id} className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedLegalProcess(process)} data-testid={`legal-process-card-${process.id}`}>
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className={process.categoria === "administrativo" ? "bg-blue-100 text-blue-800 border border-blue-300" : "bg-purple-100 text-purple-800 border border-purple-300"}>
                                      {process.categoria === "administrativo" ? "Administrativo" : "Judicial"}
                                    </Badge>
                                    <h4 className="font-bold text-black text-lg">
                                      {process.categoria === "judicial" ? "Nº Processo" : "Protocolo"}: {process.numero_protocolo}
                                    </h4>
                                  </div>
                                  <p className="text-sm text-gray-700 mt-1"><strong>Cliente:</strong> {process.client_name}</p>
                                  {process.categoria === "administrativo" && process.valor_processo ? (
                                    <p className="text-sm text-gray-700"><strong>Valor Processo:</strong> {formatCurrency(process.valor_processo)} <span className="text-gray-500">• Honorário (30%): {formatCurrency(process.valor_honorario)}</span></p>
                                  ) : (
                                    <p className="text-sm text-gray-700"><strong>Honorário:</strong> {formatCurrency(process.valor_honorario)}</p>
                                  )}
                                  {process.categoria === "judicial" && process.rpv_valor ? (
                                    <p className="text-sm text-green-700"><strong>RPV:</strong> {formatCurrency(process.rpv_valor)} {process.rpv_protocolo ? `• Protocolo ${process.rpv_protocolo}` : ""} {process.rpv_recebido ? <span className="ml-1 font-bold">✓ Recebida</span> : ""}</p>
                                  ) : null}
                                  {process.honorario_pago ? (
                                    <p className="text-sm text-green-700 font-bold">✓ Honorário Pago (Entrada lançada)</p>
                                  ) : null}
                                  {process.etapa_atual && process.etapa_atual !== 'nenhuma' && (
                                    <p className="text-sm text-blue-700 font-medium mt-1">
                                      <strong>Etapa Atual:</strong> {getEtapaAtualLabel(process.etapa_atual)}
                                    </p>
                                  )}
                                  <div className="flex gap-2 mt-2 flex-wrap">
                                    <Badge className={getAndamentoColor(process.andamento)}>
                                      {getAndamentoLabel(process.andamento)}
                                    </Badge>
                                    <Badge variant="outline">{getTipoProcessoLabel(process.tipo_processo)}</Badge>
                                  </div>
                                  {process.andamento === 'concedido' && process.prazo_final && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Prazo: {new Date(process.prazo_inicio).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} a {new Date(process.prazo_final).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                    </p>
                                  )}
                                </div>
                                <div className="flex gap-2 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
                                  {!process.honorario_pago && (parseFloat(process.valor_honorario) || 0) > 0 && (
                                    <Button
                                      size="sm"
                                      className="bg-green-500 hover:bg-green-600 text-white"
                                      onClick={() => payLegalHonorario(process.id)}
                                      data-testid={`mark-honorario-pago-${process.id}`}
                                    >
                                      Marcar Pago
                                    </Button>
                                  )}
                                  {process.categoria === "judicial" && !process.rpv_recebido && (parseFloat(process.rpv_valor) || 0) > 0 && (
                                    <Button
                                      size="sm"
                                      className="bg-green-500 hover:bg-green-600 text-white"
                                      onClick={() => receiveLegalRPV(process.id)}
                                      data-testid={`mark-rpv-recebido-${process.id}`}
                                    >
                                      RPV Recebida
                                    </Button>
                                  )}
                                  <Button variant="outline" size="sm" onClick={() => startEditLegalProcess(process)} data-testid={`edit-legal-process-${process.id}`}>
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button variant="destructive" size="sm" onClick={() => deleteLegalProcess(process.id)} data-testid={`delete-legal-process-${process.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        {legalProcesses.length === 0 && (
                          <p className="text-center text-gray-500 py-8">Nenhum processo cadastrado</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ========== HONORÁRIOS A RECEBER / RECEBIDOS ========== */}
                <TabsContent value="honorarios">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-black font-bold">Honorários — A Receber e Recebidos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        // Monta lista de honorários (Administrativo + Judicial Honorário + RPV Judicial)
                        const items = [];
                        legalProcesses.forEach(p => {
                          const hon = parseFloat(p.valor_honorario) || 0;
                          if (hon > 0) {
                            items.push({
                              id: p.id + "_hon",
                              process_id: p.id,
                              process_numero: p.numero_protocolo,
                              client_name: p.client_name,
                              tipo: p.categoria === "administrativo" ? "administrativo" : "judicial_honorario",
                              valor: hon,
                              status: p.honorario_pago ? "recebido" : "a_receber",
                              data_prevista: p.honorario_previsao_pagamento,
                              data_pagamento: p.honorario_paid_date,
                              tipo_processo: p.tipo_processo
                            });
                          }
                          if (p.categoria === "judicial" && (parseFloat(p.rpv_valor) || 0) > 0) {
                            items.push({
                              id: p.id + "_rpv",
                              process_id: p.id,
                              process_numero: p.numero_protocolo,
                              client_name: p.client_name,
                              tipo: "judicial_rpv",
                              valor: parseFloat(p.rpv_valor) || 0,
                              status: p.rpv_recebido ? "recebido" : "a_receber",
                              rpv_protocolo: p.rpv_protocolo,
                              data_pagamento: p.rpv_paid_date,
                              tipo_processo: p.tipo_processo
                            });
                          }
                        });

                        // Aplica filtros
                        const f = honorariosFilters;
                        const filtered = items.filter(item => {
                          if (f.status !== "all" && item.status !== f.status) return false;
                          if (f.tipo !== "all" && item.tipo !== f.tipo) return false;
                          if (f.search) {
                            const s = f.search.toLowerCase();
                            const hay = `${item.client_name || ""} ${item.process_numero || ""} ${item.rpv_protocolo || ""}`.toLowerCase();
                            if (!hay.includes(s)) return false;
                          }
                          if (f.data_inicio || f.data_fim) {
                            const d = item.status === "recebido" ? item.data_pagamento : item.data_prevista;
                            if (!d) return !(f.data_inicio || f.data_fim) ? true : false;
                            const dObj = new Date(d);
                            if (f.data_inicio && dObj < new Date(f.data_inicio)) return false;
                            if (f.data_fim && dObj > new Date(f.data_fim + "T23:59:59")) return false;
                          }
                          return true;
                        });

                        const totalReceber = filtered.filter(i => i.status === "a_receber").reduce((s, i) => s + i.valor, 0);
                        const totalRecebido = filtered.filter(i => i.status === "recebido").reduce((s, i) => s + i.valor, 0);

                        return (
                          <div className="space-y-4">
                            {/* Cards de totais */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4" data-testid="honorarios-total-receber">
                                <p className="text-sm text-yellow-800 font-bold">A RECEBER</p>
                                <p className="text-xl md:text-2xl font-bold text-yellow-900">{formatCurrency(totalReceber)}</p>
                                <p className="text-xs text-yellow-700">{filtered.filter(i => i.status === "a_receber").length} registro(s)</p>
                              </div>
                              <div className="bg-green-50 border border-green-300 rounded-lg p-4" data-testid="honorarios-total-recebido">
                                <p className="text-sm text-green-800 font-bold">RECEBIDOS</p>
                                <p className="text-xl md:text-2xl font-bold text-green-900">{formatCurrency(totalRecebido)}</p>
                                <p className="text-xs text-green-700">{filtered.filter(i => i.status === "recebido").length} registro(s)</p>
                              </div>
                            </div>

                            {/* Filtros */}
                            <div className="bg-gray-50 p-3 rounded border grid grid-cols-1 md:grid-cols-5 gap-3">
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">Buscar</label>
                                <Input
                                  placeholder="Cliente, protocolo..."
                                  value={honorariosFilters.search}
                                  onChange={(e) => setHonorariosFilters({...honorariosFilters, search: e.target.value})}
                                  className="text-black h-9"
                                  data-testid="honorarios-filter-search"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">Status</label>
                                <Select value={honorariosFilters.status} onValueChange={(v) => setHonorariosFilters({...honorariosFilters, status: v})}>
                                  <SelectTrigger className="text-black h-9" data-testid="honorarios-filter-status"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="a_receber">A Receber</SelectItem>
                                    <SelectItem value="recebidos">Recebidos</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">Tipo</label>
                                <Select value={honorariosFilters.tipo} onValueChange={(v) => setHonorariosFilters({...honorariosFilters, tipo: v})}>
                                  <SelectTrigger className="text-black h-9" data-testid="honorarios-filter-tipo"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="administrativo">Admin (Honorário)</SelectItem>
                                    <SelectItem value="judicial_honorario">Judicial (Honorário)</SelectItem>
                                    <SelectItem value="judicial_rpv">Judicial (RPV)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">Data De</label>
                                <Input
                                  type="date"
                                  value={honorariosFilters.data_inicio}
                                  onChange={(e) => setHonorariosFilters({...honorariosFilters, data_inicio: e.target.value})}
                                  className="text-black h-9"
                                  data-testid="honorarios-filter-data-inicio"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-bold text-black block mb-1">Data Até</label>
                                <Input
                                  type="date"
                                  value={honorariosFilters.data_fim}
                                  onChange={(e) => setHonorariosFilters({...honorariosFilters, data_fim: e.target.value})}
                                  className="text-black h-9"
                                  data-testid="honorarios-filter-data-fim"
                                />
                              </div>
                            </div>

                            {honorariosFilters.search || honorariosFilters.status !== "all" || honorariosFilters.tipo !== "all" || honorariosFilters.data_inicio || honorariosFilters.data_fim ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setHonorariosFilters({ search: "", status: "all", tipo: "all", data_inicio: "", data_fim: "" })}
                                data-testid="honorarios-clear-filters"
                              >
                                Limpar Filtros
                              </Button>
                            ) : null}

                            {/* Tabela */}
                            <div className="border rounded-lg overflow-x-auto">
                              <table className="w-full text-sm min-w-[700px]">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="p-2 text-left text-black font-bold">Status</th>
                                    <th className="p-2 text-left text-black font-bold">Tipo</th>
                                    <th className="p-2 text-left text-black font-bold">Cliente</th>
                                    <th className="p-2 text-left text-black font-bold">Protocolo</th>
                                    <th className="p-2 text-right text-black font-bold">Valor</th>
                                    <th className="p-2 text-left text-black font-bold">Data</th>
                                    <th className="p-2 text-center text-black font-bold">Ação</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filtered.length === 0 && (
                                    <tr><td colSpan="7" className="text-center text-gray-500 py-6">Nenhum honorário encontrado com os filtros aplicados.</td></tr>
                                  )}
                                  {filtered.map(item => (
                                    <tr key={item.id} className="border-t hover:bg-gray-50" data-testid={`honorario-row-${item.id}`}>
                                      <td className="p-2">
                                        {item.status === "recebido" ? (
                                          <Badge className="bg-green-100 text-green-800 border border-green-300">Recebido</Badge>
                                        ) : (
                                          <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300">A Receber</Badge>
                                        )}
                                      </td>
                                      <td className="p-2">
                                        {item.tipo === "administrativo" ? (
                                          <Badge className="bg-blue-100 text-blue-800 border border-blue-300">Admin</Badge>
                                        ) : item.tipo === "judicial_honorario" ? (
                                          <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-300">Judicial</Badge>
                                        ) : (
                                          <Badge className="bg-purple-100 text-purple-800 border border-purple-300">RPV</Badge>
                                        )}
                                      </td>
                                      <td className="p-2 text-black">{item.client_name || "-"}</td>
                                      <td className="p-2 text-black">{item.process_numero}{item.rpv_protocolo ? ` / RPV ${item.rpv_protocolo}` : ""}</td>
                                      <td className="p-2 text-right font-bold text-black">{formatCurrency(item.valor)}</td>
                                      <td className="p-2 text-gray-700">
                                        {item.status === "recebido"
                                          ? (item.data_pagamento ? new Date(item.data_pagamento).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : "-")
                                          : (item.data_prevista ? new Date(item.data_prevista).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : "-")}
                                      </td>
                                      <td className="p-2 text-center">
                                        {item.status === "a_receber" ? (
                                          item.tipo === "judicial_rpv" ? (
                                            <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white" onClick={() => receiveLegalRPV(item.process_id)} data-testid={`honorario-receive-rpv-${item.id}`}>
                                              RPV Recebida
                                            </Button>
                                          ) : (
                                            <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white" onClick={() => payLegalHonorario(item.process_id)} data-testid={`honorario-pay-${item.id}`}>
                                              Marcar Pago
                                            </Button>
                                          )
                                        ) : (
                                          <span className="text-xs text-gray-500">—</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ========== AGENDA DE AUDIÊNCIAS E PERÍCIAS ========== */}
                <TabsContent value="agenda">
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-black font-bold">Agenda de Audiências e Perícias</CardTitle>
                        <Dialog open={showAgendamentoForm} onOpenChange={(open) => {
                          if (!open) resetAgendamentoForm();
                          setShowAgendamentoForm(open);
                        }}>
                          <DialogTrigger asChild>
                            <Button className="bg-purple-500 hover:bg-purple-600">
                              <Plus className="h-4 w-4 mr-2" />
                              Novo Agendamento
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="text-black font-bold">
                                {editingAgendamento ? 'Editar Agendamento' : 'Novo Agendamento'}
                              </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAgendamentoSubmit} className="space-y-4">
                              <div>
                                <label className="text-sm font-bold text-black block mb-1">Processo *</label>
                                <Select 
                                  value={agendamentoForm.process_id} 
                                  onValueChange={(value) => setAgendamentoForm({...agendamentoForm, process_id: value})}
                                >
                                  <SelectTrigger className="text-black">
                                    <SelectValue placeholder="Selecione o processo" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {legalProcesses.map(p => (
                                      <SelectItem key={p.id} value={p.id}>
                                        {p.numero_protocolo} - {p.client_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-bold text-black block mb-1">Tipo *</label>
                                  <Select 
                                    value={agendamentoForm.tipo} 
                                    onValueChange={(value) => setAgendamentoForm({...agendamentoForm, tipo: value})}
                                  >
                                    <SelectTrigger className="text-black">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="audiencia">Audiência</SelectItem>
                                      <SelectItem value="pericia_medica">Perícia Médica</SelectItem>
                                      <SelectItem value="consultar_social">Consultar Social</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="text-sm font-bold text-black block mb-1">Data e Hora *</label>
                                  <Input
                                    type="datetime-local"
                                    value={agendamentoForm.data_hora}
                                    onChange={(e) => setAgendamentoForm({...agendamentoForm, data_hora: e.target.value})}
                                    required
                                    className="text-black"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="text-sm font-bold text-black block mb-1">Título *</label>
                                <Input
                                  value={agendamentoForm.titulo}
                                  onChange={(e) => setAgendamentoForm({...agendamentoForm, titulo: e.target.value})}
                                  required
                                  className="text-black"
                                  placeholder="Ex: Audiência de Instrução"
                                />
                              </div>
                              <div>
                                <label className="text-sm font-bold text-black block mb-1">Local</label>
                                <Input
                                  value={agendamentoForm.local}
                                  onChange={(e) => setAgendamentoForm({...agendamentoForm, local: e.target.value})}
                                  className="text-black"
                                  placeholder="Ex: Fórum da Comarca de..."
                                />
                              </div>
                              <div>
                                <label className="text-sm font-bold text-black block mb-1">Advogado Responsável</label>
                                <Select 
                                  value={agendamentoForm.advogado_responsavel_id} 
                                  onValueChange={(value) => setAgendamentoForm({...agendamentoForm, advogado_responsavel_id: value})}
                                >
                                  <SelectTrigger className="text-black">
                                    <SelectValue placeholder="Selecione o advogado" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {companyUsers.map(u => (
                                      <SelectItem key={u.user_id} value={u.user_id}>
                                        {u.name} ({u.email})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-sm font-bold text-black block mb-1">Descrição</label>
                                <textarea
                                  value={agendamentoForm.descricao}
                                  onChange={(e) => setAgendamentoForm({...agendamentoForm, descricao: e.target.value})}
                                  className="w-full border rounded p-2 text-black"
                                  rows={2}
                                  placeholder="Observações sobre o agendamento..."
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id="notificar_email"
                                  checked={agendamentoForm.notificar_email}
                                  onChange={(e) => setAgendamentoForm({...agendamentoForm, notificar_email: e.target.checked})}
                                  className="h-4 w-4"
                                />
                                <label htmlFor="notificar_email" className="text-sm text-black">
                                  Notificar advogado por email
                                </label>
                              </div>
                              <div className="flex gap-3">
                                <Button type="submit" className="flex-1 bg-green-500 hover:bg-green-600">
                                  {editingAgendamento ? 'Salvar' : 'Criar Agendamento'}
                                </Button>
                                <Button type="button" variant="outline" onClick={() => {
                                  resetAgendamentoForm();
                                  setShowAgendamentoForm(false);
                                }}>
                                  Cancelar
                                </Button>
                              </div>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        // Agrupa agendamentos por dia (YYYY-MM-DD)
                        const byDay = new Map();
                        agendamentos.forEach(ag => {
                          if (!ag.data_hora) return;
                          const d = new Date(ag.data_hora);
                          const key = d.toISOString().split('T')[0];
                          if (!byDay.has(key)) byDay.set(key, []);
                          byDay.get(key).push(ag);
                        });
                        const datesWithEvents = Array.from(byDay.keys()).map(k => {
                          const [y, m, d] = k.split('-').map(Number);
                          return new Date(y, m - 1, d);
                        });

                        // Resumo > 30 dias
                        const now = new Date();
                        const in30 = new Date(); in30.setDate(in30.getDate() + 30);
                        const farAhead = agendamentos
                          .filter(ag => ag.data_hora && new Date(ag.data_hora) > in30 && ag.status === 'pendente')
                          .sort((a, b) => new Date(a.data_hora) - new Date(b.data_hora));

                        // Dia selecionado → lista
                        const selKey = agendaSelectedDay ? new Date(agendaSelectedDay.getTime() - agendaSelectedDay.getTimezoneOffset() * 60000).toISOString().split('T')[0] : null;
                        const dayEvents = selKey ? (byDay.get(selKey) || []) : [];

                        return (
                          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
                            {/* Calendário */}
                            <div className="border rounded-lg p-3 bg-white" data-testid="agenda-calendar">
                              <ShadCalendar
                                mode="single"
                                selected={agendaSelectedDay}
                                onSelect={(date) => {
                                  if (!date) return;
                                  setAgendaSelectedDay(date);
                                  const k = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
                                  if (byDay.has(k)) setAgendaDayDialogOpen(true);
                                }}
                                modifiers={{ hasEvents: datesWithEvents }}
                                modifiersClassNames={{
                                  hasEvents: "bg-green-500 text-white font-bold hover:bg-green-600 focus:bg-green-600"
                                }}
                                className="rounded-md"
                              />
                              <div className="mt-3 text-xs flex items-center gap-2">
                                <span className="inline-block h-3 w-3 rounded-full bg-green-500"></span>
                                <span className="text-gray-700">Dias com agendamento</span>
                              </div>
                            </div>

                            {/* Painel lateral — Próximos 30 dias + Resumo > 30 dias */}
                            <div className="space-y-4">
                              {/* Próximos 30 dias */}
                              <div>
                                <h3 className="font-bold text-black mb-2">Próximos 30 dias</h3>
                                <div className="space-y-2 max-h-[350px] overflow-y-auto">
                                  {(() => {
                                    const upcoming = agendamentos
                                      .filter(ag => {
                                        if (!ag.data_hora) return false;
                                        const dt = new Date(ag.data_hora);
                                        return dt >= new Date(now.getFullYear(), now.getMonth(), now.getDate()) && dt <= in30;
                                      })
                                      .sort((a, b) => new Date(a.data_hora) - new Date(b.data_hora));
                                    if (upcoming.length === 0) return <p className="text-sm text-gray-500">Nenhum agendamento nos próximos 30 dias.</p>;
                                    return upcoming.map(ag => (
                                      <div
                                        key={ag.id}
                                        className={`border-l-4 p-3 rounded bg-gray-50 hover:bg-gray-100 cursor-pointer ${ag.tipo === 'audiencia' ? 'border-l-blue-500' : ag.tipo === 'consultar_social' ? 'border-l-teal-500' : 'border-l-orange-500'}`}
                                        onClick={() => {
                                          const d = new Date(ag.data_hora);
                                          setAgendaSelectedDay(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
                                          setAgendaDayDialogOpen(true);
                                        }}
                                        data-testid={`upcoming-agenda-${ag.id}`}
                                      >
                                        <div className="flex items-center gap-2 mb-1">
                                          <Badge className={ag.tipo === 'audiencia' ? 'bg-blue-100 text-blue-800' : ag.tipo === 'consultar_social' ? 'bg-teal-100 text-teal-800' : 'bg-orange-100 text-orange-800'}>
                                            {getTipoAgendamentoLabel(ag.tipo)}
                                          </Badge>
                                          <span className="text-xs font-bold text-black">{new Date(ag.data_hora).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                        <p className="text-sm font-bold text-black">{ag.titulo}</p>
                                        <p className="text-xs text-gray-600">{ag.client_name} • {ag.process_protocolo}</p>
                                      </div>
                                    ));
                                  })()}
                                </div>
                              </div>

                              {/* Resumo > 30 dias */}
                              <div className="border-t pt-4">
                                <h3 className="font-bold text-black mb-2 flex items-center gap-2">
                                  <AlertCircle className="h-4 w-4 text-amber-600" />
                                  Agendamentos futuros (+30 dias) — {farAhead.length}
                                </h3>
                                <p className="text-xs text-gray-600 mb-2">Para não se perder na organização.</p>
                                <div className="space-y-2 max-h-[250px] overflow-y-auto" data-testid="agenda-far-ahead">
                                  {farAhead.length === 0 ? (
                                    <p className="text-sm text-gray-500">Nenhum agendamento após 30 dias.</p>
                                  ) : farAhead.map(ag => (
                                    <div
                                      key={ag.id}
                                      className="border border-amber-200 bg-amber-50 p-2 rounded cursor-pointer hover:bg-amber-100"
                                      onClick={() => {
                                        const d = new Date(ag.data_hora);
                                        setAgendaSelectedDay(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
                                        setAgendaDayDialogOpen(true);
                                      }}
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-bold text-black">{new Date(ag.data_hora).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>
                                        <Badge className={ag.tipo === 'audiencia' ? 'bg-blue-100 text-blue-800' : ag.tipo === 'consultar_social' ? 'bg-teal-100 text-teal-800' : 'bg-orange-100 text-orange-800'}>
                                          {getTipoAgendamentoLabel(ag.tipo)}
                                        </Badge>
                                      </div>
                                      <p className="text-xs text-black">{ag.titulo}</p>
                                      <p className="text-xs text-gray-600">{ag.client_name} • {ag.process_protocolo}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            {/* Dialog com agendamentos do dia selecionado */}
            <Dialog open={agendaDayDialogOpen} onOpenChange={setAgendaDayDialogOpen}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto w-[95vw]" data-testid="agenda-day-dialog">
                <DialogHeader>
                  <DialogTitle className="text-black font-bold">
                    Agendamentos — {agendaSelectedDay ? agendaSelectedDay.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : ""}
                  </DialogTitle>
                </DialogHeader>
                {(() => {
                  if (!agendaSelectedDay) return null;
                  const k = new Date(agendaSelectedDay.getTime() - agendaSelectedDay.getTimezoneOffset() * 60000).toISOString().split('T')[0];
                  const evs = agendamentos
                    .filter(ag => ag.data_hora && new Date(ag.data_hora).toISOString().split('T')[0] === k)
                    .sort((a, b) => new Date(a.data_hora) - new Date(b.data_hora));
                  if (evs.length === 0) return <p className="text-gray-500 py-4">Nenhum agendamento neste dia.</p>;
                  return (
                    <div className="space-y-3">
                      {evs.map(ag => (
                        <div key={ag.id} className={`border-l-4 p-3 rounded ${ag.tipo === 'audiencia' ? 'border-l-blue-500 bg-blue-50' : ag.tipo === 'consultar_social' ? 'border-l-teal-500 bg-teal-50' : 'border-l-orange-500 bg-orange-50'}`}>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <Badge className={ag.tipo === 'audiencia' ? 'bg-blue-100 text-blue-800' : ag.tipo === 'consultar_social' ? 'bg-teal-100 text-teal-800' : 'bg-orange-100 text-orange-800'}>
                                {getTipoAgendamentoLabel(ag.tipo)}
                              </Badge>
                              <Badge className={getStatusAgendamentoColor(ag.status)}>
                                {ag.status === 'pendente' ? 'Pendente' : ag.status === 'realizado' ? 'Realizado' : 'Cancelado'}
                              </Badge>
                              <span className="text-sm font-bold text-black">
                                {new Date(ag.data_hora).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                          <h4 className="font-bold text-black">{ag.titulo}</h4>
                          <p className="text-sm text-gray-700"><strong>Processo:</strong> {ag.process_protocolo} — {ag.client_name}</p>
                          {ag.local && <p className="text-sm text-gray-700"><strong>Local:</strong> {ag.local}</p>}
                          {ag.advogado_responsavel_nome && (
                            <p className="text-sm text-gray-700"><strong>Advogado:</strong> {ag.advogado_responsavel_nome}{ag.notificado && <span className="text-green-600 ml-2">✓ Notificado</span>}</p>
                          )}
                          {ag.descricao && <p className="text-sm text-gray-700 mt-1">{ag.descricao}</p>}
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {ag.status === 'pendente' && (
                              <>
                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => updateAgendamentoStatus(ag.id, 'realizado')}>
                                  <Check className="h-4 w-4 mr-1" /> Realizado
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => updateAgendamentoStatus(ag.id, 'cancelado')}>
                                  <X className="h-4 w-4 mr-1" /> Cancelar
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="outline" onClick={() => { setAgendaDayDialogOpen(false); startEditAgendamento(ag); }}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => { if (window.confirm("Excluir este agendamento?")) deleteAgendamento(ag.id); }}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </DialogContent>
            </Dialog>

            {/* Dialog de Parcelamento Honorário/RPV */}
            <Dialog open={!!paymentDialog.open} onOpenChange={(open) => !open && setPaymentDialog({ open: false })}>
              <DialogContent className="max-w-md w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="payment-dialog">
                <DialogHeader>
                  <DialogTitle className="text-black font-bold">Registrar Pagamento</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-700">{paymentDialog.label}</p>
                    <p className="font-bold text-lg text-black">{formatCurrency(paymentDialog.valor || 0)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-black block mb-1">Será parcelado?</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={(paymentDialog.parcelas || 1) === 1}
                          onChange={() => setPaymentDialog({ ...paymentDialog, parcelas: 1 })}
                          data-testid="pay-not-parceled"
                        />
                        <span className="text-black">Não (recebido à vista)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={(paymentDialog.parcelas || 1) > 1}
                          onChange={() => setPaymentDialog({ ...paymentDialog, parcelas: 2 })}
                          data-testid="pay-parceled"
                        />
                        <span className="text-black">Sim (parcelar)</span>
                      </label>
                    </div>
                  </div>
                  {(paymentDialog.parcelas || 1) === 1 && (
                    <>
                      <div>
                        <label className="text-sm font-bold text-black block mb-1">Data de Previsão do Pagamento *</label>
                        <Input
                          type="date"
                          value={paymentDialog.first_due_date}
                          onChange={(e) => setPaymentDialog({ ...paymentDialog, first_due_date: e.target.value })}
                          className="text-black"
                          data-testid="pay-vista-due-date"
                        />
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-sm text-yellow-900">
                        <p className="font-semibold">⚠ Importante</p>
                        <p className="text-xs mt-1">Será gerada uma <strong>venda pendente</strong> em &quot;Vendas&quot; com a data acima. O valor <strong>NÃO entra no saldo</strong> até você marcar como pago em Vendas.</p>
                      </div>
                    </>
                  )}
                  {(paymentDialog.parcelas || 1) > 1 && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-bold text-black block mb-1">Nº de Parcelas *</label>
                          <Input
                            type="number"
                            min={2}
                            max={60}
                            value={paymentDialog.parcelas}
                            onChange={(e) => setPaymentDialog({ ...paymentDialog, parcelas: parseInt(e.target.value) || 2 })}
                            className="text-black"
                            data-testid="pay-parcelas-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-bold text-black block mb-1">Intervalo (dias)</label>
                          <Input
                            type="number"
                            min={1}
                            value={paymentDialog.interval_days}
                            onChange={(e) => setPaymentDialog({ ...paymentDialog, interval_days: parseInt(e.target.value) || 30 })}
                            className="text-black"
                            data-testid="pay-interval-input"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-bold text-black block mb-1">1º Vencimento *</label>
                        <Input
                          type="date"
                          value={paymentDialog.first_due_date}
                          onChange={(e) => setPaymentDialog({ ...paymentDialog, first_due_date: e.target.value })}
                          className="text-black"
                          data-testid="pay-first-due-date"
                        />
                      </div>
                      <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm text-blue-900">
                        <p><strong>{paymentDialog.parcelas}x de {formatCurrency(((paymentDialog.valor || 0) / Math.max(1, paymentDialog.parcelas || 1)))}</strong></p>
                        <p className="text-xs mt-1">As parcelas ficarão pendentes e serão marcadas como pagas à medida que forem recebidas.</p>
                      </div>
                    </>
                  )}
                  <div className="flex gap-3 pt-2">
                    <Button className="flex-1 bg-green-500 hover:bg-green-600 text-white" onClick={confirmPaymentDialog} data-testid="pay-confirm-btn">
                      {(paymentDialog.parcelas || 1) > 1 ? "Criar Parcelas" : "Registrar Venda Pendente"}
                    </Button>
                    <Button variant="outline" onClick={() => setPaymentDialog({ open: false })}>Cancelar</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>


            {/* Dialog de Detalhes do Processo */}
            <Dialog open={!!selectedLegalProcess} onOpenChange={(open) => !open && setSelectedLegalProcess(null)}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw]">
                <DialogHeader>
                  <DialogTitle className="text-black font-bold text-xl">
                    Processo: {selectedLegalProcess?.numero_protocolo}
                  </DialogTitle>
                </DialogHeader>
                {selectedLegalProcess && (
                  <div className="space-y-6">
                    {/* Badge Categoria */}
                    <div className="flex items-center gap-2">
                      <Badge className={selectedLegalProcess.categoria === "administrativo" ? "bg-blue-100 text-blue-800 border border-blue-300" : "bg-purple-100 text-purple-800 border border-purple-300"}>
                        {selectedLegalProcess.categoria === "administrativo" ? "Processo Administrativo" : "Processo Judicial"}
                      </Badge>
                      <span className="text-gray-500">•</span>
                      <Badge variant="outline">{getTipoProcessoLabel(selectedLegalProcess.tipo_processo)}</Badge>
                    </div>

                    {/* Informações do Processo */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Cliente</p>
                        <p className="font-bold text-black">{selectedLegalProcess.client_name}</p>
                      </div>
                      {selectedLegalProcess.categoria === "administrativo" && selectedLegalProcess.valor_processo ? (
                        <div>
                          <p className="text-sm text-gray-600">Valor do Processo</p>
                          <p className="font-bold text-black">{formatCurrency(selectedLegalProcess.valor_processo)}</p>
                        </div>
                      ) : null}
                      <div>
                        <p className="text-sm text-gray-600">{selectedLegalProcess.categoria === "administrativo" ? "Honorário (30%)" : "Honorário"}</p>
                        <p className="font-bold text-black">{formatCurrency(selectedLegalProcess.valor_honorario)}</p>
                      </div>
                      {/* Andamento só aparece em Administrativo (foi removido do Judicial) */}
                      {selectedLegalProcess.categoria === "administrativo" && (
                        <div>
                          <p className="text-sm text-gray-600">Andamento</p>
                          <Badge className={getAndamentoColor(selectedLegalProcess.andamento)}>
                            {getAndamentoLabel(selectedLegalProcess.andamento)}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Etapa Atual — APENAS Judicial (para Admin é "Status Interno" que foi removido) */}
                    {selectedLegalProcess.categoria === "judicial" && selectedLegalProcess.etapa_atual && selectedLegalProcess.etapa_atual !== 'nenhuma' && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-600 font-bold">ETAPA ATUAL DO PROCESSO</p>
                        <p className="text-xl font-bold text-blue-800">{getEtapaAtualLabel(selectedLegalProcess.etapa_atual)}</p>
                      </div>
                    )}

                    {/* Honorário Administrativo — status de pagamento */}
                    {selectedLegalProcess.categoria === "administrativo" && (parseFloat(selectedLegalProcess.valor_honorario) || 0) > 0 && (
                      <div className={`p-4 rounded-lg border ${selectedLegalProcess.honorario_pago ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'}`}>
                        <p className="text-sm font-bold text-black">HONORÁRIO</p>
                        <p className="text-lg font-bold">{formatCurrency(selectedLegalProcess.valor_honorario)}</p>
                        {selectedLegalProcess.honorario_pago ? (
                          <p className="text-sm text-green-700 font-bold">✓ Pago em {selectedLegalProcess.honorario_paid_date ? new Date(selectedLegalProcess.honorario_paid_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-'} (lançado em Entradas)</p>
                        ) : (
                          <p className="text-sm text-orange-700 font-bold">⏳ Aguardando pagamento</p>
                        )}
                      </div>
                    )}

                    {/* RPV Judicial */}
                    {selectedLegalProcess.categoria === "judicial" && (parseFloat(selectedLegalProcess.rpv_valor) || 0) > 0 && (
                      <div className={`p-4 rounded-lg border ${selectedLegalProcess.rpv_recebido ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'}`}>
                        <p className="text-sm font-bold text-black">RPV — Requisição de Pequeno Valor</p>
                        <p className="text-lg font-bold">{formatCurrency(selectedLegalProcess.rpv_valor)}</p>
                        {selectedLegalProcess.rpv_protocolo && <p className="text-sm text-gray-700">Protocolo: {selectedLegalProcess.rpv_protocolo}</p>}
                        {selectedLegalProcess.rpv_recebido ? (
                          <p className="text-sm text-green-700 font-bold">✓ Recebida em {selectedLegalProcess.rpv_paid_date ? new Date(selectedLegalProcess.rpv_paid_date).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-'} (lançada em Entradas)</p>
                        ) : (
                          <p className="text-sm text-orange-700 font-bold">⏳ Aguardando recebimento</p>
                        )}
                      </div>
                    )}

                    {/* Detalhamento */}
                    {selectedLegalProcess.detalhamento && (
                      <div className="border-t pt-4">
                        <h4 className="font-bold text-black mb-2">Detalhamento do Processo</h4>
                        <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedLegalProcess.detalhamento}</p>
                      </div>
                    )}

                    {/* Dados do Benefício — só Admin (Judicial não tem mais andamento=concedido) */}
                    {selectedLegalProcess.categoria === "administrativo" && selectedLegalProcess.andamento === 'concedido' && (selectedLegalProcess.prazo_inicio || selectedLegalProcess.prazo_final) && (
                      <div className="border-t pt-4">
                        <h4 className="font-bold text-black mb-2">Dados do Benefício Concedido</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Prazo Início</p>
                            <p className="text-black">{selectedLegalProcess.prazo_inicio ? new Date(selectedLegalProcess.prazo_inicio).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Prazo Final</p>
                            <p className="text-black">{selectedLegalProcess.prazo_final ? new Date(selectedLegalProcess.prazo_final).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Prorrogação</p>
                            <p className="text-black">{selectedLegalProcess.status_prorrogacao || 'Sem prorrogação'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Etapas do Processo e Observações foram removidas conforme solicitação */}

                    {/* Documentos */}
                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-bold text-black">Documentos Anexados</h4>
                        <div className="flex gap-2">
                          <Select onValueChange={(type) => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
                            input.onchange = (e) => {
                              const file = e.target.files[0];
                              if (file) uploadLegalDocument(selectedLegalProcess.id, type, file);
                            };
                            input.click();
                          }}>
                            <SelectTrigger className="w-48 text-black">
                              <SelectValue placeholder="Anexar documento..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="comprovante">Comprovante</SelectItem>
                              <SelectItem value="laudo">Laudo Médico</SelectItem>
                              <SelectItem value="documento_pessoal">Documento Pessoal</SelectItem>
                              <SelectItem value="procuracao">Procuração</SelectItem>
                              <SelectItem value="sentenca">Sentença</SelectItem>
                              <SelectItem value="despacho">Despacho</SelectItem>
                              <SelectItem value="outros">Outros</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {selectedLegalProcess.documents?.length > 0 ? (
                          selectedLegalProcess.documents.map((doc) => (
                            <div key={doc.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                              <div>
                                <p className="font-medium text-black">{doc.filename}</p>
                                <p className="text-xs text-gray-500">
                                  Tipo: {doc.document_type} | Enviado por: {doc.uploaded_by} | {new Date(doc.uploaded_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => downloadLegalDocument(selectedLegalProcess.id, doc.id, doc.filename)}>
                                  <FileText className="h-4 w-4 mr-1" /> Download
                                </Button>
                                <Button variant="destructive" size="sm" onClick={() => deleteLegalDocument(selectedLegalProcess.id, doc.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-center py-4">Nenhum documento anexado</p>
                        )}
                      </div>
                    </div>

                    {/* Observações removida conforme solicitação */}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Relatórios */}
          <TabsContent value="reports">
            <Reports 
              transactions={transactions}
              materials={materials}
              materialUsage={materialUsage}
              clients={clients}
            />
          </TabsContent>

          {hasAdminAccess && (
            <TabsContent value="admin">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-black font-bold text-xl">Administração</CardTitle>
                    <Dialog open={showUserForm} onOpenChange={(open) => {
                      if (!open) cancelEditUser();
                      else setShowUserForm(open);
                    }}>
                      <DialogTrigger asChild>
                        <Button className="bg-blue-500 hover:bg-blue-600">
                          <UserPlus className="h-4 w-4 mr-2" />
                          Novo Usuário
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw]">
                        <DialogHeader>
                          <DialogTitle className="text-black text-xl font-bold">
                            {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
                          </DialogTitle>
                          <DialogDescription className="text-gray-600">
                            {editingUser 
                              ? `Editando permissões de ${editingUser.name}`
                              : 'Preencha os dados e selecione as permissões do novo usuário'
                            }
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleUserSubmit} className="space-y-6">
                          {!editingUser && (
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-bold text-black mb-1 block">Nome Completo *</label>
                                  <Input
                                    placeholder="Nome completo"
                                    value={userForm.name}
                                    onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                                    required
                                    className="text-black font-medium"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-bold text-black mb-1 block">Email *</label>
                                  <Input
                                    type="email"
                                    placeholder="Email"
                                    value={userForm.email}
                                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                                    required
                                    className="text-black font-medium"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-bold text-black mb-1 block">Senha *</label>
                                  <Input
                                    type="password"
                                    placeholder="Senha"
                                    value={userForm.password}
                                    onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                                    required={!editingUser}
                                    className="text-black font-medium"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-bold text-black mb-1 block">Tipo de Usuário *</label>
                                  <Select 
                                    value={userForm.role} 
                                    onValueChange={(value) => setUserForm({...userForm, role: value})}
                                  >
                                    <SelectTrigger className="text-black font-medium">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="user">Usuário (Acesso Limitado)</SelectItem>
                                      <SelectItem value="manager">Gerente (Acesso Intermediário)</SelectItem>
                                      <SelectItem value="admin">Administrador (Acesso Total + Dashboard)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </>
                          )}
                          
                          {editingUser && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-black font-bold">{editingUser.name}</p>
                              <p className="text-gray-600">{editingUser.email}</p>
                              <div className="mt-2">
                                <label className="text-sm font-bold text-black mb-1 block">Tipo de Usuário</label>
                                <Select 
                                  value={userForm.role} 
                                  onValueChange={(value) => setUserForm({...userForm, role: value})}
                                >
                                  <SelectTrigger className="text-black font-medium w-64">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="user">Usuário (Acesso Limitado)</SelectItem>
                                    <SelectItem value="manager">Gerente (Acesso Intermediário)</SelectItem>
                                    <SelectItem value="admin">Administrador (Acesso Total + Dashboard)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}

                          <div>
                            <div className="flex justify-between items-center mb-3">
                              <label className="text-lg font-bold text-black">Permissões do Usuário</label>
                              <div className="flex gap-2">
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const allCodes = availablePermissions.map(p => p.code);
                                    console.log("Selecting all:", allCodes);
                                    setUserForm(prev => ({...prev, permissions: allCodes}));
                                  }}
                                >
                                  Selecionar Todas
                                </Button>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log("Clearing all permissions");
                                    setUserForm(prev => ({...prev, permissions: []}));
                                  }}
                                >
                                  Limpar Todas
                                </Button>
                              </div>
                            </div>
                            
                            <div className="border rounded-lg p-4 max-h-[400px] overflow-y-auto bg-white">
                              {/* Agrupar permissões por categoria */}
                              {Object.entries(
                                availablePermissions.reduce((acc, perm) => {
                                  const category = perm.category || perm.module || 'Outros';
                                  if (!acc[category]) acc[category] = [];
                                  acc[category].push(perm);
                                  return acc;
                                }, {})
                              ).map(([category, permissions]) => (
                                <div key={category} className="mb-4">
                                  <h4 className="font-bold text-black text-base bg-gray-100 p-2 rounded mb-2 sticky top-0">
                                    {category}
                                  </h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-2">
                                    {permissions.map((permission) => (
                                      <div 
                                        key={permission.code} 
                                        className={`flex items-start space-x-3 p-2 rounded cursor-pointer transition-colors ${
                                          userForm.permissions?.includes(permission.code) 
                                            ? 'bg-blue-50 border border-blue-200' 
                                            : 'hover:bg-gray-50 border border-transparent'
                                        }`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handlePermissionToggle(permission.code);
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          id={`perm-${permission.code}`}
                                          checked={userForm.permissions?.includes(permission.code) || false}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handlePermissionToggle(permission.code);
                                          }}
                                          className="mt-1 h-4 w-4 cursor-pointer"
                                        />
                                        <div className="flex-1">
                                          <label 
                                            htmlFor={`perm-${permission.code}`}
                                            className="text-sm font-semibold text-black cursor-pointer block"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {permission.name}
                                          </label>
                                          <span className="text-xs text-gray-500">
                                            {permission.description}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            <p className="text-sm text-gray-500 mt-2">
                              <strong>{userForm.permissions.length}</strong> permissão(ões) selecionada(s)
                            </p>
                          </div>
                          
                          <div className="flex gap-3">
                            <Button type="submit" className="flex-1 bg-green-500 hover:bg-green-600 font-bold">
                              {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
                            </Button>
                            <Button type="button" variant="outline" onClick={cancelEditUser}>
                              Cancelar
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>

                    {/* Phase Services Dialog */}
                    <Dialog open={showServiceForm} onOpenChange={(open) => {
                      setShowServiceForm(open);
                      if (!open) {
                        setEditingService(null);
                        setPhaseServiceForm({
                          service_name: "",
                          description: "",
                          unit: "horas",
                          quantity: "",
                          unit_price: "",
                          supplier_name: "",
                          observations: ""
                        });
                      }
                    }}>
                      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto w-[95vw]">
                        <DialogHeader>
                          <DialogTitle className="text-black">
                            {editingService ? 'Editar' : 'Adicionar'} Serviço - {selectedPhaseForService?.phase_name}
                          </DialogTitle>
                          <DialogDescription>
                            Gerencie os serviços necessários para esta fase do projeto.
                          </DialogDescription>
                        </DialogHeader>

                        {/* Service Form */}
                        <form onSubmit={handlePhaseServiceSubmit} className="space-y-4 mb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nome do Serviço *
                              </label>
                              <Input
                                placeholder="Ex: Pedreiro, Eletricista, etc."
                                value={phaseServiceForm.service_name}
                                onChange={(e) => setPhaseServiceForm({...phaseServiceForm, service_name: e.target.value})}
                                required
                                className="text-black"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Fornecedor/Prestador
                              </label>
                              <Input
                                placeholder="Nome da empresa ou pessoa"
                                value={phaseServiceForm.supplier_name}
                                onChange={(e) => setPhaseServiceForm({...phaseServiceForm, supplier_name: e.target.value})}
                                className="text-black"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Descrição
                            </label>
                            <Textarea
                              placeholder="Descrição detalhada do serviço..."
                              value={phaseServiceForm.description}
                              onChange={(e) => setPhaseServiceForm({...phaseServiceForm, description: e.target.value})}
                              className="text-black"
                              rows={2}
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Unidade de Medida *
                              </label>
                              <Select value={phaseServiceForm.unit} onValueChange={(value) => setPhaseServiceForm({...phaseServiceForm, unit: value})}>
                                <SelectTrigger className="text-black">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="horas">Horas</SelectItem>
                                  <SelectItem value="diarias">Diárias</SelectItem>
                                  <SelectItem value="m²">m² (metro quadrado)</SelectItem>
                                  <SelectItem value="m³">m³ (metro cúbico)</SelectItem>
                                  <SelectItem value="unidade">Unidade</SelectItem>
                                  <SelectItem value="projeto">Por Projeto</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quantidade *
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0"
                                value={phaseServiceForm.quantity}
                                onChange={(e) => setPhaseServiceForm({...phaseServiceForm, quantity: e.target.value})}
                                required
                                className="text-black"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Preço Unitário (R$) *
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0,00"
                                value={phaseServiceForm.unit_price}
                                onChange={(e) => setPhaseServiceForm({...phaseServiceForm, unit_price: e.target.value})}
                                required
                                className="text-black"
                              />
                            </div>
                          </div>

                          {phaseServiceForm.quantity && phaseServiceForm.unit_price && (
                            <div className="p-3 bg-green-50 rounded-md">
                              <p className="text-sm text-green-700">
                                <strong>Total estimado:</strong> {formatCurrency(parseFloat(phaseServiceForm.quantity || 0) * parseFloat(phaseServiceForm.unit_price || 0))}
                              </p>
                            </div>
                          )}

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Observações
                            </label>
                            <Textarea
                              placeholder="Observações adicionais, termos especiais, etc."
                              value={phaseServiceForm.observations}
                              onChange={(e) => setPhaseServiceForm({...phaseServiceForm, observations: e.target.value})}
                              className="text-black"
                              rows={2}
                            />
                          </div>

                          <div className="flex gap-2">
                            <Button type="submit" className="flex-1">
                              <Save className="h-4 w-4 mr-2" />
                              {editingService ? 'Atualizar' : 'Adicionar'} Serviço
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setShowServiceForm(false);
                                setEditingService(null);
                              }}
                            >
                              Cancelar
                            </Button>
                          </div>
                        </form>

                        {/* Services List */}
                        {phaseServices.length > 0 && (
                          <div className="border-t pt-4">
                            <h3 className="font-semibold mb-4 text-gray-900">Serviços desta Fase:</h3>
                            <div className="space-y-3">
                              {phaseServices.map((service) => (
                                <div key={service.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">{service.service_name}</div>
                                    <div className="text-sm text-gray-600">
                                      {service.quantity} {service.unit} × {formatCurrency(service.unit_price)} = <strong>{formatCurrency(service.total_cost)}</strong>
                                    </div>
                                    {service.supplier_name && (
                                      <div className="text-xs text-gray-500">Fornecedor: {service.supplier_name}</div>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => startEditPhaseServiceItem(service)}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => deleteService(service.id)}
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-black">Usuários da Empresa</h3>
                    <div className="grid gap-4">
                      {companyUsers.map((companyUser) => (
                        <Card key={companyUser.id} className="border-l-4 border-l-blue-500">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-bold text-black text-lg">{companyUser.name}</h4>
                                <p className="text-sm text-gray-700 font-medium">{companyUser.email}</p>
                                <div className="flex gap-2 mt-2">
                                  <Badge 
                                    variant="outline" 
                                    className={`font-bold ${
                                      companyUser.role === 'admin' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                                      companyUser.role === 'manager' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                      'bg-gray-100 text-gray-800 border-gray-300'
                                    }`}
                                  >
                                    {companyUser.role === 'admin' ? 'Administrador' : 
                                     companyUser.role === 'manager' ? 'Gerente' : 'Usuário'}
                                  </Badge>
                                  <Badge 
                                    variant={companyUser.is_active ? "default" : "secondary"}
                                    className={`font-bold ${companyUser.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                                  >
                                    {companyUser.is_active ? "Ativo" : "Inativo"}
                                  </Badge>
                                </div>
                                
                                {/* Lista de Permissões Atribuídas */}
                                <div className="mt-3">
                                  <p className="text-sm font-bold text-black mb-2">
                                    Permissões Atribuídas ({companyUser.permissions?.length || 0}):
                                  </p>
                                  {companyUser.permissions && companyUser.permissions.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                                      {/* Agrupar e mostrar permissões por categoria */}
                                      {Object.entries(
                                        (companyUser.permissions || []).reduce((acc, permCode) => {
                                          const permInfo = availablePermissions.find(p => p.code === permCode);
                                          if (permInfo) {
                                            const category = permInfo.category || 'Outros';
                                            if (!acc[category]) acc[category] = [];
                                            acc[category].push(permInfo);
                                          }
                                          return acc;
                                        }, {})
                                      ).map(([category, perms]) => (
                                        <div key={category} className="w-full mb-2">
                                          <span className="text-xs font-bold text-gray-600 block mb-1">{category}:</span>
                                          <div className="flex flex-wrap gap-1">
                                            {perms.map((perm) => (
                                              <Badge 
                                                key={perm.code} 
                                                variant="outline" 
                                                className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                                              >
                                                {perm.name}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-red-500 italic">Nenhuma permissão atribuída</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className={`font-bold ${companyUser.is_active ? "border-red-500 text-red-600 hover:bg-red-50" : "border-green-500 text-green-600 hover:bg-green-50"}`}
                                  onClick={async () => {
                                    const action = companyUser.is_active ? "desativar" : "ativar";
                                    if (!window.confirm(`Confirmar ${action} o usuário "${companyUser.name}"?`)) return;
                                    try {
                                      await axios.patch(`${API}/companies/${selectedCompany.id}/users/${companyUser.id}/toggle-active`);
                                      toast.success(`Usuário ${action === "desativar" ? "desativado" : "ativado"}`);
                                      fetchData && fetchData();
                                      const r = await axios.get(`${API}/companies/${selectedCompany.id}/users`);
                                      setCompanyUsers(r.data || []);
                                    } catch (e) {
                                      toast.error("Erro: " + (e.response?.data?.detail || ""));
                                    }
                                  }}
                                  data-testid={`toggle-active-${companyUser.id}`}
                                >
                                  {companyUser.is_active ? "Desativar" : "Ativar"}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="font-bold border-blue-500 text-blue-600 hover:bg-blue-50"
                                  onClick={() => startEditUser(companyUser)}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar Permissões
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
        </main>
      </div>

      {/* Dialog: Pedido de Material/Serviço (montado fora das Tabs para edição funcionar de qualquer aba) */}
      <Dialog open={showMaterialOrderForm} onOpenChange={(open) => {
        setShowMaterialOrderForm(open);
        if (!open) {
          setEditingMaterialOrder(false);
          setMaterialOrderForm({
            project_id: "",
            phase_id: "",
            supplier_id: "",
            observations: "",
            kind: "material",
            destination: "obra",
            items: [{ id: Date.now().toString(), description: "", unit: "unidade", quantity: "", unit_price: "" }]
          });
          setMoProjectSearch("");
          setMoSupplierSearch("");
          setMoProjectDropdownOpen(false);
          setMoSupplierDropdownOpen(false);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw]">
          <DialogHeader>
            <DialogTitle className="text-black">
              {editingMaterialOrder
                ? `Editar Pedido${editingMaterialOrder?.order_number ? ` #${String(editingMaterialOrder.order_number).padStart(4, '0')}` : ''}`
                : 'Novo Pedido'} de {materialOrderForm.kind === "servico" ? "Serviço" : "Material"}
            </DialogTitle>
            <DialogDescription>
              {editingMaterialOrder
                ? 'Edite os dados do pedido'
                : 'Crie um pedido vinculado a um projeto e medição'
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingMaterialOrder ? updateMaterialOrder : handleMaterialOrderSubmit} className="space-y-4">
            <div className="bg-gray-50 border rounded-lg p-3">
              <label className="text-sm font-bold text-black block mb-2">Tipo do Pedido *</label>
              <div className="flex gap-6 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={(materialOrderForm.kind || "material") === "material"}
                    onChange={() => {
                      const newItems = materialOrderForm.items.map(i => ({...i, unit: i.unit && !["hora","diaria","empreitada","mensal"].includes(i.unit) ? i.unit : "unidade"}));
                      setMaterialOrderForm({...materialOrderForm, kind: "material", items: newItems});
                    }}
                    data-testid="mo-kind-material"
                  />
                  <span className="text-black font-medium">Material</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={materialOrderForm.kind === "servico"}
                    onChange={() => {
                      const newItems = materialOrderForm.items.map(i => ({...i, unit: ["hora","diaria","empreitada","mensal"].includes(i.unit) ? i.unit : "hora"}));
                      setMaterialOrderForm({...materialOrderForm, kind: "servico", items: newItems});
                    }}
                    data-testid="mo-kind-servico"
                  />
                  <span className="text-black font-medium">Serviço</span>
                </label>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {materialOrderForm.kind === "servico"
                  ? "Itens serão lançados como Serviço na medição (unidades em hora, diária, empreitada, etc.)"
                  : "Itens serão lançados como Material na medição."}
              </p>
            </div>

            {/* Destino do Pedido: Obra ou Estoque */}
            <div className="bg-gray-50 border rounded-lg p-3">
              <label className="text-sm font-bold text-black block mb-2">Destino *</label>
              <div className="flex gap-6 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={(materialOrderForm.destination || "obra") === "obra"}
                    onChange={() => setMaterialOrderForm({...materialOrderForm, destination: "obra"})}
                    data-testid="mo-dest-obra"
                  />
                  <span className="text-black font-medium">Obra com Medição</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={materialOrderForm.destination === "estoque"}
                    onChange={() => setMaterialOrderForm({...materialOrderForm, destination: "estoque", project_id: "", phase_id: ""})}
                    data-testid="mo-dest-estoque"
                  />
                  <span className="text-black font-medium">Estoque Central</span>
                </label>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {materialOrderForm.destination === "estoque"
                  ? "Aprovação técnica + financeira → após o financeiro, os itens entram no Estoque Central e a despesa é gerada. A relação com Obra/Medição é feita na SAÍDA do estoque."
                  : "Ao aprovar tecnicamente, os itens são adicionados em Materiais e Serviços Utilizados da medição."}
              </p>
            </div>

            {materialOrderForm.destination !== "estoque" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Projeto *</label>
                  {(() => {
                    const selected = constructionProjects.find(p => p.id === materialOrderForm.project_id);
                    const value = moProjectDropdownOpen ? moProjectSearch : (selected ? selected.name : moProjectSearch);
                    const q = moProjectSearch.toLowerCase().trim();
                    const filtered = constructionProjects.filter(p => p.status === 'em_andamento').filter(p => !q || (p.name || "").toLowerCase().includes(q)).slice(0, 30);
                    return (
                      <>
                        <Input
                          type="text"
                          value={value}
                          placeholder="Buscar projeto por nome..."
                          onChange={(e) => {
                            setMoProjectSearch(e.target.value);
                            setMoProjectDropdownOpen(true);
                            if (materialOrderForm.project_id) setMaterialOrderForm({...materialOrderForm, project_id: "", phase_id: ""});
                          }}
                          onFocus={() => setMoProjectDropdownOpen(true)}
                          onBlur={() => setTimeout(() => setMoProjectDropdownOpen(false), 200)}
                          className="text-black"
                          data-testid="mo-project-search-input"
                        />
                        {moProjectDropdownOpen && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border rounded-md shadow-lg">
                            {filtered.length === 0 ? (
                              <div className="p-3 text-sm text-gray-500">Nenhum projeto em andamento</div>
                            ) : (
                              filtered.map(p => (
                                <button
                                  type="button"
                                  key={p.id}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setMaterialOrderForm({...materialOrderForm, project_id: p.id, phase_id: ""});
                                    setMoProjectSearch("");
                                    setMoProjectDropdownOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-black border-b last:border-b-0"
                                >
                                  <div className="font-medium">{p.name}</div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Medição/Fase *</label>
                  <Select
                    value={materialOrderForm.phase_id}
                    onValueChange={(value) => setMaterialOrderForm({...materialOrderForm, phase_id: value})}
                    disabled={!materialOrderForm.project_id}
                  >
                    <SelectTrigger className="text-black"><SelectValue placeholder="Selecione a medição" /></SelectTrigger>
                    <SelectContent>
                      {allProjectPhases.filter(phase => phase.project_id === materialOrderForm.project_id && phase.status !== 'concluido').map(phase => (
                        <SelectItem key={phase.id} value={phase.id}>{phase.phase_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-1 md:col-span-2 relative">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Fornecedor *</label>
                {(() => {
                  const selected = suppliers.find(s => s.id === materialOrderForm.supplier_id);
                  const value = moSupplierDropdownOpen ? moSupplierSearch : (selected ? `${selected.name}${selected.cnpj || selected.cpf ? ` - ${selected.cnpj || selected.cpf}` : ""}` : moSupplierSearch);
                  const q = moSupplierSearch.toLowerCase().trim();
                  const filtered = suppliers.filter(s => {
                    if (!q) return true;
                    return (s.name || "").toLowerCase().includes(q)
                      || (s.cnpj || "").toLowerCase().includes(q)
                      || (s.cpf || "").toLowerCase().includes(q);
                  }).slice(0, 30);
                  return (
                    <>
                      <Input
                        type="text"
                        value={value}
                        placeholder="Buscar por nome, CNPJ ou CPF..."
                        onChange={(e) => {
                          setMoSupplierSearch(e.target.value);
                          setMoSupplierDropdownOpen(true);
                          if (materialOrderForm.supplier_id) setMaterialOrderForm({...materialOrderForm, supplier_id: ""});
                        }}
                        onFocus={() => setMoSupplierDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setMoSupplierDropdownOpen(false), 200)}
                        className="text-black"
                        data-testid="mo-supplier-search-input"
                      />
                      {moSupplierDropdownOpen && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border rounded-md shadow-lg">
                          {filtered.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500">Nenhum fornecedor encontrado</div>
                          ) : (
                            filtered.map(s => (
                              <button
                                type="button"
                                key={s.id}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setMaterialOrderForm({...materialOrderForm, supplier_id: s.id});
                                  setMoSupplierSearch("");
                                  setMoSupplierDropdownOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-black border-b last:border-b-0"
                              >
                                <div className="font-medium">{s.name}</div>
                                {(s.cnpj || s.cpf) && <div className="text-xs text-gray-500">{s.cnpj || s.cpf}</div>}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Itens do Pedido *</label>
                <Button type="button" size="sm" variant="outline" onClick={addOrderItem}>
                  <Plus className="h-3 w-3 mr-1" /> Adicionar Item
                </Button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
                <div className="grid grid-cols-[24%_14%_10%_14%_14%_18%_6%] gap-2 items-center text-xs font-medium text-gray-600 px-2">
                  <div>Descrição</div><div>Unid.</div><div>Qtd</div><div>Val. Unit.</div><div>Desconto</div><div className="text-right">Total</div><div></div>
                </div>
                {materialOrderForm.items.map((item) => (
                  <div key={item.id} className="grid grid-cols-[24%_14%_10%_14%_14%_18%_6%] gap-2 items-center bg-gray-50 p-2 rounded">
                    <Input placeholder="Descrição" value={item.description} onChange={(e) => updateOrderItem(item.id, 'description', e.target.value)} className="text-black text-sm" />
                    <Select value={item.unit} onValueChange={(value) => updateOrderItem(item.id, 'unit', value)}>
                      <SelectTrigger className="text-black text-sm whitespace-nowrap overflow-hidden [&>span]:truncate min-w-0"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {materialOrderForm.kind === "servico" ? (
                          <>
                            <SelectItem value="hora">Hora</SelectItem>
                            <SelectItem value="diaria">Diária</SelectItem>
                            <SelectItem value="empreitada">Empr.</SelectItem>
                            <SelectItem value="mensal">Mensal</SelectItem>
                            <SelectItem value="m²">m²</SelectItem>
                            <SelectItem value="m³">m³</SelectItem>
                            <SelectItem value="unidade">Un</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="unidade">Un</SelectItem>
                            <SelectItem value="m">m</SelectItem>
                            <SelectItem value="m²">m²</SelectItem>
                            <SelectItem value="m³">m³</SelectItem>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="saco">Saco</SelectItem>
                            <SelectItem value="litro">L</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <Input type="number" step="0.01" placeholder="Qtd" value={item.quantity} onChange={(e) => updateOrderItem(item.id, 'quantity', e.target.value)} className="text-black text-sm" />
                    <Input type="number" step="0.01" placeholder="Valor unit." value={item.unit_price} onChange={(e) => updateOrderItem(item.id, 'unit_price', e.target.value)} className="text-black text-sm" />
                    <Input type="number" step="0.01" placeholder="R$ 0,00" value={item.discount || ""} onChange={(e) => updateOrderItem(item.id, 'discount', e.target.value)} className="text-black text-sm" data-testid={`item-discount-${item.id}`} title="Desconto em R$ sobre o subtotal deste item" />
                    <div className="text-right text-sm font-medium text-black">
                      {formatCurrency(Math.max(((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)) - (parseFloat(item.discount) || 0), 0))}
                    </div>
                    <div className="text-right">
                      {materialOrderForm.items.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" className="text-red-500 p-1" onClick={() => removeOrderItem(item.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-2 text-sm">
                <span className="text-gray-600">Total do Pedido: </span>
                <span className="font-bold text-black ml-2">
                  {formatCurrency(materialOrderForm.items.reduce((sum, item) => sum + Math.max(((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)) - (parseFloat(item.discount) || 0), 0), 0))}
                </span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Observações</label>
              <Textarea placeholder="Observações do pedido..." value={materialOrderForm.observations} onChange={(e) => setMaterialOrderForm({...materialOrderForm, observations: e.target.value})} className="text-black" />
            </div>

            <Button type="submit" className="w-full" disabled={!materialOrderForm.supplier_id || (materialOrderForm.destination !== "estoque" && (!materialOrderForm.project_id || !materialOrderForm.phase_id))}>
              {editingMaterialOrder ? 'Salvar Alterações' : 'Criar Pedido'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>


      {/* Dialog: Nova Saída de Estoque */}
      <Dialog open={showStockExitForm} onOpenChange={setShowStockExitForm}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="stock-exit-dialog">
          <DialogHeader>
            <DialogTitle className="text-black font-bold">Solicitar Saída de Estoque</DialogTitle>
            <DialogDescription>A saída entra como pendente até a aprovação técnica.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitStockExit} className="space-y-3">
            <div>
              <label className="text-sm font-bold text-black block mb-1">Item *</label>
              <Select value={stockExitForm.item_id} onValueChange={(v) => setStockExitForm({...stockExitForm, item_id: v})}>
                <SelectTrigger className="text-black" data-testid="stock-exit-item"><SelectValue placeholder="Selecione o item" /></SelectTrigger>
                <SelectContent>
                  {stockItems.filter(i => (i.quantity || 0) > 0).map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.name} ({i.unit}) — saldo: {i.quantity}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-black block mb-1">Quantidade *</label>
                <Input type="number" step="0.01" value={stockExitForm.quantity} onChange={(e) => setStockExitForm({...stockExitForm, quantity: e.target.value})} className="text-black" required data-testid="stock-exit-qty" />
              </div>
              <div>
                <label className="text-sm font-bold text-black block mb-1">Solicitante *</label>
                <Input value={stockExitForm.requester_name} onChange={(e) => setStockExitForm({...stockExitForm, requester_name: e.target.value})} placeholder="Nome de quem solicita" className="text-black" required data-testid="stock-exit-requester" />
              </div>
              <div>
                <label className="text-sm font-bold text-black block mb-1">Obra *</label>
                <Select value={stockExitForm.project_id} onValueChange={(v) => setStockExitForm({...stockExitForm, project_id: v, phase_id: ""})}>
                  <SelectTrigger className="text-black" data-testid="stock-exit-project"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {constructionProjects.filter(p => p.status === 'em_andamento').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-bold text-black block mb-1">Medição *</label>
                <Select value={stockExitForm.phase_id} onValueChange={(v) => setStockExitForm({...stockExitForm, phase_id: v})} disabled={!stockExitForm.project_id}>
                  <SelectTrigger className="text-black" data-testid="stock-exit-phase"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {allProjectPhases.filter(p => p.project_id === stockExitForm.project_id && p.status !== 'concluido').map(p => <SelectItem key={p.id} value={p.id}>{p.phase_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-bold text-black block mb-1">Observações</label>
              <Textarea value={stockExitForm.observations} onChange={(e) => setStockExitForm({...stockExitForm, observations: e.target.value})} className="text-black" rows={2} />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600" data-testid="stock-exit-submit">Criar Solicitação</Button>
              <Button type="button" variant="outline" onClick={() => setShowStockExitForm(false)}>Cancelar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Ajuste de Estoque */}
      <Dialog open={showStockAdjustForm} onOpenChange={setShowStockAdjustForm}>
        <DialogContent className="max-w-md w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="stock-adjust-dialog">
          <DialogHeader>
            <DialogTitle className="text-black font-bold">Ajuste de Estoque</DialogTitle>
            <DialogDescription>Use para corrigir saldos (avaria, perda, doação, etc.). Use número negativo para saída.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitStockAdjust} className="space-y-3">
            <div>
              <label className="text-sm font-bold text-black block mb-1">Item *</label>
              <Select value={stockAdjustForm.item_id} onValueChange={(v) => setStockAdjustForm({...stockAdjustForm, item_id: v})}>
                <SelectTrigger className="text-black"><SelectValue placeholder="Selecione o item" /></SelectTrigger>
                <SelectContent>
                  {stockItems.map(i => <SelectItem key={i.id} value={i.id}>{i.name} (saldo: {i.quantity})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-bold text-black block mb-1">Quantidade (use negativo para saída) *</label>
              <Input type="number" step="0.01" value={stockAdjustForm.quantity} onChange={(e) => setStockAdjustForm({...stockAdjustForm, quantity: e.target.value})} className="text-black" required />
            </div>
            <div>
              <label className="text-sm font-bold text-black block mb-1">Motivo *</label>
              <Textarea value={stockAdjustForm.reason} onChange={(e) => setStockAdjustForm({...stockAdjustForm, reason: e.target.value})} placeholder="Ex: avaria, perda, contagem física..." rows={2} required className="text-black" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600">Registrar Ajuste</Button>
              <Button type="button" variant="outline" onClick={() => setShowStockAdjustForm(false)}>Cancelar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Item */}
      <Dialog open={showStockEditForm} onOpenChange={setShowStockEditForm}>
        <DialogContent className="max-w-md w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="stock-edit-dialog">
          <DialogHeader><DialogTitle className="text-black font-bold">Editar Item</DialogTitle></DialogHeader>
          {editingStockItem && (
            <form onSubmit={updateStockItem} className="space-y-3">
              <div>
                <label className="text-sm font-bold text-black block mb-1">Nome</label>
                <Input value={editingStockItem.name} onChange={(e) => setEditingStockItem({...editingStockItem, name: e.target.value})} className="text-black" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold text-black block mb-1">Unidade</label>
                  <Input value={editingStockItem.unit} onChange={(e) => setEditingStockItem({...editingStockItem, unit: e.target.value})} className="text-black" />
                </div>
                <div>
                  <label className="text-sm font-bold text-black block mb-1">Valor Unit.</label>
                  <Input type="number" step="0.01" value={editingStockItem.unit_price} onChange={(e) => setEditingStockItem({...editingStockItem, unit_price: e.target.value})} className="text-black" />
                </div>
                <div>
                  <label className="text-sm font-bold text-black block mb-1">Qtd. Mínima</label>
                  <Input type="number" step="0.01" value={editingStockItem.min_quantity} onChange={(e) => setEditingStockItem({...editingStockItem, min_quantity: e.target.value})} className="text-black" />
                </div>
                <div>
                  <label className="text-sm font-bold text-black block mb-1">Fornecedor</label>
                  <Select value={editingStockItem.supplier_id || "none"} onValueChange={(v) => setEditingStockItem({...editingStockItem, supplier_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="text-black"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nenhum —</SelectItem>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" className="flex-1">Salvar</Button>
                <Button type="button" variant="outline" onClick={() => setShowStockEditForm(false)}>Cancelar</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>


      {/* Dialog: Pagar Parcela (vínculo obra/medição) */}
      <Dialog open={!!payingInstallment} onOpenChange={(open) => { if (!open) setPayingInstallment(null); }}>
        <DialogContent className="sm:max-w-md w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="pay-installment-dialog">
          <DialogHeader>
            <DialogTitle>Pagar Parcela</DialogTitle>
            <DialogDescription>
              {payingInstallment && (
                <>
                  Parcela <strong>{payingInstallment.installment_number}</strong>
                  {" — "}
                  <strong>{formatCurrency(payingInstallment.amount || 0)}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-black block mb-1">Conta Bancária que recebe o valor *</label>
              <Select value={payForm.bank_account_id} onValueChange={(v) => setPayForm({ ...payForm, bank_account_id: v })}>
                <SelectTrigger className="text-black" data-testid="pay-installment-bank"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}{b.bank_name ? ` — ${b.bank_name}` : ""} · Saldo: {formatCurrency(b.current_balance)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="border p-3 rounded-lg bg-blue-50">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="pay_is_obra"
                  checked={payForm.is_obra}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setPayForm({ ...payForm, is_obra: checked, project_id: "", phase_id: "" });
                    if (!checked) setPayPhases([]);
                  }}
                  className="h-4 w-4"
                  data-testid="pay-is-obra-checkbox"
                />
                <label htmlFor="pay_is_obra" className="text-sm font-bold text-black">
                  Esta parcela é de obra? (vincular a Obra + Medição)
                </label>
              </div>
              {payForm.is_obra && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">Obra *</label>
                    <Select
                      value={payForm.project_id}
                      onValueChange={async (value) => {
                        setPayForm({ ...payForm, project_id: value, phase_id: "" });
                        try {
                          const res = await axios.get(`${API}/companies/${selectedCompany.id}/construction/projects/${value}/schedule`);
                          setPayPhases(res.data);
                        } catch (err) {
                          console.error("Erro ao carregar medições:", err);
                          setPayPhases([]);
                        }
                      }}
                    >
                      <SelectTrigger className="text-black w-full" data-testid="pay-obra-select">
                        <SelectValue placeholder="Selecione a obra" />
                      </SelectTrigger>
                      <SelectContent className="max-w-[90vw]">
                        {constructionProjects.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            <span className="whitespace-normal break-words block">
                              {p.name}{p.client_name ? ` — ${p.client_name}` : ''}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">Medição *</label>
                    <Select
                      value={payForm.phase_id}
                      onValueChange={(value) => setPayForm({ ...payForm, phase_id: value })}
                      disabled={!payForm.project_id}
                    >
                      <SelectTrigger className="text-black w-full" data-testid="pay-phase-select">
                        <SelectValue placeholder={payForm.project_id ? "Selecione a medição" : "Primeiro selecione a obra"} />
                      </SelectTrigger>
                      <SelectContent className="max-w-[90vw]">
                        {payPhases.length === 0 ? (
                          <SelectItem value="none" disabled>Nenhuma medição cadastrada</SelectItem>
                        ) : (
                          payPhases
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map(phase => (
                              <SelectItem key={phase.id} value={phase.id}>
                                <span className="whitespace-normal break-words block">{phase.phase_name}</span>
                              </SelectItem>
                            ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setPayingInstallment(null)} data-testid="pay-cancel-btn">
                Cancelar
              </Button>
              <Button onClick={confirmPayInstallment} data-testid="pay-confirm-btn" className="bg-green-500 hover:bg-green-600">
                <Check className="h-4 w-4 mr-1" />
                Confirmar Pagamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// AI Insights Component
function AIInsights({ companyId }) {
  const [insights, setInsights] = useState("");
  const [loading, setLoading] = useState(false);

  const getInsights = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/companies/${companyId}/ai/financial-insights`);
      setInsights(response.data.insights);
    } catch (error) {
      toast.error("Erro ao gerar insights");
    } finally {
      setLoading(false);
    }
  };

  const analyzeDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/companies/${companyId}/ai/analyze-documents`);
      setInsights(response.data.analysis);
    } catch (error) {
      toast.error("Erro ao analisar documentos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
          Insights com IA
        </CardTitle>
        <CardDescription>
          Análise inteligente dos seus dados financeiros
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4">
          <Button 
            onClick={getInsights} 
            disabled={loading}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Gerar Insights Financeiros
          </Button>
          <Button 
            onClick={analyzeDocuments} 
            disabled={loading}
            variant="outline"
          >
            <FileText className="h-4 w-4 mr-2" />
            Analisar Documentos
          </Button>
        </div>
        
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-2">Gerando análise...</span>
          </div>
        )}
        
        {insights && (
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
            <CardContent className="p-6">
              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                {insights}
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}

// Main App Component
// App Internal Component
function AppContent() {
  const auth = useAuth();

  console.log("AppContent - auth state:", { 
    hasToken: !!auth.token, 
    hasUser: !!auth.user, 
    hasCompany: !!auth.selectedCompany 
  });

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/catalogo/:token" element={<PublicCatalog />} />
        <Route path="/" element={
          !auth.token ? (
            <LoginPage />
          ) : !auth.selectedCompany ? (
            <CompanySelection />
          ) : (
            <Dashboard />
          )
        } />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

// Main App Component
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;